import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Animated,
  Easing,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Share,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import QRCode from 'react-native-qrcode-svg';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as GoogleSigninModule from '@react-native-google-signin/google-signin';
import { appleAuth } from '@invertase/react-native-apple-authentication';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect, useIsFocused, useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { GestureDetector, type NativeGesture } from 'react-native-gesture-handler';
import { pickImageFromLibrary, requestNotificationPermission, takePhotoWithCamera } from '../utils/permissions';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from 'firebase/auth';
import { addDoc, collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { useProfile } from '../hooks/useProfile';
import LoadingSpinner from '../components/LoadingSpinner';
import AuraBadge from '../components/AuraBadge';
import VibeVerifiedBadge from '../components/VibeVerifiedBadge';
import { Colors, Radius, Shadow, Typography } from '../theme';
import type { OrganizerEventTemplate, RootStackParamList, TonyteActivity, TonyteUser, UserRole } from '../types';
import type { AuraType } from '../data/dayVibesQuestions';
import { auth, DEFAULT_AVATAR, DEFAULT_COVER, FIRESTORE_COLLECTIONS, firestore, functionsEu, httpsCallable, PUBLIC_ACTIVITY_COLLECTIONS, readOrganizerEventTemplates, storage, uploadFaceVerificationImage, uploadUserImage } from '../firebase';
import { buildCompletionPatch, computeCompletionScore } from '../utils/profileCompletion';
import { PASSWORD_RESET_ACTION_CODE_SETTINGS } from '../utils/authActionSettings';
import { callFunctionViaFetch } from '../utils/callFunctionViaFetch';
import { useCityContext } from '../context/CityContext';
import {
  callApproveVerificationRequest,
  callDenyVerificationRequest,
  callSendVerificationEmail,
  type StudentVerificationRequest,
} from '../utils/studentVerification';
import TonyteBackground from '../components/TonyteBackground';
import TonyteCropModal, { type ImagePreviewSession } from '../components/TonyteCropModal';
import TonyteModal from '../components/TonyteModal';
import PhysicalLightSwitch from '../components/PhysicalLightSwitch';
import {
  BUILT_IN_INTEREST_KEYS,
  getInterestCategoryTranslationKey,
  getInterestTranslationKey,
  INTEREST_CATEGORIES,
} from '../data/interests';
import { unblockUser } from '../utils/blocking';
import { buildTonyteProfileLink } from '../utils/shareLinks';
import { useTodaysAuraState } from '../utils/todaysAura';
import { normalizeActivity } from '../hooks/useFeed';
import { getResolvedEventId } from '../utils/eventIds';
import { ANALYTICS_SOURCES, trackEventDetailOpen } from '../utils/eventAnalytics';
import { getDayVibeDateKey } from '../utils/dayVibeDate';
import { getLocalizedAuraName } from '../utils/auraTranslations';
import { formatDateOfBirth } from '../utils/age';
import { normalizeUserRole } from '../utils/roles';
import { isActivityCurrentOrUpcoming as isSharedActivityCurrentOrUpcoming, sortActivitiesByStart } from '../utils/activityDates';
import { useAdminAllPendingRequests } from '../hooks/usePlanJoinRequest';
import { useFollowedPartners } from '../hooks/useVenueVibes';
import { useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/types';
import { getPlanMemberCount } from '../utils/planMembership';
import { getWebFallbackImage, resolveActivityImage } from '../utils/activityImages';

type ProfileUpdatePatch = Partial<TonyteUser> & Record<string, unknown>;

type SidebarModalKey =
  | 'accountPrivacy'
  | 'blockedAccounts'
  | 'faq'
  | 'feedback'
  | 'notifications'
  | 'studentVerification'
  | 'faceVerification'
  | 'deleteAccount'
  | null;

type GoogleSigninApi = {
  configure: (options?: Record<string, unknown>) => void;
  hasPlayServices: (options: { showPlayServicesUpdateDialog: boolean }) => Promise<boolean>;
  signIn: () => Promise<
    | { type: 'success'; data: { idToken: string | null } }
    | { type: 'cancelled'; data: null }
  >;
  getTokens: () => Promise<{ idToken: string; accessToken: string }>;
  signOut: () => Promise<unknown>;
};

interface Props {
  userId: string | null;
  onSignOut: () => void;
  scrollGesture?: NativeGesture;
  onImagePreviewActiveChange?: (active: boolean) => void;
}

type AdminManagedUser = {
  id: string;
  name: string;
  email: string;
  photo: string;
  role: UserRole;
  restricted?: boolean;
  studentVerificationStatus?: string;
  faceVerified?: boolean;
};

type FaceVerificationCapture = {
  uri: string;
  fileName?: string;
  fileSize?: number;
  width?: number;
  height?: number;
};

type FaceVerificationRequest = {
  id: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  userPhoto?: string;
  selfieImageUrl?: string;
  poseType?: 'shaka' | string;
  frontImageUrl?: string;
  leftImageUrl?: string;
  rightImageUrl?: string;
  status?: 'pending' | 'approved' | 'rejected' | 'auto_approved';
  poseVerified?: boolean;
  livenessVerified?: boolean;
  verificationMethod?: string;
  automatedResult?: { front?: boolean; left?: boolean; right?: boolean; algorithmVersion?: string };
  submittedAt?: unknown;
  reviewedAt?: unknown;
  reviewedBy?: string;
  rejectionReason?: string;
};

type FaceVerificationUiStep = 'idle' | 'review' | 'uploading' | 'pending' | 'success' | 'failed';

const VerificationCheckBadge: React.FC<{ label: string; passed?: boolean }> = ({ label, passed }) => {
  const color = passed === true ? '#26d0ce' : passed === false ? '#f87171' : 'rgba(224,242,254,0.55)';
  const mark = passed === true ? '✓' : passed === false ? '✕' : '—';
  return (
    <View style={[styles.adminVerificationBadge, { borderColor: color }]}>
      <Text style={[styles.adminVerificationBadgeText, { color }]}>{mark} {label}</Text>
    </View>
  );
};

type BlockedAccount = {
  id: string;
  name: string;
  username: string;
  photo: string;
};

const ADMIN_ROLE_OPTIONS: UserRole[] = ['member', 'organizer', 'venue', 'co-admin', 'admin'];
const CO_ADMIN_ROLE_OPTIONS: UserRole[] = ['member', 'organizer', 'venue'];
const IOS_CLIENT_ID = '138042732807-7h70usp7fbfa6j9cfpj8thlneqk90v7e.apps.googleusercontent.com';
const WEB_CLIENT_ID = '138042732807-g11dm7v3r7rrtegqof8fvc6hnv62bobo.apps.googleusercontent.com';
const FAQ_ITEMS = Array.from({ length: 16 }, (_, index) => ({
  questionKey: `profile.faq${index + 1}Question`,
  answerKey: `profile.faq${index + 1}Answer`,
}));
function getGoogleSignin(): GoogleSigninApi | null {
  const moduleValue = GoogleSigninModule as unknown as {
    GoogleSignin?: GoogleSigninApi;
    default?: { GoogleSignin?: GoogleSigninApi } | GoogleSigninApi;
  };

  if (moduleValue.GoogleSignin?.configure) return moduleValue.GoogleSignin;
  if (
    moduleValue.default &&
    'GoogleSignin' in moduleValue.default &&
    moduleValue.default.GoogleSignin?.configure
  ) {
    return moduleValue.default.GoogleSignin;
  }
  if (moduleValue.default && 'configure' in moduleValue.default) {
    return moduleValue.default as GoogleSigninApi;
  }

  return null;
}

function getDeletionErrorMessage(error: unknown, t: (key: string) => string): string {
  const source = error as { code?: string; message?: string; deletionStage?: string; details?: { originalMessage?: string; message?: string; stage?: string } } | null;
  const code = typeof source?.code === 'string' ? source.code : '';
  const stage = source?.deletionStage || source?.details?.stage || '';
  const usefulMessage = source?.details?.originalMessage || source?.details?.message || source?.message || '';

  if (code === 'auth/requires-recent-login') return t('profile.deleteRequiresRecentLogin');
  if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') return t('profile.deleteInvalidCredential');
  if (code === 'auth/user-mismatch') return t('profile.deleteUserMismatch');
  if (code === 'SIGN_IN_CANCELLED' || code === '1001') return t('profile.deleteReauthInterrupted');
  if (code === 'auth/network-request-failed') return t('profile.deleteNetworkError');
  if (code === 'permission-denied' || code === 'functions/permission-denied') return t('profile.deletePermissionDenied');

  return stage
    ? t('profile.deleteFailedAtStage')
      .replace('{stage}', stage)
      .replace('{error}', usefulMessage || t('common.unknownError'))
    : usefulMessage || t('profile.deleteUnavailable');
}

const isAdminOrCoAdmin = (role?: string) => role === 'admin' || role === 'co-admin';
const isAdminRole = (role?: string) => role === 'admin';
const isCoAdminRole = (role?: string) => role === 'co-admin';
const canPublishEvents = (role?: string) => isAdminOrCoAdmin(role) || role === 'organizer' || role === 'partner';
const isVerifiedStudent = (profile?: { isStudentVerified?: boolean; studentVerified?: boolean; studentVerificationStatus?: string } | null) =>
  profile?.studentVerificationStatus === 'verified' && (profile?.isStudentVerified === true || profile?.studentVerified === true);

const getStudentUniversityLabel = (profile?: {
  studentUniversity?: string;
  studentVerificationStatus?: string;
  studentEmail?: string;
  requestedStudentEmail?: string;
  email?: string;
} | null) => {
  if (profile?.studentUniversity?.trim()) return profile.studentUniversity.trim();
  if (profile?.studentVerificationStatus === 'pending' || profile?.studentVerificationStatus === 'pending_admin_review') {
    return 'Verification Pending';
  }
  const email = (profile?.studentEmail || profile?.requestedStudentEmail || profile?.email || '').toLowerCase();
  if (email.includes('erasmus')) return 'Erasmus Student';
  return 'Erasmus Student';
};

const normalizeAdminUser = (id: string, data: Record<string, any>): AdminManagedUser => ({
  id,
  name: String(data.username || data.displayName || data.name || 'Tonyte User'),
  email: String(data.email || ''),
  photo: String(data.profilePhotoURL || data.profilePhoto || data.photoURL || data.profileImage || data.avatar || data.photo || DEFAULT_AVATAR),
  role: normalizeUserRole(data.role || (data.isAdmin ? 'admin' : 'member')),
  restricted: data.restricted === true,
  studentVerificationStatus: String(data.studentVerificationStatus || data.studentVerified ? data.studentVerificationStatus || 'verified' : 'unverified'),
  faceVerified: data.isVibeVerified === true || data.faceVerified === true,
});

const normalizeRequestDate = (value: unknown, locale = 'en-US'): string => {
  if (!value) return '?';
  const maybeTimestamp = value as { toDate?: () => Date };
  const date = typeof maybeTimestamp?.toDate === 'function'
    ? maybeTimestamp.toDate()
    : new Date(value as string | number);
  return Number.isNaN(date.getTime())
    ? '?'
    : date.toLocaleString(locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const getActivityImage = (activity: Partial<TonyteActivity>, fallback: string) =>
  activity.coverPhoto ||
  activity.coverImageUrl ||
  activity.coverImage ||
  activity.coverUrl ||
  activity.photoUrl ||
  activity.imageUrl ||
  activity.image ||
  activity.posterUrl ||
  fallback;

const getActivityDateLabel = (activity: Partial<TonyteActivity>, locale = 'en-US') => {
  const rawDate = activity.startDate || activity.date || activity.startAt || activity.startsAt || activity.timestamp;
  let date: Date | null = null;
  if (typeof rawDate === 'string' || typeof rawDate === 'number') date = new Date(rawDate);
  const maybeTimestamp = rawDate as { toDate?: () => Date } | undefined;
  if (!date && typeof maybeTimestamp?.toDate === 'function') date = maybeTimestamp.toDate();
  if (!date || Number.isNaN(date.getTime())) return activity.startTime || 'Soon';
  const day = date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  return activity.startTime ? `${day} · ${activity.startTime}` : day;
};

const getActivityVenueLabel = (activity: Partial<TonyteActivity>) =>
  activity.venueName || activity.clubName || activity.locationName || activity.location || activity.city || 'Tonyte spot';

const isLiveOrUpcomingActivity = (activity: Partial<TonyteActivity>, now = new Date()) => {
  if (!activity.id) return false;
  return isSharedActivityCurrentOrUpcoming(activity as TonyteActivity, now);
};

const sortUpcomingActivities = (items: TonyteActivity[]) =>
  [...items].sort(sortActivitiesByStart);

const userIsConnectedToPlan = (plan: TonyteActivity, userId: string) =>
  plan.createdBy === userId ||
  plan.hostId === userId ||
  plan.adminId === userId ||
  (Array.isArray(plan.adminIds) && plan.adminIds.includes(userId)) ||
  (Array.isArray(plan.members) && plan.members.includes(userId)) ||
  (Array.isArray(plan.participantIds) && plan.participantIds.includes(userId)) ||
  (Array.isArray((plan as any).approvedRequests) && (plan as any).approvedRequests.includes(userId)) ||
  (Array.isArray(plan.attendees) && plan.attendees.some(attendee => attendee.id === userId || (attendee as any).userId === userId));

const captureLooksUsable = (capture: FaceVerificationCapture): boolean => {
  if (!capture.uri) return false;
  if (capture.width && capture.width < 420) return false;
  if (capture.height && capture.height < 420) return false;
  if (capture.fileSize && capture.fileSize < 18_000) return false;
  return true;
};

const LOCALE_MAP: Record<string, string> = { de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU' };

const ProfileScreen: React.FC<Props> = ({ userId, onSignOut, scrollGesture, onImagePreviewActiveChange }) => {
  const { t, tp, language } = useI18n();
  const { theme, appearance } = useProfileAppearance();
  const locale = LOCALE_MAP[language] ?? 'en-US';
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === 'web' && width >= 1024;
  const isIOS = Platform.OS === 'ios';
  const profileBottomPadding = Math.max(insets.bottom, isIOS ? 18 : 0) + (isIOS ? 360 : 236);
  const topHeaderOffset = isIOS ? 14 : insets.top + 14;
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const profileEntrance = useRef(new Animated.Value(0)).current;
  const { selectedCityKey, setSelectedCityKey, visibleCities } = useCityContext();
  const { profile, loading, updateProfile } = useProfile(userId);
  const { aura: todaysAura, loaded: todaysAuraLoaded } = useTodaysAuraState(userId);
  const [myPosts, setMyPosts] = useState<TonyteActivity[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [interestedEvents, setInterestedEvents] = useState<TonyteActivity[]>([]);
  const [interestedEventsLoading, setInterestedEventsLoading] = useState(false);
  const [connectedPlans, setConnectedPlans] = useState<TonyteActivity[]>([]);
  const [connectedPlansLoading, setConnectedPlansLoading] = useState(false);
  const [todayVibeMatchCount, setTodayVibeMatchCount] = useState(0);
  const [todayVibeMatchesLoading, setTodayVibeMatchesLoading] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState<OrganizerEventTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const loggedStaleAcceptedPlanIdsRef = useRef<Set<string>>(new Set());
  const staleAcceptedPlanCleanupAttemptsRef = useRef<Set<string>>(new Set());
  const { requests: adminPlanRequests, loading: planRequestsLoading } = useAdminAllPendingRequests(userId);
  const [adminUsers, setAdminUsers] = useState<AdminManagedUser[]>([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [userManagementOpen, setUserManagementOpen] = useState(false);
  const [adminUserSearch, setAdminUserSearch] = useState('');
  const [adminRoleMessage, setAdminRoleMessage] = useState('');
  const [roleUpdatingUserId, setRoleUpdatingUserId] = useState<string | null>(null);
  const [activeImageViewer, setActiveImageViewer] = useState<'profile' | 'cover' | null>(null);
  const [pendingImagePreview, setPendingImagePreview] = useState<ImagePreviewSession | null>(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [coverActionsOpen, setCoverActionsOpen] = useState(false);
  const [imageActionBusy, setImageActionBusy] = useState(false);
  const [imageActionMessage, setImageActionMessage] = useState('');
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const [settingsSearchOpen, setSettingsSearchOpen] = useState(false);
  const [settingsSearchQuery, setSettingsSearchQuery] = useState('');
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);
  const settingsBackdropEntrance = useRef(new Animated.Value(0)).current;
  const settingsProfileEntrance = useRef(new Animated.Value(0)).current;
  const settingsLightDrift = useRef(new Animated.Value(0)).current;
  const settingsScrollRef = useRef<ScrollView | null>(null);
  const [interestComposerOpen, setInterestComposerOpen] = useState(false);
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [interestSearchQuery, setInterestSearchQuery] = useState('');
  const [savingInterests, setSavingInterests] = useState(false);
  const [activeSidebarModal, setActiveSidebarModal] = useState<SidebarModalKey>(null);
  const [passwordResetMessage, setPasswordResetMessage] = useState('');
  const [passwordResetSending, setPasswordResetSending] = useState(false);
  const [blockedAccounts, setBlockedAccounts] = useState<BlockedAccount[]>([]);
  const [blockedAccountsLoading, setBlockedAccountsLoading] = useState(false);
  const [blockedAccountsMessage, setBlockedAccountsMessage] = useState('');
  const [pendingUnblockUserId, setPendingUnblockUserId] = useState<string | null>(null);
  const [faqSearchQuery, setFaqSearchQuery] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [feedbackType, setFeedbackType] = useState('general');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [feedbackSubmitMessage, setFeedbackSubmitMessage] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [studentEmailInput, setStudentEmailInput] = useState('');
  const [studentVerificationSaving, setStudentVerificationSaving] = useState(false);
  const [studentVerificationMessage, setStudentVerificationMessage] = useState('');
  const [pendingVerifications, setPendingVerifications] = useState<StudentVerificationRequest[]>([]);
  const [pendingVerificationsLoading, setPendingVerificationsLoading] = useState(false);
  const [studentVerifActionSaving, setStudentVerifActionSaving] = useState<string | null>(null);
  const [studentVerifModalOpen, setStudentVerifModalOpen] = useState(false);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [deleteAccountError, setDeleteAccountError] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [pendingFaceVerifications, setPendingFaceVerifications] = useState<FaceVerificationRequest[]>([]);
  const [pendingFaceVerificationsLoading, setPendingFaceVerificationsLoading] = useState(false);
  const [faceVerifActionSaving, setFaceVerifActionSaving] = useState<string | null>(null);
  const [faceVerifModalOpen, setFaceVerifModalOpen] = useState(false);
  const [revokingBadgeUserId, setRevokingBadgeUserId] = useState<string | null>(null);
  // Rendered inline, next to each "Revoke Vibe Verified" button, instead of
  // relying solely on Alert.alert/toast: both those buttons live inside a
  // native <Modal>, which presents as its own separate surface (a distinct
  // UIViewController on iOS, a separate Dialog/window on Android) — anything
  // mounted at the app root as an overlay (the toast library included) can
  // render BEHIND an open Modal, and Alert.alert has had known inconsistency
  // stacking over an open Modal on Android. Inline state is part of the
  // Modal's own view tree, so it's guaranteed visible regardless of that.
  const [revokeFeedback, setRevokeFeedback] = useState<{ userId: string; message: string } | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [suspendingUserId, setSuspendingUserId] = useState<string | null>(null);
  const [cityPickerOpen, setCityPickerOpen] = useState(false);
  const canManageUsers = isAdminOrCoAdmin(profile?.role);

  useEffect(() => {
    if (!settingsDrawerOpen) {
      setSettingsSearchOpen(false);
      setSettingsSearchQuery('');
      settingsBackdropEntrance.setValue(0);
      settingsProfileEntrance.setValue(0);
      settingsLightDrift.stopAnimation();
      settingsLightDrift.setValue(0);
      return;
    }

    settingsScrollRef.current?.scrollTo({ y: 0, animated: false });

    Animated.sequence([
      Animated.timing(settingsBackdropEntrance, {
        toValue: 1,
        duration: 220,
        easing: Easing.out(Easing.quad),
        useNativeDriver: true,
      }),
      Animated.timing(settingsProfileEntrance, {
        toValue: 1,
        duration: 440,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();

    const lightLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(settingsLightDrift, {
          toValue: 1,
          duration: 7000,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(settingsLightDrift, {
          toValue: 0,
          duration: 7000,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ]),
    );
    lightLoop.start();
    return () => lightLoop.stop();
  }, [settingsBackdropEntrance, settingsDrawerOpen, settingsLightDrift, settingsProfileEntrance]);

  useEffect(() => {
    Animated.timing(profileEntrance, {
      toValue: 1,
      duration: 520,
      useNativeDriver: true,
    }).start();
  }, [profileEntrance]);

  useEffect(() => {
    console.log('[Tonyte][ImagePreview] profile screen state', {
      imagePreviewVisible: Boolean(pendingImagePreview),
      pendingImageType: pendingImagePreview?.kind ?? null,
      pendingImageUri: pendingImagePreview?.uri ?? null,
    });
    onImagePreviewActiveChange?.(Boolean(pendingImagePreview));
  }, [onImagePreviewActiveChange, pendingImagePreview]);

  useEffect(() => () => {
    onImagePreviewActiveChange?.(false);
  }, [onImagePreviewActiveChange]);

  const loadPosts = useCallback(async () => {
    if (!userId) return;
    setPostsLoading(true);
    try {
      const activitiesRef = collection(firestore, FIRESTORE_COLLECTIONS.activities);
      const plansRef = collection(firestore, FIRESTORE_COLLECTIONS.plans);
      const sources = [
        {
          source: FIRESTORE_COLLECTIONS.activities,
          target: query(activitiesRef, where('createdBy', '==', userId), limit(80)),
        },
        {
          source: FIRESTORE_COLLECTIONS.activities,
          target: query(activitiesRef, where('hostId', '==', userId), limit(80)),
        },
        {
          source: FIRESTORE_COLLECTIONS.plans,
          target: query(plansRef, where('createdBy', '==', userId), limit(80)),
        },
        {
          source: FIRESTORE_COLLECTIONS.plans,
          target: query(plansRef, where('hostId', '==', userId), limit(80)),
        },
      ];
      const posts = new Map<string, TonyteActivity>();
      const results = await Promise.allSettled(sources.map(source => getDocs(source.target)));
      results.forEach((result, index) => {
        if (result.status !== 'fulfilled') return;
        const source = sources[index].source;
        result.value.forEach(docSnap => {
          const normalized = normalizeActivity(docSnap.id, docSnap.data() as any, source);
          if (source === FIRESTORE_COLLECTIONS.activities && normalized.entryType === 'community_plan') return;
          posts.set(`${source}/${docSnap.id}`, normalized);
        });
      });
      setMyPosts(Array.from(posts.values()));
    } finally {
      setPostsLoading(false);
    }
  }, [userId]);

  const loadTemplates = useCallback(async () => {
    if (!userId) return;
    setTemplatesLoading(true);
    try {
      const templates = await readOrganizerEventTemplates(userId);
      setSavedTemplates(templates);
    } catch {
      setSavedTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    if (!userId) {
      setTodayVibeMatchCount(0);
      setTodayVibeMatchesLoading(false);
      return undefined;
    }

    setTodayVibeMatchesLoading(true);
    const dateKey = getDayVibeDateKey();
    const matchesQuery = query(
      collection(firestore, FIRESTORE_COLLECTIONS.dayVibeMatches),
      where('users', 'array-contains', userId),
      where('dateKey', '==', dateKey),
      limit(50),
    );
    const collectionPath = FIRESTORE_COLLECTIONS.dayVibeMatches;

    console.log('[Tonyte][RN][Profile] starting today vibe matches subscription', {
      collectionPath,
      queryFilters: `users array-contains ${userId}; dateKey == ${dateKey}; limit 50`,
      currentUserId: userId,
      authUid: auth.currentUser?.uid,
    });

    return onSnapshot(
      matchesQuery,
      snapshot => {
        setTodayVibeMatchCount(snapshot.size);
        setTodayVibeMatchesLoading(false);
      },
      error => {
        console.error('[Firestore Permission Error]', collectionPath, auth.currentUser?.uid, error);
        console.error('[Tonyte][RN][Profile] today vibe matches subscription failed', {
          collectionPath,
          queryFilters: `users array-contains ${userId}; dateKey == ${dateKey}; limit 50`,
          currentUserId: userId,
          authUid: auth.currentUser?.uid,
          code: (error as { code?: string }).code,
          error,
        });
        setTodayVibeMatchCount(0);
        setTodayVibeMatchesLoading(false);
      },
    );
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setInterestedEvents([]);
      setInterestedEventsLoading(false);
      return undefined;
    }

    console.log('[Tonyte][RN][Profile] interested events - currentUserId:', userId);
    setInterestedEventsLoading(true);

    let cancelled = false;
    // Incremented on every snapshot fire so stale in-flight fetches are dropped.
    let latestCallId = 0;

    const userInterestsQuery = query(
      collection(firestore, FIRESTORE_COLLECTIONS.users, userId, 'interestedEvents'),
      limit(80),
    );

    const fetchEventById = async (eventId: string): Promise<TonyteActivity | null> => {
      for (const collectionName of PUBLIC_ACTIVITY_COLLECTIONS) {
        try {
          const snap = await getDoc(doc(firestore, collectionName, eventId));
          if (snap.exists()) {
            return normalizeActivity(snap.id, snap.data() as any, collectionName);
          }
        } catch {
          // skip permission errors for individual collections
        }
      }
      // Fallback: query by eventId / canonicalEventId field in case doc ID differs
      for (const collectionName of PUBLIC_ACTIVITY_COLLECTIONS) {
        try {
          const matches = await Promise.all([
            getDocs(query(collection(firestore, collectionName), where('eventId', '==', eventId), limit(1))),
            getDocs(query(collection(firestore, collectionName), where('canonicalEventId', '==', eventId), limit(1))),
          ]);
          const matchedDoc = matches.flatMap(s => s.docs)[0];
          if (matchedDoc) {
            return normalizeActivity(matchedDoc.id, matchedDoc.data() as any, collectionName);
          }
        } catch {
          // skip permission errors
        }
      }
      return null;
    };

    const unsubscribe = onSnapshot(
      userInterestsQuery,
      async snapshot => {
        const thisCallId = ++latestCallId;

        const rawIds = snapshot.docs
          .map(d => {
            const data = d.data() as Record<string, unknown>;
            return String(data.activityId || data.eventId || data.canonicalEventId || d.id || '').trim();
          })
          .filter(Boolean);

        const interestedEventIds = Array.from(new Set(rawIds)).slice(0, 50);
        console.log('[Tonyte][RN][Profile] interestedEventIds found:', interestedEventIds.length, interestedEventIds);

        if (!interestedEventIds.length) {
          if (!cancelled && thisCallId === latestCallId) {
            setInterestedEvents([]);
            setInterestedEventsLoading(false);
          }
          return;
        }

        const results = await Promise.allSettled(interestedEventIds.map(fetchEventById));
        if (cancelled || thisCallId !== latestCallId) return;

        const byId = new Map<string, TonyteActivity>();
        let skippedCount = 0;

        results.forEach(result => {
          if (result.status !== 'fulfilled' || !result.value) {
            skippedCount++;
            return;
          }
          const activity = { ...result.value, viewerInterested: true };
          if (activity.entryType !== 'official_event') {
            skippedCount++;
            return;
          }
          if (!isLiveOrUpcomingActivity(activity)) {
            skippedCount++;
            return;
          }
          byId.set(getResolvedEventId(activity), activity);
        });

        console.log('[Tonyte][RN][Profile] loaded interestedEvents count:', byId.size);
        console.log('[Tonyte][RN][Profile] skipped expired/missing events count:', skippedCount);

        setInterestedEvents(sortUpcomingActivities(Array.from(byId.values())));
        setInterestedEventsLoading(false);
      },
      error => {
        console.warn('[Tonyte][RN][Profile] user interested events subscription failed', error);
        if (!cancelled) {
          setInterestedEvents([]);
          setInterestedEventsLoading(false);
        }
      },
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setConnectedPlans([]);
      setConnectedPlansLoading(false);
      return undefined;
    }

    setConnectedPlansLoading(true);
    let cancelled = false;
    const snapshotBuckets = new Map<string, TonyteActivity[]>();
    let acceptedPlanIds = new Set<string>();
    let acceptedPlanRequestIds = new Map<string, string[]>();

    const markAcceptedPlanRequestStale = (planId: string, reason: string) => {
      const requestIds = acceptedPlanRequestIds.get(planId) ?? [];
      requestIds.forEach(requestId => {
        if (staleAcceptedPlanCleanupAttemptsRef.current.has(requestId)) return;
        staleAcceptedPlanCleanupAttemptsRef.current.add(requestId);
        updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.planJoinRequests, requestId), {
          status: 'stale',
          staleReason: reason,
          staleAt: serverTimestamp(),
          updatedAt: new Date().toISOString(),
        }).catch(() => {
          // Best-effort cleanup only. Stale accepted requests should never make Profile noisy.
        });
      });
    };

    const logStaleAcceptedPlanOnce = (planId: string, reason: string) => {
      if (!__DEV__ || loggedStaleAcceptedPlanIdsRef.current.has(planId)) return;
      loggedStaleAcceptedPlanIdsRef.current.add(planId);
      console.log('[Profile] stale accepted plan reference skipped', { planId, reason });
    };

    const publishPlans = async () => {
      const byId = new Map<string, TonyteActivity>();
      Array.from(snapshotBuckets.values()).flat().forEach(plan => {
        if (plan.entryType !== 'community_plan') return;
        if (!userIsConnectedToPlan(plan, userId) && !acceptedPlanIds.has(plan.id)) return;
        if (!isLiveOrUpcomingActivity(plan)) return;
        byId.set(plan.id, plan);
      });

      const missingAcceptedIds = Array.from(acceptedPlanIds).filter(planId => !byId.has(planId));
      if (missingAcceptedIds.length) {
        const fetchJobs = missingAcceptedIds.map(planId => ({
            planId,
            path: `${FIRESTORE_COLLECTIONS.plans}/${planId}`,
            promise: getDoc(doc(firestore, FIRESTORE_COLLECTIONS.plans, planId))
              .then(snap => snap.exists() ? normalizeActivity(snap.id, snap.data() as any, FIRESTORE_COLLECTIONS.plans) : null),
          }));
        const fetched = await Promise.allSettled(fetchJobs.map(job => job.promise));
        if (cancelled) return;
        fetched.forEach((result, index) => {
          const { planId } = fetchJobs[index];
          if (result.status === 'rejected') {
            const code = (result.reason as { code?: string })?.code || 'read-failed';
            logStaleAcceptedPlanOnce(planId, code);
            markAcceptedPlanRequestStale(planId, code);
            return;
          }
          if (!result.value) {
            logStaleAcceptedPlanOnce(planId, 'missing-plan');
            markAcceptedPlanRequestStale(planId, 'missing-plan');
            return;
          }
          const plan = result.value;
          if (plan.entryType === 'community_plan' && isLiveOrUpcomingActivity(plan)) byId.set(plan.id, plan);
        });
      }

      if (cancelled) return;
      setConnectedPlans(sortUpcomingActivities(Array.from(byId.values())));
      setConnectedPlansLoading(false);
    };

    const planQueries = [
      {
        key: 'plans.participantIds',
        target: query(
          collection(firestore, FIRESTORE_COLLECTIONS.plans),
          where('participantIds', 'array-contains', userId),
          limit(120),
        ),
        source: FIRESTORE_COLLECTIONS.plans,
        filters: `participantIds array-contains ${userId}; limit 120`,
      },
    ];

    const unsubs = planQueries.map(item => {
      console.log('[Tonyte][RN][Profile] starting plan subscription', {
        collectionPath: item.source,
        queryFilters: item.filters,
        currentUserId: userId,
        authUid: auth.currentUser?.uid,
      });

      return onSnapshot(
        item.target,
        snapshot => {
          snapshotBuckets.set(
            item.key,
            snapshot.docs.map(d => normalizeActivity(d.id, d.data() as any, item.source)),
          );
          void publishPlans();
        },
        error => {
          console.error('[Firestore Permission Error]', item.source, auth.currentUser?.uid, error);
          console.error('[Tonyte][RN][Profile] plan subscription failed', {
            key: item.key,
            collectionPath: item.source,
            queryFilters: item.filters,
            currentUserId: userId,
            authUid: auth.currentUser?.uid,
            code: (error as { code?: string }).code,
            error,
          });
          snapshotBuckets.set(item.key, []);
          void publishPlans();
        },
      );
    });

    const joinRequestsPath = FIRESTORE_COLLECTIONS.planJoinRequests;
    const joinRequestsFilters = `fromUserId == ${userId}; status in [accepted, approved]; limit 80`;
    console.log('[Tonyte][RN][Profile] starting accepted plan request subscription', {
      collectionPath: joinRequestsPath,
      queryFilters: joinRequestsFilters,
      currentUserId: userId,
      authUid: auth.currentUser?.uid,
    });
    unsubs.push(onSnapshot(
      query(
        collection(firestore, FIRESTORE_COLLECTIONS.planJoinRequests),
        where('fromUserId', '==', userId),
        where('status', 'in', ['accepted', 'approved']),
        limit(80),
      ),
      snapshot => {
        acceptedPlanRequestIds = new Map();
        snapshot.docs.forEach(d => {
          const planId = String(d.data().planId || '').trim();
          if (!planId) return;
          const existing = acceptedPlanRequestIds.get(planId) ?? [];
          acceptedPlanRequestIds.set(planId, [...existing, d.id]);
        });
        acceptedPlanIds = new Set(acceptedPlanRequestIds.keys());
        void publishPlans();
      },
      error => {
        console.error('[Firestore Permission Error]', joinRequestsPath, auth.currentUser?.uid, error);
        console.error('[Tonyte][RN][Profile] accepted plan request subscription failed', {
          collectionPath: joinRequestsPath,
          queryFilters: joinRequestsFilters,
          currentUserId: userId,
          authUid: auth.currentUser?.uid,
          code: (error as { code?: string }).code,
          error,
        });
        acceptedPlanIds = new Set();
        void publishPlans();
      },
    ));

    return () => {
      cancelled = true;
      unsubs.forEach(unsub => unsub());
    };
  }, [userId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (!interestComposerOpen) return;
    setSelectedInterests([...new Set((profile?.interests ?? []).map(i => i.trim()).filter(Boolean))]);
    setInterestSearchQuery('');
  }, [interestComposerOpen, profile?.interests]);

  useEffect(() => {
    setFeedbackEmail(profile?.email || auth.currentUser?.email || '');
    setStudentEmailInput(profile?.studentEmail || profile?.requestedStudentEmail || profile?.email || '');
  }, [profile?.email, profile?.requestedStudentEmail, profile?.studentEmail]);

  useEffect(() => {
    if (!canManageUsers) {
      setAdminUsers([]);
      setAdminUsersLoading(false);
      return undefined;
    }

    setAdminUsersLoading(true);
    console.log('[Tonyte][RN][Admin] starting users collection subscription', {
      collectionPath: FIRESTORE_COLLECTIONS.users,
      currentUserId: userId,
      currentUserRole: profile?.role || null,
    });
    const unsubscribe = onSnapshot(
      collection(firestore, FIRESTORE_COLLECTIONS.users),
      snapshot => {
        const nextUsers = snapshot.docs
          .map(userDoc => normalizeAdminUser(userDoc.id, userDoc.data()))
          .sort((left, right) => left.name.localeCompare(right.name));
        setAdminUsers(nextUsers);
        setAdminUsersLoading(false);
        console.log('[Tonyte][RN][Admin] users collection snapshot received', {
          collectionPath: FIRESTORE_COLLECTIONS.users,
          count: snapshot.size,
          currentUserId: userId,
        });
      },
      error => {
        console.warn('[Tonyte][RN][Admin] failed to load users', {
          collectionPath: FIRESTORE_COLLECTIONS.users,
          currentUserId: userId,
          currentUserRole: profile?.role || null,
          code: typeof error === 'object' && error !== null && 'code' in error
            ? String((error as { code?: unknown }).code || '')
            : '',
          message: error.message,
          error,
        });
        setAdminUsers([]);
        setAdminUsersLoading(false);
      },
    );

    return unsubscribe;
  }, [canManageUsers, profile?.role, userId]);

  // Subscribe to pending student verification requests (admin/co-admin only).
  useEffect(() => {
    if (!canManageUsers) {
      setPendingVerifications([]);
      return undefined;
    }
    setPendingVerificationsLoading(true);
    const q = query(
      collection(firestore, 'studentVerificationRequests'),
      where('status', 'in', ['pending_admin_review']),
      orderBy('createdAt', 'desc'),
      limit(30),
    );
    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        setPendingVerifications(
          snapshot.docs.map(d => ({ id: d.id, ...d.data() } as StudentVerificationRequest)),
        );
        setPendingVerificationsLoading(false);
      },
      () => setPendingVerificationsLoading(false),
    );
    return unsubscribe;
  }, [canManageUsers]);

  useEffect(() => {
    if (!canManageUsers) {
      setPendingFaceVerifications([]);
      setPendingFaceVerificationsLoading(false);
      return undefined;
    }
    setPendingFaceVerificationsLoading(true);
    // 'auto_approved' (the new instant face-pose flow) alongside 'pending'
    // (legacy shaka requests still awaiting manual review, or a rare
    // in-flight face-pose request whose grant call hasn't landed yet) —
    // admins still need to see auto-approved submissions to spot-check and
    // revoke them if suspicious. Reuses the existing (status ASC,
    // submittedAt DESC) composite index — an `in` query indexes the same
    // as `==` for this purpose, no new index required.
    const q = query(
      collection(firestore, 'faceVerificationRequests'),
      where('status', 'in', ['pending', 'auto_approved']),
      orderBy('submittedAt', 'desc'),
      limit(50),
    );
    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        // One entry per person — newest first (query order), so repeat
        // submissions never show as duplicates. The server also marks older
        // ones 'superseded' (supersedeFaceVerificationRequestsEu).
        const seenUsers = new Set<string>();
        setPendingFaceVerifications(snapshot.docs
          .map(d => ({ id: d.id, ...d.data() } as FaceVerificationRequest))
          .filter(req => {
            if (!req.userId || seenUsers.has(req.userId)) return false;
            seenUsers.add(req.userId);
            return true;
          }));
        setPendingFaceVerificationsLoading(false);
      },
      error => {
        console.warn('[Tonyte][RN][FaceVerification] failed to load requests', error);
        setPendingFaceVerificationsLoading(false);
      },
    );
    return unsubscribe;
  }, [canManageUsers]);

  // Sync computed completion score to Firestore whenever the profile changes.
  // Only fires when the score actually differs from the stored value, preventing loops.
  useEffect(() => {
    if (!profile?.id) return;
    const { score, missing } = computeCompletionScore(profile);
    const previousMissing = profile.missingProfileFields || [];
    const sameMissing = previousMissing.length === missing.length &&
      previousMissing.every((field, index) => field === missing[index]);
    if (profile.profileCompletionPercent === score && sameMissing) return;
    const timer = setTimeout(() => {
      updateProfile(buildCompletionPatch(score, missing) as any).catch(() => {});
    }, 600);
    return () => clearTimeout(timer);
  }, [profile, updateProfile]);

  // Normalize legacy verified accounts: studentVerified may be true while
  // studentVerificationStatus is still 'approved' or another legacy value.
  // Bring them in sync so UI states and Firestore rules work correctly.
  useEffect(() => {
    if (!profile?.id) return;
    if (!profile.studentVerified) return;
    if (profile.studentVerificationStatus === 'verified') return;
    updateProfile({
      studentVerified: true,
      studentVerificationStatus: 'verified',
    } as ProfileUpdatePatch).catch(() => {});
  }, [profile?.id, profile?.studentVerified, profile?.studentVerificationStatus, updateProfile]);

  // Some accounts were approved through studentVerificationRequests while their
  // user document still says pending. The web app treats approved requests as
  // the source of truth, so sync the profile doc instead of showing pending.
  useEffect(() => {
    if (!profile?.id || profile.studentVerified) return;
    let cancelled = false;
    const syncApprovedStudentRequest = async () => {
      try {
        const approvedSnapshot = await getDocs(query(
          collection(firestore, 'studentVerificationRequests'),
          where('userId', '==', profile.id),
          where('status', 'in', ['approved', 'verified']),
          limit(1),
        ));
        if (cancelled || approvedSnapshot.empty) return;
        const request = approvedSnapshot.docs[0].data() as {
          submittedEmail?: string;
          studentUniversity?: string;
          approvedAt?: unknown;
        };
        await updateProfile({
          studentVerified: true,
          isStudent: true,
          studentVerificationStatus: 'verified',
          studentEmail: request.submittedEmail || profile.studentEmail || profile.requestedStudentEmail || '',
          studentUniversity: request.studentUniversity || profile.studentUniversity || getStudentUniversityLabel(profile),
          studentVerifiedAt: request.approvedAt || new Date().toISOString(),
        } as ProfileUpdatePatch);
      } catch (error) {
        console.warn('[Tonyte][RN][StudentVerification] approved-request sync failed', error);
      }
    };
    void syncApprovedStudentRequest();
    return () => {
      cancelled = true;
    };
  }, [profile, updateProfile]);

  // Refresh posts and templates when returning from UploadEvent/UploadPlan screens
  useFocusEffect(
    useCallback(() => {
      loadPosts();
      loadTemplates();
    }, [loadPosts, loadTemplates]),
  );

  const role = profile?.role;
  const [ownedVenue, setOwnedVenue] = useState<{ id: string; name: string } | null>(null);
  const { partners: followedPartners, loading: followedPartnersLoading } = useFollowedPartners(userId);
  // Partner features follow partner status, not only role: a Tonyte admin who
  // is also a partner keeps role 'admin' but owns a partner profile.
  const hasPartnerAccess = role === 'partner' || Boolean(ownedVenue);

  // Partners who manage a venue (venues/{id}.ownerUid) get a shortcut to its
  // public profile — the same page visitors see in Vibe at Venues.
  useEffect(() => {
    if (!userId) {
      setOwnedVenue(null);
      return undefined;
    }
    let active = true;
    getDocs(query(collection(firestore, 'venues'), where('ownerUid', '==', userId), limit(1)))
      .then(snapshot => {
        if (!active) return;
        const venueDoc = snapshot.docs[0];
        setOwnedVenue(venueDoc ? { id: venueDoc.id, name: String(venueDoc.data().name || '') } : null);
      })
      .catch(error => console.warn('[Tonyte][Profile] owned venue lookup failed', error));
    return () => { active = false; };
  }, [userId]);

  const displayName = profile?.name || 'Tonyte User';
  const city = profile?.locationCity || profile?.city || '';
  const country = profile?.locationCountry || profile?.country || '';
  const basedInLabel = city || country ? `${t('profile.basedIn')} ${[city, country].filter(Boolean).join(', ')}` : t('profile.locationNotAdded');
  const aboutText = profile?.bio || t('profile.addBio');
  const { score: profileCompletionScore } = computeCompletionScore(profile);
  const interests = [...new Set((profile?.interests ?? []).map(i => i.trim()).filter(Boolean))];
  const renderedProfileImage = profile?.photo || DEFAULT_AVATAR;
  const renderedCoverImage = profile?.coverPhoto || DEFAULT_COVER;
  const normalizedSettingsSearch = settingsSearchQuery.trim().toLocaleLowerCase();
  const matchesSettingsSearch = (label: string) =>
    !normalizedSettingsSearch || label.toLocaleLowerCase().includes(normalizedSettingsSearch);
  const normalizedInterestSearch = interestSearchQuery.trim().replace(/\s+/g, ' ');
  const getInterestLabel = useCallback((interest: string) => (
    BUILT_IN_INTEREST_KEYS.has(interest.trim().toLowerCase())
      ? t(getInterestTranslationKey(interest), interest)
      : interest
  ), [t]);
  const hasExactInterestMatch = normalizedInterestSearch
    ? [...INTEREST_CATEGORIES.flatMap(category => category.interests), ...selectedInterests]
      .some(interest =>
        interest.toLowerCase() === normalizedInterestSearch.toLowerCase() ||
        getInterestLabel(interest).toLowerCase() === normalizedInterestSearch.toLowerCase())
    : true;
  const filteredInterestCategories = useMemo(() => {
    const queryText = interestSearchQuery.trim().toLowerCase();
    return INTEREST_CATEGORIES
      .map(category => ({
        ...category,
        interests: category.interests.filter(interest =>
          interest.toLowerCase().includes(queryText) ||
          getInterestLabel(interest).toLowerCase().includes(queryText)),
      }))
      .filter(category => !queryText || category.interests.length > 0);
  }, [getInterestLabel, interestSearchQuery]);
  const filteredAdminUsers = useMemo(() => {
    const queryText = adminUserSearch.trim().toLowerCase();
    if (!queryText) return adminUsers;
    return adminUsers.filter(user =>
      user.name.toLowerCase().includes(queryText) ||
      user.email.toLowerCase().includes(queryText) ||
      user.role.toLowerCase().includes(queryText),
    );
  }, [adminUserSearch, adminUsers]);
  const filteredFaqItems = useMemo(() => {
    const items = FAQ_ITEMS.map(item => ({
      question: t(item.questionKey),
      answer: t(item.answerKey),
    }));
    const queryText = faqSearchQuery.trim().toLowerCase();
    if (!queryText) return items;
    return items.filter(item =>
      item.question.toLowerCase().includes(queryText) ||
      item.answer.toLowerCase().includes(queryText),
    );
  }, [faqSearchQuery, t]);
  const notificationsEnabled = (profile as ProfileUpdatePatch | null)?.notificationsEnabled !== false;
  const chatNotificationsEnabled = (profile as ProfileUpdatePatch | null)?.chatNotificationsEnabled !== false;
  const eventNotificationsEnabled = (profile as ProfileUpdatePatch | null)?.eventNotificationsEnabled !== false;
  // The two newer categories follow the older event switch until set
  // explicitly — same fallback as the server (functions/notifications.js
  // isCategoryEnabled), so the toggle always shows what will be delivered.
  const storedEventRecommendations = (profile as ProfileUpdatePatch | null)?.eventRecommendationNotificationsEnabled;
  const eventRecommendationNotificationsEnabled = typeof storedEventRecommendations === 'boolean' ? storedEventRecommendations : eventNotificationsEnabled;
  const storedPartnerNotifications = (profile as ProfileUpdatePatch | null)?.partnerNotificationsEnabled;
  const partnerNotificationsEnabled = typeof storedPartnerNotifications === 'boolean' ? storedPartnerNotifications : eventNotificationsEnabled;
  // Optional reminder categories (functions/notifications.js): on unless turned off.
  const profileReminderNotificationsEnabled = (profile as ProfileUpdatePatch | null)?.profileReminderNotificationsEnabled !== false;
  const reviewReminderNotificationsEnabled = (profile as ProfileUpdatePatch | null)?.reviewReminderNotificationsEnabled !== false;
  const feedbackReminderNotificationsEnabled = (profile as ProfileUpdatePatch | null)?.feedbackReminderNotificationsEnabled !== false;
  const deletionProvider = auth.currentUser?.providerData.find(provider => provider.providerId === 'password')?.providerId ||
    auth.currentUser?.providerData.find(provider => provider.providerId === 'google.com')?.providerId ||
    auth.currentUser?.providerData.find(provider => provider.providerId === 'apple.com')?.providerId ||
    auth.currentUser?.providerData[0]?.providerId ||
    'password';
  const deletionProviderLabel = deletionProvider === 'password'
    ? 'your password'
    : deletionProvider === 'google.com'
      ? 'Google'
      : deletionProvider === 'apple.com'
        ? 'Apple'
        : 'your account';

  const canChangeRole = useCallback((targetUser: AdminManagedUser, nextRole: UserRole) => {
    if (!isAdminOrCoAdmin(role)) return false;
    if (isCoAdminRole(role)) {
      if (nextRole === 'admin' || nextRole === 'co-admin') return false;
      if (targetUser.role === 'admin' || targetUser.role === 'co-admin') return false;
    }
    return true;
  }, [role]);

  const handleRoleUpdate = useCallback((targetUser: AdminManagedUser, nextRole: UserRole) => {
    if (!canChangeRole(targetUser, nextRole)) {
      setAdminRoleMessage(
        isCoAdminRole(role)
          ? t('profile.coAdminRoleRestriction')
          : t('profile.noRolePermission'),
      );
      return;
    }
    if (targetUser.role === nextRole) return;

    Alert.alert(
      t('profile.updateRole'),
      t('profile.changeRole').replace('{name}', targetUser.name).replace('{from}', targetUser.role).replace('{to}', nextRole),
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: t('profile.updateRoleAction'),
          style: nextRole === 'admin' || targetUser.role === 'admin' ? 'destructive' : 'default',
          onPress: async () => {
            setRoleUpdatingUserId(targetUser.id);
            setAdminRoleMessage('');
            try {
              const idToken = await auth.currentUser?.getIdToken(true);
              if (!idToken) throw new Error(t('profile.adminSignInRequired'));
              const response = await fetch('https://europe-west3-triptribe-8babd.cloudfunctions.net/setUserRoleHttpEu', {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${idToken}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ data: { uid: targetUser.id, role: nextRole } }),
              });
              const body = await response.json().catch(() => null);
              if (!response.ok || body?.error) {
                throw new Error(body?.error?.message || response.statusText || t('profile.roleUpdateFailed'));
              }
              setAdminUsers(current => current.map(user =>
                user.id === targetUser.id ? { ...user, role: nextRole } : user,
              ));
              setAdminRoleMessage(t('profile.roleUpdated'));
            } catch (error) {
              console.error('[Tonyte][RN][Admin] failed to update role', {
                targetUid: targetUser.id,
                role: nextRole,
                error,
              });
              setAdminRoleMessage(error instanceof Error ? error.message : t('profile.roleUpdateFailed'));
            } finally {
              setRoleUpdatingUserId(null);
            }
          },
        },
      ],
    );
  }, [canChangeRole, role, t]);

  // Shared by both Suspend and Delete: never your own account, and a
  // co-admin caller can never touch an admin/co-admin target — the same
  // PROTECTED_ROLES rule the backend (functions/index.js) enforces
  // independently. This is a UX convenience (hide/disable what would fail
  // server-side anyway); the actual authorization happens in the Cloud
  // Functions, never here.
  const canModerateUser = useCallback((targetUser: AdminManagedUser) => {
    if (!isAdminOrCoAdmin(role)) return false;
    if (targetUser.id === userId) return false;
    if (isCoAdminRole(role) && (targetUser.role === 'admin' || targetUser.role === 'co-admin')) return false;
    return true;
  }, [role, userId]);

  // Stricter than canModerateUser: no admin-vs-admin deletion, since no
  // super-admin tier exists to make that an intentional hierarchy (mirrors
  // the same check in deleteUserByAdminHandler). Suspension doesn't carry
  // this extra restriction — only permanent deletion does.
  const canDeleteUser = useCallback((targetUser: AdminManagedUser) => {
    if (!canModerateUser(targetUser)) return false;
    if (isAdminRole(role) && targetUser.role === 'admin') return false;
    return true;
  }, [canModerateUser, role]);

  const performDeleteUser = useCallback(async (targetUser: AdminManagedUser) => {
    setDeletingUserId(targetUser.id);
    setAdminRoleMessage('');
    try {
      const deleteFn = httpsCallable(functionsEu, 'deleteUserByAdminEu');
      const response = await deleteFn({ uid: targetUser.id });
      const result = response.data as { success?: boolean };
      if (!result?.success) throw new Error(t('settings.deleteAccountFailed'));
      // Immediate removal — don't wait on the users collection onSnapshot to
      // re-fire, even though it will (the function deletes users/{uid}).
      setAdminUsers(current => current.filter(u => u.id !== targetUser.id));
      setAdminRoleMessage(t('settings.deleteAccountSuccess').replace('{name}', targetUser.name));
    } catch (error) {
      console.error('[Tonyte][Admin] delete user failed', { targetUid: targetUser.id, error });
      setAdminRoleMessage(error instanceof Error ? error.message : t('settings.deleteAccountFailed'));
    } finally {
      setDeletingUserId(null);
    }
  }, [t]);

  const handleDeleteUser = useCallback((targetUser: AdminManagedUser) => {
    if (!canDeleteUser(targetUser)) {
      setAdminRoleMessage(
        targetUser.id === userId
          ? t('settings.cannotDeleteSelf')
          : isAdminRole(role) && targetUser.role === 'admin'
            ? t('settings.cannotDeleteAdmin')
            : t('profile.noRolePermission'),
      );
      return;
    }

    // Step 1: name the account and state plainly that this is permanent.
    Alert.alert(
      t('settings.deleteAccountTitle'),
      t('settings.deleteAccountConfirm1')
        .replace('{name}', targetUser.name)
        .replace('{email}', targetUser.email || t('settings.noEmailAvailable')),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('settings.deleteAccountAction'),
          style: 'destructive',
          onPress: () => {
            // Step 2: a second, explicit "are you sure" before the
            // irreversible call actually fires.
            Alert.alert(
              t('settings.deleteAccountFinalTitle'),
              t('settings.deleteAccountFinalBody'),
              [
                { text: t('common.cancel'), style: 'cancel' },
                {
                  text: t('settings.deleteAccountFinalAction'),
                  style: 'destructive',
                  onPress: () => void performDeleteUser(targetUser),
                },
              ],
            );
          },
        },
      ],
    );
  }, [canDeleteUser, performDeleteUser, role, t, userId]);

  const performToggleSuspend = useCallback(async (targetUser: AdminManagedUser, nextSuspended: boolean) => {
    setSuspendingUserId(targetUser.id);
    setAdminRoleMessage('');
    try {
      const fnName = nextSuspended ? 'restrictUserByAdminEu' : 'unrestrictUserByAdminEu';
      const fn = httpsCallable(functionsEu, fnName);
      const response = await fn({ uid: targetUser.id, email: targetUser.email });
      const result = response.data as { success?: boolean };
      if (!result?.success) throw new Error(nextSuspended ? t('settings.suspendAccountFailed') : t('settings.unsuspendAccountFailed'));
      setAdminUsers(current => current.map(u => (u.id === targetUser.id ? { ...u, restricted: nextSuspended } : u)));
      setAdminRoleMessage(
        (nextSuspended ? t('settings.suspendAccountSuccess') : t('settings.unsuspendAccountSuccess')).replace('{name}', targetUser.name),
      );
    } catch (error) {
      console.error('[Tonyte][Admin] toggle suspend failed', { targetUid: targetUser.id, nextSuspended, error });
      setAdminRoleMessage(
        error instanceof Error ? error.message : nextSuspended ? t('settings.suspendAccountFailed') : t('settings.unsuspendAccountFailed'),
      );
    } finally {
      setSuspendingUserId(null);
    }
  }, [t]);

  const handleToggleSuspend = useCallback((targetUser: AdminManagedUser) => {
    if (!canModerateUser(targetUser)) {
      setAdminRoleMessage(targetUser.id === userId ? t('settings.cannotSuspendSelf') : t('profile.noRolePermission'));
      return;
    }
    if (!targetUser.email) {
      setAdminRoleMessage(t('settings.noEmailAvailable'));
      return;
    }

    const nextSuspended = !targetUser.restricted;
    Alert.alert(
      nextSuspended ? t('settings.suspendAccountTitle') : t('settings.unsuspendAccountTitle'),
      (nextSuspended ? t('settings.suspendAccountConfirm') : t('settings.unsuspendAccountConfirm')).replace('{name}', targetUser.name),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: nextSuspended ? t('settings.suspendAccountAction') : t('settings.unsuspendAccountAction'),
          style: nextSuspended ? 'destructive' : 'default',
          onPress: () => void performToggleSuspend(targetUser, nextSuspended),
        },
      ],
    );
  }, [canModerateUser, performToggleSuspend, t, userId]);

  const pickImageForPreview = useCallback(async (kind: 'profile' | 'cover') => {
    if (!userId || imageActionBusy) return;
    const result = await pickImageFromLibrary({ mediaType: 'photo', quality: 0.8 });
    const uri = result?.assets?.[0]?.uri;
    if (!uri) return;

    console.log('[Tonyte][ImagePreview] selected image', uri);
    setImageActionMessage('');
    setActiveImageViewer(null);
    setCoverActionsOpen(false);
    setPendingImagePreview({ kind, uri, aspectRatio: kind === 'profile' ? '1:1' : '8:5' });
    console.log('[Tonyte][ImagePreview] opening preview', {
      pendingImageType: kind,
      imagePreviewVisible: true,
    });
    return;
  }, [imageActionBusy, userId]);

  const applyPreviewImage = useCallback(async (uri: string) => {
    const kind = pendingImagePreview?.kind;
    if (!userId || !kind || kind === 'event' || imageActionBusy) return;

    console.log('[Tonyte][ImagePreview] modal closed by confirm', {
      pendingImageType: kind,
      uri,
    });
    setPendingImagePreview(null);
    setImageActionBusy(true);
    setImageActionMessage(kind === 'profile' ? t('profile.uploadingProfile') : t('profile.uploadingCover'));
    try {
      const { downloadUrl, storagePath } = await uploadUserImage(userId, uri, kind);
      if (kind === 'profile') {
        await updateProfile({
          photo: downloadUrl,
          photoPath: storagePath,
          profileImage: downloadUrl,
          profileImagePath: storagePath,
          profilePhoto: downloadUrl,
          profilePhotoPath: storagePath,
          profilePhotoURL: downloadUrl,
          profileComplete: true,
          isProfileComplete: true,
        } as ProfileUpdatePatch);
      } else {
        await updateProfile({
          coverPhoto: downloadUrl,
          coverPhotoPath: storagePath,
          coverImage: downloadUrl,
          coverImagePath: storagePath,
          coverPhotoURL: downloadUrl,
        } as ProfileUpdatePatch);
      }
      setImageActionMessage(kind === 'profile' ? t('profile.profileSaved') : t('profile.coverSaved'));
      setActiveImageViewer(null);
      setCoverActionsOpen(false);
    } catch (error) {
      Alert.alert(t('profile.imageUpdateFailed'), error instanceof Error ? error.message : t('common.tryAgain'));
      setImageActionMessage('');
    } finally {
      setImageActionBusy(false);
    }
  }, [imageActionBusy, pendingImagePreview?.kind, t, updateProfile, userId]);

  const cancelImagePreview = useCallback(() => {
    console.log('[Tonyte][ImagePreview] modal closed by cancel', {
      pendingImageType: pendingImagePreview?.kind ?? null,
      pendingImageUri: pendingImagePreview?.uri ?? null,
    });
    setPendingImagePreview(null);
  }, [pendingImagePreview]);

  const removeImage = useCallback((kind: 'profile' | 'cover') => {
    Alert.alert(
      kind === 'profile' ? t('profile.removeProfilePhoto') : t('profile.removeCoverPhoto'),
      t('profile.restoreDefault'),
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: t('profile.remove'),
          style: 'destructive',
          onPress: async () => {
            setImageActionBusy(true);
            try {
              if (kind === 'profile') {
                await updateProfile({
                  photo: DEFAULT_AVATAR,
                  profileImage: DEFAULT_AVATAR,
                  profileImagePath: '',
                  profilePhoto: DEFAULT_AVATAR,
                  profilePhotoPath: '',
                  profilePhotoURL: DEFAULT_AVATAR,
                  photoPath: '',
                } as ProfileUpdatePatch);
              } else {
                await updateProfile({
                  coverPhoto: DEFAULT_COVER,
                  coverImage: DEFAULT_COVER,
                  coverImagePath: '',
                  coverPhotoURL: DEFAULT_COVER,
                  coverPhotoPath: '',
                } as ProfileUpdatePatch);
              }
              setActiveImageViewer(null);
              setCoverActionsOpen(false);
            } catch (error) {
              Alert.alert(t('profile.imageRemoveFailed'), error instanceof Error ? error.message : t('common.tryAgain'));
            } finally {
              setImageActionBusy(false);
            }
          },
        },
      ],
    );
  }, [t, updateProfile]);

  const toggleInterestSelection = useCallback((interest: string) => {
    setSelectedInterests(current => {
      if (current.includes(interest)) return current.filter(item => item !== interest);
      if (current.length >= 30) return current;
      return [...current, interest];
    });
  }, []);

  const clearSelectedInterests = useCallback(() => {
    setSelectedInterests([]);
    setInterestSearchQuery('');
  }, []);

  const addCustomInterest = useCallback(() => {
    if (!normalizedInterestSearch || hasExactInterestMatch) return;
    setSelectedInterests(current =>
      current.includes(normalizedInterestSearch) || current.length >= 30 ? current : [...current, normalizedInterestSearch],
    );
    setInterestSearchQuery('');
  }, [hasExactInterestMatch, normalizedInterestSearch]);

  const saveInterests = useCallback(async () => {
    const nextCustomInterests = selectedInterests.filter(interest => !BUILT_IN_INTEREST_KEYS.has(interest.trim().toLowerCase()));
    setSavingInterests(true);
    try {
      await updateProfile({
        interests: selectedInterests,
        customInterests: nextCustomInterests,
      });
      setInterestComposerOpen(false);
    } catch (error) {
      Alert.alert(t('profile.interestsSaveFailed'), error instanceof Error ? error.message : t('common.tryAgain'));
    } finally {
      setSavingInterests(false);
    }
  }, [selectedInterests, t, updateProfile]);

  const openSidebarModal = useCallback((modal: Exclude<SidebarModalKey, null>) => {
    setSettingsDrawerOpen(false);
    setActiveSidebarModal(modal);
    if (modal === 'faq') {
      setFaqSearchQuery('');
      setOpenFaqIndex(0);
    }
  }, []);

  const closeSidebarModal = useCallback(() => {
    if (isDeletingAccount) return;
    setActiveSidebarModal(null);
    setPasswordResetMessage('');
    setBlockedAccountsMessage('');
    setFeedbackSubmitMessage('');
    setStudentVerificationMessage('');
    setDeleteAccountError('');
    setDeleteAccountPassword('');
  }, [isDeletingAccount]);

  const loadBlockedAccounts = useCallback(async () => {
    if (!userId) return;
    setBlockedAccountsLoading(true);
    setBlockedAccountsMessage('');
    try {
      const [blocksSnapshot, compatBlocksSnapshot] = await Promise.all([
        getDocs(collection(firestore, FIRESTORE_COLLECTIONS.users, userId, 'blockedUsers')),
        getDocs(query(collection(firestore, FIRESTORE_COLLECTIONS.blocks), where('blockerId', '==', userId))).catch(() => ({ docs: [] })),
      ]);
      const blockedIds = [
        ...blocksSnapshot.docs.map(blockDoc => blockDoc.data().blockedUserId || blockDoc.id),
        ...compatBlocksSnapshot.docs.map(blockDoc => blockDoc.data().blockedId),
      ]
        .filter((blockedId): blockedId is string => typeof blockedId === 'string' && blockedId.trim().length > 0);
      const uniqueBlockedIds = Array.from(new Set(blockedIds));
      const profiles = await Promise.all(uniqueBlockedIds.map(async blockedId => {
        try {
          const userSnapshot = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, blockedId));
          const data = userSnapshot.exists() ? userSnapshot.data() : {};
          return {
            id: blockedId,
            name: String(data.username || data.displayName || data.name || t('profile.tonyteTraveler')),
            username: String(data.username || ''),
            photo: String(data.profilePhotoURL || data.profilePhoto || data.photoURL || data.profileImage || data.avatar || data.photo || DEFAULT_AVATAR),
          };
        } catch {
          return { id: blockedId, name: t('profile.tonyteTraveler'), username: '', photo: DEFAULT_AVATAR };
        }
      }));
      setBlockedAccounts(profiles.sort((left, right) => left.name.localeCompare(right.name)));
    } catch (error) {
      console.error('[Tonyte][RN][Blocks] failed to load blocked accounts', error);
      setBlockedAccounts([]);
      setBlockedAccountsMessage(t('profile.blockedAccountsLoadFailed'));
    } finally {
      setBlockedAccountsLoading(false);
    }
  }, [t, userId]);

  const unblockAccount = useCallback(async (targetUserId: string) => {
    if (!userId) return;
    setPendingUnblockUserId(targetUserId);
    setBlockedAccountsMessage('');
    try {
      await unblockUser(userId, targetUserId);
      setBlockedAccounts(current => current.filter(account => account.id !== targetUserId));
    } catch (error) {
      console.error('[Tonyte][RN][Blocks] failed to unblock account', { targetUserId, error });
      setBlockedAccountsMessage(t('profile.unblockFailed'));
    } finally {
      setPendingUnblockUserId(null);
    }
  }, [t, userId]);

  const sendPasswordReset = useCallback(async () => {
    const email = auth.currentUser?.email || profile?.email || '';
    if (!email) {
      setPasswordResetMessage(t('profile.noConnectedEmail'));
      return;
    }
    setPasswordResetSending(true);
    setPasswordResetMessage('');
    try {
      await callFunctionViaFetch(
        'https://europe-west3-triptribe-8babd.cloudfunctions.net/requestPasswordResetEmailEu',
        { email, continueUrl: PASSWORD_RESET_ACTION_CODE_SETTINGS.url },
      );
      setPasswordResetMessage(t('profile.passwordResetSent').replace('{email}', email));
    } catch {
      setPasswordResetMessage(t('profile.passwordResetFailed'));
    } finally {
      setPasswordResetSending(false);
    }
  }, [profile?.email, t]);

  const toggleNotificationPreference = useCallback(async (key: 'notificationsEnabled' | 'chatNotificationsEnabled' | 'eventNotificationsEnabled' | 'eventRecommendationNotificationsEnabled' | 'partnerNotificationsEnabled' | 'profileReminderNotificationsEnabled' | 'reviewReminderNotificationsEnabled' | 'feedbackReminderNotificationsEnabled', value: boolean) => {
    setNotificationSaving(true);
    try {
      if (value) {
        await requestNotificationPermission();
      }
      await updateProfile({ [key]: value } as ProfileUpdatePatch);
    } catch (error) {
      Alert.alert(t('profile.notificationsSaveFailed'), error instanceof Error ? error.message : t('common.tryAgain'));
    } finally {
      setNotificationSaving(false);
    }
  }, [t, updateProfile]);

  const submitStudentVerification = useCallback(async () => {
    const nextEmail = studentEmailInput.trim().toLowerCase();
    if (!nextEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
      setStudentVerificationMessage(t('profile.enterValidStudentEmail'));
      return;
    }
    setStudentVerificationSaving(true);
    setStudentVerificationMessage('');
    try {
      await callSendVerificationEmail(nextEmail);
      setStudentVerificationMessage(
        t('profile.studentVerificationEmailSent'),
      );
    } catch (error) {
      setStudentVerificationMessage(
        error instanceof Error ? error.message : t('profile.studentVerificationEmailFailed'),
      );
    } finally {
      setStudentVerificationSaving(false);
    }
  }, [studentEmailInput, t]);

  const approveVerification = useCallback(async (requestId: string, saveDomain?: boolean) => {
    setStudentVerifActionSaving(requestId);
    try {
      await callApproveVerificationRequest(requestId, saveDomain);
    } catch (error) {
      console.error('[Tonyte][Admin] approve verification failed', error);
    } finally {
      setStudentVerifActionSaving(null);
    }
  }, []);

  const denyVerification = useCallback(async (requestId: string) => {
    setStudentVerifActionSaving(requestId);
    try {
      await callDenyVerificationRequest(requestId);
    } catch (error) {
      console.error('[Tonyte][Admin] deny verification failed', error);
    } finally {
      setStudentVerifActionSaving(null);
    }
  }, []);

  const submitFeedback = useCallback(async () => {
    const message = feedbackMessage.trim();
    const type = feedbackType.trim();
    const email = profile?.email || auth.currentUser?.email || feedbackEmail.trim();
    if (!message) {
      setFeedbackSubmitMessage(t('profile.feedbackMessageRequired'));
      return;
    }
    if (!type) {
      setFeedbackSubmitMessage(t('profile.feedbackTypeRequired'));
      return;
    }
    setFeedbackSubmitting(true);
    setFeedbackSubmitMessage('');
    const feedbackPayload = {
      userId,
      userEmail: email,
      feedbackType: type,
      message,
      createdAt: serverTimestamp(),
      platform: 'react-native',
      appVersion: '1.0.0',
    };
    try {
      await addDoc(collection(firestore, 'feedback'), feedbackPayload);
      await addDoc(collection(firestore, 'mail'), {
        to: ['tonyte.official@gmail.com'],
        message: {
          subject: `Tonyte Feedback: ${type}`,
          text: `User: ${email}\nUser ID: ${userId || ''}\nType: ${type}\n\nMessage:\n${message}`,
        },
      }).catch(() => undefined);
      setFeedbackSubmitMessage(t('profile.feedbackThanks'));
      setFeedbackMessage('');
    } catch (error) {
      console.error('[Tonyte][RN][Feedback] failed to submit feedback', error);
      setFeedbackSubmitMessage(t('profile.feedbackSaveFailed'));
    } finally {
      setFeedbackSubmitting(false);
    }
  }, [feedbackEmail, feedbackMessage, feedbackType, profile?.email, t, userId]);

  const reauthenticateForAccountDeletion = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error(t('profile.noSignedInUser'));

    if (deletionProvider === 'password') {
      if (!currentUser.email) throw new Error(t('profile.missingEmailForConfirmation'));
      if (!deleteAccountPassword.trim()) throw new Error(t('profile.enterPasswordToContinue'));
      const credential = EmailAuthProvider.credential(currentUser.email, deleteAccountPassword);
      await reauthenticateWithCredential(currentUser, credential);
      return;
    }

    if (deletionProvider === 'google.com') {
      if (Platform.OS === 'web') {
        // No native Google Sign-In SDK in a browser (see web-stubs/googleSignIn.ts,
        // which always throws) — reauthenticateWithPopup runs the whole OAuth
        // round trip against the SAME Firebase project/GoogleAuthProvider config
        // native uses, same as login's appleSignIn.web.ts/googleSignIn.web.ts.
        let reauthResult;
        try {
          reauthResult = await reauthenticateWithPopup(currentUser, new GoogleAuthProvider());
        } catch (error) {
          const code = typeof error === 'object' && error !== null && 'code' in error
            ? String((error as { code?: unknown }).code || '')
            : '';
          if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
            const cancelError = new Error(t('profile.deleteReauthInterrupted'));
            (cancelError as Error & { code?: string }).code = 'SIGN_IN_CANCELLED';
            throw cancelError;
          }
          throw error;
        }
        if (reauthResult.user.uid !== currentUser.uid) {
          const mismatchError = new Error(t('profile.deleteUserMismatch'));
          (mismatchError as Error & { code?: string }).code = 'auth/user-mismatch';
          throw mismatchError;
        }
        return;
      }
      const googleSignin = getGoogleSignin();
      if (!googleSignin) throw new Error(t('profile.googleReauthUnavailable'));
      googleSignin.configure({
        webClientId: WEB_CLIENT_ID,
        iosClientId: IOS_CLIENT_ID,
        offlineAccess: false,
        scopes: ['profile', 'email'],
      });
      await googleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true }).catch(() => undefined);
      const response = await googleSignin.signIn();
      if (response.type === 'cancelled') {
        const cancelError = new Error(t('profile.deleteReauthInterrupted'));
        (cancelError as Error & { code?: string }).code = 'SIGN_IN_CANCELLED';
        throw cancelError;
      }
      let idToken = response.data.idToken;
      if (!idToken) {
        const tokens = await googleSignin.getTokens();
        idToken = tokens.idToken;
      }
      if (!idToken) throw new Error(t('profile.googleMissingIdToken'));
      const credential = GoogleAuthProvider.credential(idToken);
      const reauthResult = await reauthenticateWithCredential(currentUser, credential);
      if (reauthResult.user.uid !== currentUser.uid) {
        const mismatchError = new Error(t('profile.deleteUserMismatch'));
        (mismatchError as Error & { code?: string }).code = 'auth/user-mismatch';
        throw mismatchError;
      }
      return;
    }

    if (deletionProvider === 'apple.com') {
      if (Platform.OS === 'web') {
        // Mirrors the google.com web branch above — @invertase/react-native-apple-authentication
        // has no browser build (web-stubs/appleAuthentication.ts always throws),
        // so reauthenticate via the same Firebase popup flow appleSignIn.web.ts
        // uses for login.
        let reauthResult;
        try {
          reauthResult = await reauthenticateWithPopup(currentUser, new OAuthProvider('apple.com'));
        } catch (error) {
          const code = typeof error === 'object' && error !== null && 'code' in error
            ? String((error as { code?: unknown }).code || '')
            : '';
          if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
            const cancelError = new Error(t('profile.deleteReauthInterrupted'));
            (cancelError as Error & { code?: string }).code = '1001';
            throw cancelError;
          }
          throw error;
        }
        if (reauthResult.user.uid !== currentUser.uid) {
          const mismatchError = new Error(t('profile.deleteUserMismatch'));
          (mismatchError as Error & { code?: string }).code = 'auth/user-mismatch';
          throw mismatchError;
        }
        return;
      }
      if (!appleAuth.isSupported) throw new Error(t('profile.appleReauthUnavailable'));
      const appleResponse = await appleAuth.performRequest({
        requestedOperation: appleAuth.Operation.LOGIN,
        requestedScopes: [appleAuth.Scope.EMAIL, appleAuth.Scope.FULL_NAME],
      });
      if (!appleResponse.identityToken) throw new Error(t('profile.appleMissingIdentityToken'));
      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({
        idToken: appleResponse.identityToken,
        rawNonce: appleResponse.nonce,
      });
      await reauthenticateWithCredential(currentUser, credential);
      return;
    }

    throw new Error(t('profile.signInAgainBeforeDelete'));
  }, [deleteAccountPassword, deletionProvider, t]);

  const deleteAccount = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setDeleteAccountError(t('profile.mustSignInToDelete'));
      return;
    }

    const currentUserId = currentUser.uid;
    let deletionStage = 'not-started';

    try {
      setIsDeletingAccount(true);
      setDeleteAccountError('');

      deletionStage = 'reauth';
      await reauthenticateForAccountDeletion();

      const reauthenticatedUser = auth.currentUser;
      if (!reauthenticatedUser) throw new Error('Reauthenticated user session disappeared before cleanup.');

      deletionStage = 'cleanup http cleanup';
      const idToken = await reauthenticatedUser.getIdToken(true);
      const cleanupResponse = await fetch('https://europe-west3-triptribe-8babd.cloudfunctions.net/deleteAccountHttpEu', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: null }),
      });
      const cleanupBody = await cleanupResponse.json().catch(() => null);
      if (!cleanupResponse.ok || cleanupBody?.error) {
        const cleanupError = Object.assign(
          new Error(cleanupBody?.error?.message || cleanupResponse.statusText || t('profile.accountCleanupFailed')),
          {
            code: cleanupBody?.error?.status || `http/${cleanupResponse.status}`,
            deletionStage,
            details: cleanupBody?.error?.details || null,
          },
        );
        throw cleanupError;
      }

      // The Firebase Authentication account is now deleted server-side, as
      // the last stage of deleteAccountHttp's cleanup (see
      // functions/accountDeletion.js deleteAuthUser) — more reliable than a
      // second client-side deleteUser() call, which would depend on this
      // device's ID token still satisfying Firebase's "recent login"
      // requirement at the exact moment it runs. Reauthentication already
      // happened above, immediately before the cleanup request, which is
      // what authorized the server-side deletion.
      deletionStage = 'local sign-out';
      await AsyncStorage.clear().catch(() => undefined);
      await Promise.resolve(onSignOut()).catch(() => undefined);
      setActiveSidebarModal(null);
      setDeleteAccountPassword('');
    } catch (error) {
      console.error('[Tonyte][RN][AccountDeletion] failed', {
        userId: currentUserId,
        deletionStage,
        error,
      });
      setDeleteAccountError(getDeletionErrorMessage(Object.assign(
        error instanceof Error ? error : new Error(t('profile.deleteUnavailable')),
        { deletionStage },
      ), t));
    } finally {
      setIsDeletingAccount(false);
    }
  }, [onSignOut, reauthenticateForAccountDeletion, t]);

  useEffect(() => {
    if (activeSidebarModal === 'blockedAccounts') {
      loadBlockedAccounts();
    }
  }, [activeSidebarModal, loadBlockedAccounts]);

  // Approving/rejecting a Vibe Verified request — including deleting the
  // submitted selfie — is handled entirely server-side by the
  // reviewFaceVerification Cloud Function (functions/faceVerification.js).
  // This client only ever calls that function and waits for its result; it
  // never writes faceVerificationRequests/users directly (firestore.rules
  // denies that write regardless), and never marks the request
  // approved/rejected in local state ahead of the server confirming — the
  // request simply disappears from `pendingFaceVerifications` on its own
  // once Firestore reflects the new status, because that list's query
  // already filters to status == 'pending'.
  const reviewFaceVerification = useCallback(async (requestItem: FaceVerificationRequest, decision: 'approved' | 'rejected') => {
    if (!isAdminOrCoAdmin(role) || !requestItem.userId || faceVerifActionSaving) return;
    setFaceVerifActionSaving(requestItem.id);
    try {
      // Diagnostic instrumentation (temporary — see functions/internal
      // investigation): isolates whether a failure happens fetching the ID
      // token the Functions SDK attaches as the Authorization header (a
      // step separate from whatever Firestore's own auth wiring does) vs.
      // failing later, inside httpsCallable's own request dispatch.
      const tokenStartedAt = Date.now();
      let tokenPreview = 'none';
      try {
        const token = await auth.currentUser?.getIdToken();
        tokenPreview = token ? `${token.length}chars` : 'empty';
      } catch (tokenError) {
        console.error('[Tonyte][RN][FaceVerification] getIdToken failed before call', {
          requestId: requestItem.id,
          uid: auth.currentUser?.uid,
          elapsedMs: Date.now() - tokenStartedAt,
          error: tokenError,
        });
        throw tokenError;
      }
      console.log('[Tonyte][RN][FaceVerification] getIdToken ok, calling reviewFaceVerificationEu', {
        requestId: requestItem.id,
        uid: auth.currentUser?.uid,
        tokenPreview,
        elapsedMs: Date.now() - tokenStartedAt,
      });

      const callStartedAt = Date.now();
      // Temporary: @firebase/functions' postJSON() catches ANY fetch()
      // rejection — real network error or not — and converts it to a bare
      // {status:0}, which callAtURL then reports as a generic
      // FunctionsError('internal', ...) with no message/stack. Intercepting
      // fetch itself for the scope of this one call is the only way to see
      // what fetch() actually threw before that swallowing happens.
      const originalFetch = globalThis.fetch;
      globalThis.fetch = (async (...args: Parameters<typeof fetch>) => {
        const [fetchUrl, fetchInit] = args;
        const headers = (fetchInit?.headers || {}) as Record<string, string>;
        console.log('[Tonyte][RN][FaceVerification] fetch() invoked by Functions SDK', {
          requestId: requestItem.id,
          url: fetchUrl,
          headerKeys: Object.keys(headers),
          authHeaderLength: headers.Authorization?.length,
        });
        try {
          const res = await originalFetch(...args);
          console.log('[Tonyte][RN][FaceVerification] fetch() resolved', { requestId: requestItem.id, status: res.status });
          return res;
        } catch (fetchError) {
          console.error('[Tonyte][RN][FaceVerification] fetch() itself threw', {
            requestId: requestItem.id,
            name: fetchError instanceof Error ? fetchError.name : typeof fetchError,
            message: fetchError instanceof Error ? fetchError.message : String(fetchError),
            stack: fetchError instanceof Error ? fetchError.stack : undefined,
          });
          throw fetchError;
        }
      }) as typeof fetch;

      let response;
      try {
        const reviewFn = httpsCallable(functionsEu, 'reviewFaceVerificationEu');
        response = await reviewFn({ requestId: requestItem.id, decision });
      } finally {
        globalThis.fetch = originalFetch;
      }
      console.log('[Tonyte][RN][FaceVerification] reviewFaceVerificationEu responded', {
        requestId: requestItem.id,
        elapsedMs: Date.now() - callStartedAt,
      });
      const result = response.data as { success?: boolean; message?: string; idempotent?: boolean };
      if (!result?.success) {
        throw new Error(result?.message || t('profile.verificationReviewFailed'));
      }
      setAdminRoleMessage(decision === 'approved'
        ? t('profile.vibeVerifiedApproved').replace('{user}', requestItem.userName || requestItem.userEmail || t('common.user'))
        : t('profile.verificationClearSelfieFailed'));
    } catch (error) {
      console.error('[Tonyte][RN][FaceVerification] review failed', {
        requestId: requestItem.id,
        decision,
        uid: auth.currentUser?.uid,
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
      });
      Alert.alert(t('profile.reviewFailed'), t('profile.unchanged'));
    } finally {
      setFaceVerifActionSaving(null);
    }
  }, [faceVerifActionSaving, profile?.id, role, t]);

  // `requestId` is passed by the Pending Face Verification queue, which
  // already has the exact request doc on screen (`req.id`) — the backend
  // uses it as the sole source of truth for which faceVerificationRequests
  // doc to mark revoked, instead of the general list's fallback lookup via
  // users/{uid}.latestFaceVerificationRequestId (stale/unset for an account
  // with more than one historical request, which is what was silently
  // leaving the request stuck showing in the queue after a successful
  // revoke — the user-doc flags were correctly cleared, but the request
  // doc's status never left 'auto_approved', so the queue's own
  // `status in ['pending','auto_approved']` listener kept it in view).
  const revokeFaceBadge = useCallback(async (targetUserId: string, requestId?: string) => {
    if (!isAdminOrCoAdmin(role)) {
      // This button is only ever rendered for an admin/co-admin (see its two
      // call sites) — this firing at all would itself be a bug.
      console.warn('[Tonyte][Admin] revoke face badge blocked: caller role is not admin/co-admin', { targetUserId, role });
      Alert.alert(t('profile.revokeFailed'), t('common.tryAgain'));
      return;
    }
    setRevokingBadgeUserId(targetUserId);
    setRevokeFeedback(null);
    try {
      const revokeFn = httpsCallable(functionsEu, 'revokeVibeVerificationEu');
      // The SDK's own request can, in some failure modes (a region/IAM
      // misconfiguration at the infrastructure layer, a stalled connection),
      // never settle at all — leaving `await revokeFn` pending forever with
      // nothing for the catch block below to ever catch. Racing it against a
      // timeout turns that failure mode into a visible message instead of
      // permanent silence.
      const timeout = new Promise<never>((_, reject) => {
        setTimeout(() => reject(Object.assign(new Error('Timed out waiting for a response.'), { code: 'client/timeout' })), 12000);
      });
      const response = await Promise.race([revokeFn({ targetUserId, requestId }), timeout]);
      const result = response.data as { success?: boolean };
      if (!result?.success) {
        throw new Error(t('profile.revokeNotConfirmed'));
      }
      setAdminUsers(current => current.map(u =>
        u.id === targetUserId ? { ...u, faceVerified: false } : u,
      ));
      // Remove it from the review queue immediately rather than waiting on
      // the live listener to re-run — belt-and-suspenders with the server
      // now correctly updating the request doc's own status above, so this
      // never has anything to re-add it back on the next snapshot.
      setPendingFaceVerifications(current => current.filter(req => req.userId !== targetUserId));
      Alert.alert(t('profile.revokeSuccessTitle'), t('profile.revokeSuccessBody'));
    } catch (error) {
      // Surfacing the actual error code/message (e.g. 'functions/not-found'
      // vs 'permission-denied' vs 'internal') is what makes a silent-seeming
      // failure diagnosable — generic copy alone gives no way to tell a
      // missing/undeployed function apart from a permissions problem apart
      // from a genuine server error.
      const code = error && typeof error === 'object' && 'code' in error ? String((error as { code?: unknown }).code) : undefined;
      const message = error instanceof Error ? error.message : String(error);
      console.error('[Tonyte][Admin] revoke face badge failed', { targetUserId, requestId, code, message, error });
      Alert.alert(t('profile.revokeFailed'), code ? `${code}: ${message}` : message || t('common.tryAgain'));
      // Alert.alert has had known stacking inconsistency over an open native
      // <Modal> on Android (both buttons live inside one) — this inline,
      // in-modal fallback guarantees the failure is visible even if that
      // alert doesn't render on top.
      setRevokeFeedback({ userId: targetUserId, message: code ? `${code}: ${message}` : message || t('common.tryAgain') });
    } finally {
      setRevokingBadgeUserId(null);
    }
  }, [role, t]);

  const profileShareLink = useMemo(() => buildTonyteProfileLink(userId), [userId]);

  const shareProfile = useCallback(() => {
    if (!profileShareLink) {
      Alert.alert(t('profile.shareFailed'), t('profile.missingShareId'));
      return;
    }
    Share.share({
      title: t('profile.shareTitle'),
      message: t('profile.shareMessage').replace('{url}', profileShareLink),
      url: profileShareLink,
    }).catch(() => Alert.alert(t('profile.shareFailed'), t('profile.shareUnavailable')));
  }, [profileShareLink, t]);

  const openProfileQr = useCallback(() => {
    if (!profileShareLink) {
      Alert.alert(t('profile.qrUnavailable'), t('profile.missingShareId'));
      return;
    }
    setQrModalOpen(true);
  }, [profileShareLink, t]);

  if (loading) return <LoadingSpinner />;

  const studentStatus = profile?.studentVerificationStatus || 'unverified';
  const verifiedStudent = isVerifiedStudent(profile);
  const isStudentVerificationPending = studentStatus === 'pending' || studentStatus === 'pending_admin_review' || studentStatus === 'email_ownership_confirmed';
  const isStudentEmailVerificationSent = studentStatus === 'email_verification_sent' || studentStatus === 'email_sent';
  const isStudentVerificationFailed = studentStatus === 'failed' || studentStatus === 'denied' || studentStatus === 'rejected' || studentStatus === 'expired';
  const studentUniversityLabel = getStudentUniversityLabel(profile);
  const faceVerificationStatus = profile?.isVibeVerified || profile?.faceVerified
    ? 'approved'
    : profile?.faceVerificationStatus || 'not_started';
  const isVibeVerified = faceVerificationStatus === 'approved';
  const isFaceVerificationRejected = faceVerificationStatus === 'rejected';
  const isFaceVerificationPending = faceVerificationStatus === 'pending';
  const plansCount = myPosts.filter(post => post.entryType === 'community_plan').length;
  const planPosts = connectedPlans.slice(0, 8);
  // Published events only: the queries above return everything this user
  // created or hosts, including unpublished calendar-import drafts (reviewed
  // and published from Calendar Imports, not listed as upcoming here).
  const eventPosts = sortUpcomingActivities(
    myPosts.filter(post => post.entryType === 'official_event'
      && post.publishStatus !== 'draft'
      && post.visibility !== 'private'
      && post.approved !== false
      && post.status !== 'cancelled'
      && isLiveOrUpcomingActivity(post)),
  ).slice(0, 8);
  const interestedEventPosts = interestedEvents.slice(0, 8);
  // Only ever the user's real aura for today — never an invented default.
  const currentAuraLabel = todaysAura ? getLocalizedAuraName(todaysAura.auraType, t) : null;
  const visibleBadges = [
    isVibeVerified ? { icon: 'check-circle', label: t('profile.vibeVerified'), color: Colors.turquoise } : null,
    verifiedStudent ? { icon: 'award', label: t('profile.studentVerified'), color: '#a78bfa' } : null,
    { icon: 'star', label: t('profile.earlyMember'), color: '#facc15' },
    plansCount >= 3 ? { icon: 'trending-up', label: t('profile.topPlanner'), color: Colors.sunset } : null,
    canPublishEvents(role) ? { icon: 'mic', label: t('profile.eventHost'), color: '#fb7185' } : null,
  ].filter(Boolean) as Array<{ icon: string; label: string; color: string }>;
  const profileAgeLocation = [
    typeof profile?.age === 'number' && profile.age > 0 ? String(profile.age) : '',
    [city, country].filter(Boolean).join(', '),
  ].filter(Boolean).join(' · ');

  const confirmSignOut = () => {
    setSignOutConfirmOpen(true);
  };

  const scrollView = (
      <Animated.ScrollView
        style={styles.scroll}
        scrollEnabled
        nestedScrollEnabled
        contentContainerStyle={[styles.scrollContent, isDesktopWeb && styles.desktopScrollContent, { paddingBottom: isDesktopWeb ? 36 : profileBottomPadding }]}
        contentInsetAdjustmentBehavior="never"
        automaticallyAdjustContentInsets={false}
        scrollIndicatorInsets={{ bottom: profileBottomPadding }}
        showsVerticalScrollIndicator={false}
      >
        <TouchableOpacity style={[styles.premiumCoverWrap, isDesktopWeb && styles.desktopCoverWrap, appearance.coverWrap]} activeOpacity={0.92} onPress={() => setCoverActionsOpen(true)}>
          <Image
            source={{ uri: renderedCoverImage }}
            style={styles.premiumCover}
            resizeMode="cover"
          />
          <LinearGradient
            colors={theme.isDark
              ? ['rgba(0,0,0,0.05)', 'rgba(7,11,20,0.62)', Colors.bg]
              : ['rgba(0,0,0,0.04)', 'rgba(248,244,236,0.36)', theme.colors.background]}
            locations={[0, 0.58, 1]}
            style={StyleSheet.absoluteFill}
          />
          <View style={styles.coverGlowBlue} />
          <View style={styles.coverGlowPurple} />
          <View style={[styles.topHeader, { top: topHeaderOffset }]}> 
            <PhysicalLightSwitch />
            <TouchableOpacity
              style={styles.topButton}
              onPress={() => setSettingsDrawerOpen(true)}
              activeOpacity={0.78}
            >
              <Icon name="settings" size={18} color="#fff" />
            </TouchableOpacity>
          </View>
        </TouchableOpacity>

        <Animated.View
          style={[
            styles.premiumBody,
            isDesktopWeb && styles.desktopPremiumBody,
            {
              opacity: profileEntrance,
              transform: [{ translateY: profileEntrance.interpolate({ inputRange: [0, 1], outputRange: [22, 0] }) }],
            },
          ]}
        >
          <View style={styles.premiumIdentity}>
            <View style={styles.avatarCenterWrap}>
              <TouchableOpacity
                style={styles.premiumAvatarWrap}
                activeOpacity={0.9}
                onPress={() => setActiveImageViewer('profile')}
                accessibilityLabel={profile?.isVibeVerified === true ? `${displayName}, Vibe Verified` : displayName}
              >
                <Image
                  source={{ uri: renderedProfileImage }}
                  style={styles.premiumAvatar}
                  resizeMode="cover"
                />
                {/* Default bottom-right position would collide with the
                    camera-edit button that already lives there — shifted
                    to top-right for this specific avatar only. */}
                <VibeVerifiedBadge
                  verified={profile?.isVibeVerified === true}
                  size={150}
                  style={styles.premiumAvatarBadgePosition}
                />
                <TouchableOpacity
                  style={styles.premiumCameraButton}
                  onPress={() => pickImageForPreview('profile')}
                  activeOpacity={0.78}
                >
                  <Icon name="camera" size={17} color="#fff" />
                </TouchableOpacity>
              </TouchableOpacity>
            </View>

            <View style={styles.premiumNameBlock}>
              <Text style={[styles.premiumName, appearance.textPrimary]}>{displayName}</Text>
              {profile?.username ? <Text style={[styles.premiumUsername, appearance.textMuted]}>@{profile.username}</Text> : null}
            </View>

            <TouchableOpacity style={[styles.premiumLocationRow, appearance.chipSurface]} activeOpacity={0.78} onPress={() => setCityPickerOpen(true)}>
              <Icon name="map-pin" size={14} color={Colors.sunset} />
              <Text style={[styles.premiumLocationText, appearance.textSecondary]}>{profileAgeLocation || basedInLabel}</Text>
            </TouchableOpacity>

            {profile?.dateOfBirth ? (
              <View style={styles.premiumDobRow}>
                <Icon name="gift" size={12} color={theme.colors.iconMuted} />
                <Text style={[styles.premiumDobText, appearance.textMuted]}>{formatDateOfBirth(profile.dateOfBirth, language)}</Text>
              </View>
            ) : null}

            <Text
              style={[
                styles.premiumBio,
                appearance.textSecondary,
                appearance.bioText,
                !profile?.bio && styles.aboutEmpty,
                !profile?.bio && appearance.bioPlaceholder,
              ]}
            >
              {aboutText}
            </Text>

            <View style={styles.profileActionRow}>
              <TouchableOpacity
                onPress={() => navigation.navigate('EditProfile')}
                style={[styles.premiumEditButton, styles.profileActionButton]}
                activeOpacity={0.82}
              >
                <Icon name="edit-3" size={16} color="#fff" />
                <Text style={styles.editButtonText} numberOfLines={1}>{t('profile.editProfile')}</Text>
              </TouchableOpacity>
              {ownedVenue ? (
                <TouchableOpacity
                  onPress={() => navigation.navigate('VenueProfile', { venueId: ownedVenue.id })}
                  style={[styles.profileActionButton, styles.partnerProfileButton, appearance.chipSurface]}
                  activeOpacity={0.82}
                  accessibilityLabel={`${t('profile.myVenueProfile')}: ${ownedVenue.name}`}
                >
                  <Icon name="map-pin" size={16} color="#FF9A3C" />
                  <Text style={[styles.partnerProfileText, appearance.textPrimary]} numberOfLines={1}>{t('profile.myVenueProfile')}</Text>
                </TouchableOpacity>
              ) : null}
            </View>

            <View style={styles.quickTiles}>
              <TouchableOpacity
                // Pairs with the StudiZone tile: same structure, teal/orange
                // palette instead of sky/violet.
                style={[styles.quickTile, styles.followingTile]}
                onPress={() => navigation.navigate('FollowingPartners')}
                activeOpacity={0.86}
              >
                <View style={styles.followingGlowA} />
                <View style={styles.followingGlowB} />
                <Text style={[styles.quickTileLabel, styles.followingTileLabel]}>{t('profile.following')}</Text>
                <Text style={[styles.quickTileValue, styles.followingTileValue]}>{followedPartnersLoading ? '…' : followedPartners.length}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                // Same size as the Following tile, in the StudiZone card's
                // original look (navy, sky-blue border, sky/violet glows).
                style={[styles.quickTile, styles.studiZoneTile]}
                onPress={() => navigation.navigate(verifiedStudent && profile?.studentVerificationStatus === 'verified' ? 'StudiZone' : 'VerificationCenter')}
                activeOpacity={0.86}
              >
                <View style={styles.studiZoneGlowA} />
                <View style={styles.studiZoneGlowB} />
                <Text style={[styles.quickTileLabel, styles.studiZoneTileLabel]}>StudiZone</Text>
                <Text style={[styles.quickTileStatus, styles.studiZoneTileStatus]} numberOfLines={1}>
                  {verifiedStudent
                    ? t('profile.studiZoneOpen')
                    : isStudentVerificationPending || isStudentEmailVerificationSent
                      ? t('profile.studiZonePending')
                      : isStudentVerificationFailed
                        ? t('common.tryAgain')
                        : t('profile.studiZoneVerify')}
                </Text>
              </TouchableOpacity>
            </View>

            <GlassCard style={styles.interestsCard}>
              <View style={styles.cardHeader}>
                <Text style={[styles.cardTitle, appearance.textPrimary]}>{t('profile.personality')}</Text>
                <TouchableOpacity onPress={() => setInterestComposerOpen(true)}>
                  <Text style={[styles.linkText, appearance.accentText, appearance.interestAccentText]}>{t('profile.viewAll')}</Text>
                </TouchableOpacity>
              </View>
              {/* One scrolling row (like the badges rail) so the card stays a
                  single row high however many interests there are. */}
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                style={styles.interestScroller}
                contentContainerStyle={styles.interestRow}
              >
                {interests.slice(0, 10).map((interest, idx) => (
                  <View key={`${interest}-${idx}`} style={[styles.interestChip, appearance.chipSurface, appearance.interestChipSurface]}>
                    <Icon name={interestIcon(interest)} size={12} color={theme.isDark ? interestColor(interest) : theme.colors.accentSecondary} />
                    <Text style={[styles.interestText, appearance.textSecondary, appearance.interestText]} numberOfLines={1}>{interest}</Text>
                  </View>
                ))}
                <TouchableOpacity
                  style={[styles.addInterestChip, appearance.addInterestChipSurface]}
                  onPress={() => setInterestComposerOpen(true)}
                  activeOpacity={0.78}
                >
                  <Icon name="plus" size={12} color={theme.colors.accentSecondary} />
                  <Text style={[styles.addInterestText, appearance.accentText, appearance.interestAccentText]}>{t('profile.addInterest')}</Text>
                </TouchableOpacity>
              </ScrollView>
            </GlassCard>


            <TodayVibeCard
              aura={currentAuraLabel}
              auraType={todaysAura?.auraType ?? null}
              auraLoaded={todaysAuraLoaded}
              matches={todayVibeMatchCount}
              loading={todayVibeMatchesLoading}
              // No answers yet today: straight to the questions.
              onPress={() => navigation.navigate(todaysAura ? 'DayVibesAura' : 'DayVibesQuestion')}
            />

            {/* Vibes + profile completion side by side, same height as the
                Following / StudiZone tiles. */}
            <View style={styles.quickTiles}>
              <TouchableOpacity
                style={[styles.vibesHubButton, styles.vibesHubTile, appearance.warmSurface]}
                activeOpacity={0.82}
                onPress={() => navigation.navigate('Vibes')}
                accessibilityLabel={`${t('profile.vibesHub')}: ${t('profile.vibesHubSub')}`}
              >
                <View style={[styles.vibesHubIcon, styles.vibesHubTileIcon]}>
                  <Icon name="zap" size={16} color="#fff" />
                </View>
                <Text style={[styles.vibesHubTitle, appearance.textPrimary]} numberOfLines={1}>{t('profile.vibesHub')}</Text>
              </TouchableOpacity>
              <ProfileCompletionPrompt
                tile
                score={profileCompletionScore}
                onPress={() => navigation.navigate(
                  'CompleteProfile',
                  profileCompletionScore >= 100 ? { editAll: true } : undefined,
                )}
              />
            </View>

            <View style={styles.shareRow}>
              <PillButton icon="share-2" label={t('profile.shareProfile')} onPress={shareProfile} />
              <PillButton icon="maximize" label={t('profile.qrCode')} onPress={openProfileQr} />
              <PillButton icon="user-check" label={t('profile.verifications')} onPress={() => navigation.navigate('VerificationCenter')} />
            </View>

            <View style={styles.compactBadgeRow}>
              {currentAuraLabel ? <ProfileBadgePill icon="zap" label={`${currentAuraLabel} ${t('profile.aura')}`} color={Colors.sunset} /> : null}
              {isVibeVerified ? <ProfileBadgePill icon="check-circle" label={t('profile.vibeVerified')} color={Colors.turquoise} /> : null}
              {verifiedStudent ? <ProfileBadgePill icon="award" label={t('profile.studentVerified')} color="#a78bfa" /> : null}
            </View>


          </View>

          <ProfileSectionHeader title={t('profile.myPlans')} />
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.horizontalRail}>
            {planPosts.length ? planPosts.map(plan => (
              <PlanCard
                key={plan.id}
                plan={plan}
                locale={locale}
                fallbackImage={renderedCoverImage}
                onPress={() => {
                  trackEventDetailOpen({ eventId: plan.id, organizerId: plan.hostId, source: ANALYTICS_SOURCES.PLAN });
                  navigation.navigate('EventDetail', { eventId: plan.id, event: plan });
                }}
              />
            )) : (
              <ProfileRailEmptyState label={connectedPlansLoading || postsLoading ? t('profile.loadingPlans') : t('profile.upcomingPlansEmpty')} />
            )}
            <CreatePlanCard label={t('profile.createPlan')} onPress={() => navigation.navigate('UploadPlan')} />
          </ScrollView>

          {canPublishEvents(role) ? (
            <>
              <ProfileSectionHeader title={t('profile.upcomingEvents')} />
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.horizontalRail}>
                {eventPosts.length ? eventPosts.map(event => (
                  <EventRailCard
                    key={getResolvedEventId(event)}
                    event={event}
                    locale={locale}
                    onPress={() => {
                      trackEventDetailOpen({ eventId: getResolvedEventId(event), organizerId: event.hostId, source: ANALYTICS_SOURCES.RECOMMENDATION });
                      navigation.navigate('EventDetail', { eventId: getResolvedEventId(event), event });
                    }}
                  />
                )) : (
                  <ProfileRailEmptyState label={postsLoading ? t('profile.loadingEvents') : t('profile.upcomingEventsEmpty')} />
                )}
                <CreatePlanCard label={t('profile.uploadEvent')} onPress={() => navigation.navigate('UploadEvent')} />
              </ScrollView>
            </>
          ) : null}

          <ProfileSectionHeader title={t('profile.interestedEvents')} />
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.horizontalRail}>
            {interestedEventPosts.length ? interestedEventPosts.map(event => (
              <EventRailCard
                key={getResolvedEventId(event)}
                event={event}
                locale={locale}
                onPress={() => {
                  trackEventDetailOpen({ eventId: getResolvedEventId(event), organizerId: event.hostId, source: ANALYTICS_SOURCES.RECOMMENDATION });
                  navigation.navigate('EventDetail', { eventId: getResolvedEventId(event), event });
                }}
              />
            )) : (
              <ProfileRailEmptyState label={interestedEventsLoading ? t('profile.loadingInterestedEvents') : t('profile.interestedEventsEmpty')} />
            )}
            <CreatePlanCard
              label={t('profile.browseEvents')}
              variant="teal"
              onPress={() => navigation.navigate('Main', { initialTab: 'Feed' })}
            />
          </ScrollView>

          <ProfileSectionHeader title={t('profile.badgesAchievements')} />
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.badgeRail}>
            {visibleBadges.map(badge => <AchievementPill key={badge.label} {...badge} />)}
          </ScrollView>

          {isAdminOrCoAdmin(role) ? (
            <GlassCard style={[styles.adminCard, isIOS && styles.adminCardIOS]}>
              <View style={styles.cardHeader}>
                <View style={styles.adminHeaderText}>
                  <Text style={[styles.adminEyebrow, appearance.textMuted]}>{t('profile.admin')}</Text>
                  <Text style={[styles.adminTitle, appearance.textPrimary]}>{t('settings.adminControls')}</Text>
                </View>
                <View style={[styles.adminBadge, appearance.chipSurface]}>
                  <Text style={[styles.adminBadgeText, appearance.accentText]} numberOfLines={1}>
                    {adminUsersLoading ? '…' : t('profile.usersCount').replace('{count}', String(adminUsers.length))}
                  </Text>
                </View>
              </View>
              <Text style={[styles.adminText, appearance.textMuted]}>
                {t('profile.adminHelp')}
              </Text>
              <TouchableOpacity
                style={[styles.adminManagementButton, appearance.chipSurface]}
                onPress={() => navigation.navigate('AddPartner')}
                activeOpacity={0.82}
              >
                <View style={[styles.adminManagementIcon, { backgroundColor: 'rgba(255,122,0,0.15)' }]}>
                  <Icon name="user-plus" size={18} color="#FF9A3C" />
                </View>
                <View style={styles.adminManagementCopy}>
                  <Text style={[styles.adminManagementTitle, appearance.textPrimary]}>Add partner</Text>
                  <Text style={[styles.adminManagementSubtitle, appearance.textMuted]}>Invite a partner and send their activation email</Text>
                </View>
                <Icon name="chevron-right" size={18} color={theme.colors.iconMuted} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.adminManagementButton, appearance.chipSurface]}
                onPress={() => setUserManagementOpen(true)}
                activeOpacity={0.82}
              >
                <View style={styles.adminManagementIcon}>
                  <Icon name="users" size={18} color="#e0f2fe" />
                </View>
                <View style={styles.adminManagementCopy}>
                  <Text style={[styles.adminManagementTitle, appearance.textPrimary]}>{t('profile.userManagement')}</Text>
                  <Text style={[styles.adminManagementSubtitle, appearance.textMuted]}>{t('profile.userManagementSub')}</Text>
                </View>
                <Icon name="chevron-right" size={18} color={theme.colors.iconMuted} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.adminManagementButton, appearance.chipSurface]}
                onPress={() => navigation.navigate('AdminPromotions')}
                activeOpacity={0.82}
                accessibilityRole="button"
              >
                <View style={[styles.adminManagementIcon, { backgroundColor: 'rgba(242,125,38,0.14)' }]}>
                  <Icon name="star" size={18} color={Colors.sunset} />
                </View>
                <View style={styles.adminManagementCopy}>
                  <Text style={[styles.adminManagementTitle, appearance.textPrimary]}>{t('promotionsAdmin.title')}</Text>
                  <Text style={[styles.adminManagementSubtitle, appearance.textMuted]}>{t('promotionsAdmin.profileSub')}</Text>
                </View>
                <Icon name="chevron-right" size={18} color={theme.colors.iconMuted} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.adminManagementButton, appearance.chipSurface]}
                onPress={() => navigation.navigate('AdminReviewReports')}
                activeOpacity={0.82}
                accessibilityRole="button"
              >
                <View style={[styles.adminManagementIcon, { backgroundColor: 'rgba(255,185,56,0.14)' }]}>
                  <Icon name="flag" size={18} color="#FFB938" />
                </View>
                <View style={styles.adminManagementCopy}>
                  <Text style={[styles.adminManagementTitle, appearance.textPrimary]}>{t('partnerReviews.admin.title')}</Text>
                  <Text style={[styles.adminManagementSubtitle, appearance.textMuted]}>{t('partnerReviews.admin.profileSub')}</Text>
                </View>
                <Icon name="chevron-right" size={18} color={theme.colors.iconMuted} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.adminManagementButton, appearance.chipSurface]}
                onPress={() => setStudentVerifModalOpen(true)}
                activeOpacity={0.82}
              >
                <View style={[styles.adminManagementIcon, { backgroundColor: 'rgba(38,208,206,0.15)' }]}>
                  <Icon name="award" size={18} color={Colors.turquoise} />
                </View>
                <View style={styles.adminManagementCopy}>
                  <Text style={[styles.adminManagementTitle, appearance.textPrimary]}>{t('profile.studentVerifications')}</Text>
                  <Text style={[styles.adminManagementSubtitle, appearance.textMuted]}>
                    {pendingVerificationsLoading ? t('profile.loading') : t('profile.pendingReviewCount').replace('{count}', String(pendingVerifications.length))}
                  </Text>
                </View>
                <Icon name="chevron-right" size={18} color={theme.colors.iconMuted} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.adminManagementButton, appearance.chipSurface]}
                onPress={() => setFaceVerifModalOpen(true)}
                activeOpacity={0.82}
              >
                <View style={[styles.adminManagementIcon, { backgroundColor: 'rgba(125,211,252,0.14)' }]}>
                  <Icon name="user-check" size={18} color="#7dd3fc" />
                </View>
                <View style={styles.adminManagementCopy}>
                  <Text style={[styles.adminManagementTitle, appearance.textPrimary]}>{t('profile.faceVerificationRequests')}</Text>
                  <Text style={[styles.adminManagementSubtitle, appearance.textMuted]}>
                    {pendingFaceVerificationsLoading ? t('profile.loading') : t('profile.pendingReviewCount').replace('{count}', String(pendingFaceVerifications.length))}
                  </Text>
                </View>
                <Icon name="chevron-right" size={18} color={theme.colors.iconMuted} />
              </TouchableOpacity>
            </GlassCard>
          ) : null}

          <View style={styles.contentBoxRow}>
            {canPublishEvents(role) ? (
              <TouchableOpacity
                style={[styles.contentBox, appearance.cardSurface]}
                onPress={() => userId && navigation.navigate('MyTemplates', { userId })}
                activeOpacity={0.82}
              >
                <LinearGradient
                  colors={['rgba(242,125,38,0.18)', 'rgba(242,125,38,0.06)']}
                  style={StyleSheet.absoluteFill}
                />
                <View style={styles.contentBoxIconWrap}>
                  <Icon name="copy" size={20} color={Colors.sunset} />
                </View>
                <Text style={styles.contentBoxCount}>
                  {templatesLoading ? '…' : savedTemplates.length}
                </Text>
                <Text style={[styles.contentBoxTitle, appearance.textPrimary]}>{t('profile.savedTemplates')}</Text>
                <Text style={[styles.contentBoxSub, appearance.textMuted]}>{t('profile.reusableEventDrafts')}</Text>
                <View style={styles.contentBoxArrow}>
                  <Icon name="chevron-right" size={14} color={theme.colors.iconFaint} />
                </View>
              </TouchableOpacity>
            ) : null}

            <TouchableOpacity
              style={[styles.contentBox, appearance.cardSurface, !canPublishEvents(role) && styles.contentBoxFull]}
              onPress={() => userId && navigation.navigate('MyPosts', { userId })}
              activeOpacity={0.82}
            >
              <LinearGradient
                colors={['rgba(38,208,206,0.16)', 'rgba(38,208,206,0.05)']}
                style={StyleSheet.absoluteFill}
              />
              <View style={[styles.contentBoxIconWrap, styles.contentBoxIconCyan]}>
                <Icon name="upload" size={20} color={Colors.turquoise} />
              </View>
              <Text style={[styles.contentBoxCount, styles.contentBoxCountCyan]}>
                {postsLoading ? '…' : plansCount}
              </Text>
              <Text style={[styles.contentBoxTitle, appearance.textPrimary]}>{t('profile.uploadedPlans')}</Text>
              <Text style={[styles.contentBoxSub, appearance.textMuted]}>{t('profile.yourCreatedPlans')}</Text>
              <View style={styles.contentBoxArrow}>
                <Icon name="chevron-right" size={14} color={theme.colors.iconFaint} />
              </View>
            </TouchableOpacity>
          </View>

          {adminPlanRequests.length > 0 || !planRequestsLoading ? (
            <TouchableOpacity
              style={[
                styles.contentBox,
                appearance.cardSurface,
                styles.contentBoxFull,
                Platform.OS === 'web' && styles.planRequestsCardWeb,
              ]}
              onPress={() => userId && navigation.navigate('PlanRequests', { userId })}
              activeOpacity={0.82}
            >
              <LinearGradient
                colors={['rgba(38,208,206,0.16)', 'rgba(38,208,206,0.05)']}
                style={StyleSheet.absoluteFill}
              />
              <View style={[styles.contentBoxIconWrap, styles.contentBoxIconCyan]}>
                <Icon name="users" size={20} color={Colors.turquoise} />
              </View>
              <Text style={[styles.contentBoxCount, styles.contentBoxCountCyan]}>
                {planRequestsLoading ? '…' : adminPlanRequests.length}
              </Text>
              <Text style={[styles.contentBoxTitle, appearance.textPrimary]}>{t('profile.planRequests')}</Text>
              <Text style={[styles.contentBoxSub, appearance.textMuted]}>{t('profile.pendingPlanRequests')}</Text>
              <View style={styles.contentBoxArrow}>
                <Icon name="chevron-right" size={14} color={theme.colors.iconFaint} />
              </View>
            </TouchableOpacity>
          ) : null}

          <TouchableOpacity style={[styles.signOutButton, appearance.signOutButton]} onPress={confirmSignOut} activeOpacity={0.78}>
            <Icon name="log-out" size={16} color={Colors.sunset} />
            <Text style={styles.signOutText}>{t('profile.signOut')}</Text>
          </TouchableOpacity>
        </Animated.View>
      </Animated.ScrollView>
  );

  const profileContent = scrollGesture ? (
    <GestureDetector gesture={scrollGesture}>{scrollView}</GestureDetector>
  ) : scrollView;

  return (
    <TonyteBackground appearanceAware style={[styles.container, appearance.root]}>
      {isIOS ? (
        <SafeAreaView edges={['top']} style={styles.safeArea}>
          {profileContent}
        </SafeAreaView>
      ) : profileContent}
      <TonyteModal
        visible={signOutConfirmOpen}
        title={t('profile.signOut')}
        message={t('profile.signOutConfirm')}
        icon="log-out"
        iconColor={Colors.sunset}
        buttons={[
          { label: t('profile.cancel'), variant: 'secondary', onPress: () => setSignOutConfirmOpen(false) },
          {
            label: t('profile.signOut'),
            variant: 'danger',
            onPress: () => {
              setSignOutConfirmOpen(false);
              onSignOut();
            },
          },
        ]}
        onDismiss={() => setSignOutConfirmOpen(false)}
      />
      <Modal
        visible={Boolean(activeImageViewer) && !pendingImagePreview}
        transparent
        animationType="fade"
        onRequestClose={() => setActiveImageViewer(null)}
      >
        <Pressable style={styles.viewerOverlay} onPress={() => setActiveImageViewer(null)}>
          <Pressable style={styles.viewerCard} onPress={() => undefined}>
            <Image
              source={{ uri: activeImageViewer === 'cover' ? renderedCoverImage : renderedProfileImage }}
              style={activeImageViewer === 'cover' ? styles.viewerCoverImage : styles.viewerProfileImage}
              resizeMode="cover"
            />
            {imageActionMessage ? <Text style={styles.viewerStatus}>{imageActionMessage}</Text> : null}
            <View style={styles.viewerActions}>
              <TouchableOpacity
                style={styles.viewerAction}
                disabled={imageActionBusy}
                onPress={() => activeImageViewer && pickImageForPreview(activeImageViewer)}
              >
                <Icon name="image" size={16} color="#fff" />
                <Text style={styles.viewerActionText}>{t('profile.change')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.viewerAction}
                disabled={imageActionBusy}
                onPress={() => activeImageViewer && removeImage(activeImageViewer)}
              >
                <Icon name="trash-2" size={16} color={Colors.sunset} />
                <Text style={[styles.viewerActionText, styles.viewerRemoveText]}>{t('profile.remove')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.viewerAction} onPress={() => setActiveImageViewer(null)}>
                <Icon name="x" size={16} color="rgba(255,255,255,0.75)" />
                <Text style={styles.viewerActionText}>{t('profile.close')}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {pendingImagePreview ? (
        <TonyteCropModal
          session={pendingImagePreview}
          onCrop={applyPreviewImage}
          onCancel={cancelImagePreview}
        />
      ) : null}

      <Modal
        visible={qrModalOpen}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => setQrModalOpen(false)}
      >
        <Pressable style={styles.qrOverlay} onPress={() => setQrModalOpen(false)}>
          <Pressable style={styles.qrCard} onPress={() => undefined}>
            <Text style={styles.qrKicker}>{t('profile.myQrCode')}</Text>
            <Text style={styles.qrTitle} numberOfLines={1}>{displayName}</Text>
            <View style={styles.qrFrame}>
              {profileShareLink ? (
                <QRCode
                  value={profileShareLink}
                  size={210}
                  backgroundColor="#ffffff"
                  color="#0f172a"
                  logoBackgroundColor="transparent"
                />
              ) : null}
            </View>
            <Text style={styles.qrLink} numberOfLines={2}>{profileShareLink}</Text>
            <View style={styles.qrActions}>
              <TouchableOpacity style={styles.qrActionButton} onPress={shareProfile} activeOpacity={0.78}>
                <Icon name="share-2" size={15} color="#fff" />
                <Text style={styles.qrActionText}>{t('profile.share')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.qrActionButton} onPress={() => setQrModalOpen(false)} activeOpacity={0.78}>
                <Icon name="x" size={15} color="rgba(255,255,255,0.76)" />
                <Text style={styles.qrActionText}>{t('profile.close')}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={coverActionsOpen}
        transparent
        statusBarTranslucent
        navigationBarTranslucent
        animationType="fade"
        onRequestClose={() => setCoverActionsOpen(false)}
      >
        <Pressable style={styles.sheetOverlay} onPress={() => setCoverActionsOpen(false)}>
          <Pressable style={[styles.bottomSheet, appearance.bottomSheet, { paddingBottom: insets.bottom + 16 }]} onPress={() => undefined}>
            <Text style={[styles.sheetTitle, appearance.textPrimary]}>{t('profile.coverPhoto')}</Text>
            <DrawerRow icon="eye" label={t('profile.viewCover')} onPress={() => { setCoverActionsOpen(false); setActiveImageViewer('cover'); }} />
            <DrawerRow icon="image" label={t('profile.changeCover')} onPress={() => pickImageForPreview('cover')} />
            <DrawerRow icon="trash-2" label={t('profile.removeCover')} destructive onPress={() => removeImage('cover')} />
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={settingsDrawerOpen}
        transparent
        statusBarTranslucent
        navigationBarTranslucent
        animationType="fade"
        onRequestClose={() => setSettingsDrawerOpen(false)}
      >
        <Pressable style={[styles.drawerOverlay, appearance.drawerOverlay]} onPress={() => setSettingsDrawerOpen(false)}>
          <Animated.View
            style={[
              styles.settingsDrawer,
              appearance.settingsDrawer,
              {
                paddingTop: insets.top + 12,
                paddingBottom: insets.bottom + 12,
                opacity: settingsBackdropEntrance,
              },
            ]}
          >
            <Pressable style={styles.settingsDrawerInteractionLayer} onPress={() => undefined}>
              <LinearGradient
                pointerEvents="none"
                colors={theme.isDark
                  ? ['rgba(12,14,24,0.98)', 'rgba(7,9,17,0.985)', 'rgba(4,7,13,0.995)']
                  : theme.gradients.surfaceElevated}
                locations={[0, 0.5, 1]}
                start={{ x: 0, y: 0 }}
                end={{ x: 0.8, y: 1 }}
                style={StyleSheet.absoluteFill}
              />
              <Animated.View
                pointerEvents="none"
                style={[
                  styles.settingsPurpleGlow,
                  {
                    transform: [{ translateY: settingsLightDrift.interpolate({ inputRange: [0, 1], outputRange: [-18, 26] }) }],
                  },
                ]}
              />
              <Animated.View
                pointerEvents="none"
                style={[
                  styles.settingsOrangeGlow,
                  {
                    transform: [{ translateX: settingsLightDrift.interpolate({ inputRange: [0, 1], outputRange: [18, -14] }) }],
                  },
                ]}
              />
              <View pointerEvents="none" style={styles.settingsVignette} />
              <View pointerEvents="none" style={styles.settingsParticles}>
                {[0, 1, 2, 3, 4, 5, 6].map(particle => (
                  <View key={particle} style={[styles.settingsParticle, styles[`settingsParticle${particle}` as keyof typeof styles] as any]} />
                ))}
              </View>
              <Animated.View
                style={[
                  styles.premiumSettingsHeader,
                  {
                    opacity: settingsProfileEntrance,
                    transform: [{ translateY: settingsProfileEntrance.interpolate({ inputRange: [0, 1], outputRange: [-18, 0] }) }],
                  },
                ]}
              >
                <View style={styles.premiumAvatarRing}>
                  <LinearGradient
                    colors={['#26D0CE', '#A18CD1', '#F27D26', '#26D0CE']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={StyleSheet.absoluteFill}
                  />
                  <View style={styles.premiumAvatarMetal}>
                    <Image source={{ uri: renderedProfileImage }} style={styles.premiumSettingsAvatar} />
                  </View>
                </View>
                <View style={styles.drawerHeaderText}>
                  <Text style={[styles.drawerTitle, appearance.textPrimary]} numberOfLines={2}>{displayName}</Text>
                  <Text style={[styles.drawerSubtitle, appearance.textMuted]} numberOfLines={1}>{profile?.email || `ID: ${(userId || '').slice(0, 14)}...`}</Text>
                </View>
                <TouchableOpacity
                  style={[styles.drawerClose, appearance.drawerControl, settingsSearchOpen && styles.settingsSearchButtonActive]}
                  activeOpacity={0.68}
                  onPress={() => setSettingsSearchOpen(open => !open)}
                  accessibilityRole="button"
                  accessibilityLabel={t('settings.search')}
                >
                  <LinearGradient
                    pointerEvents="none"
                    colors={['rgba(255,255,255,0.18)', 'rgba(255,255,255,0.035)']}
                    style={StyleSheet.absoluteFill}
                  />
                  <Icon name="search" size={18} color={settingsSearchOpen ? theme.colors.accentSecondary : theme.colors.icon} />
                </TouchableOpacity>
                <TouchableOpacity style={[styles.drawerClose, appearance.drawerControl]} activeOpacity={0.68} onPress={() => setSettingsDrawerOpen(false)}>
                  <LinearGradient
                    pointerEvents="none"
                    colors={['rgba(255,255,255,0.18)', 'rgba(255,255,255,0.035)']}
                    style={StyleSheet.absoluteFill}
                  />
                  <Icon name="x" size={20} color={theme.colors.icon} />
                </TouchableOpacity>
              </Animated.View>
              {settingsSearchOpen ? (
                <View style={[styles.settingsSearchWrap, appearance.drawerSearch]}>
                  <Icon name="search" size={16} color={theme.colors.iconMuted} />
                  <TextInput
                    value={settingsSearchQuery}
                    onChangeText={setSettingsSearchQuery}
                    placeholder={t('settings.search')}
                    placeholderTextColor={theme.colors.textMuted}
                    style={[styles.settingsSearchInput, appearance.textPrimary]}
                    autoFocus
                    autoCorrect={false}
                    returnKeyType="search"
                    accessibilityLabel={t('settings.search')}
                  />
                  {settingsSearchQuery ? (
                    <TouchableOpacity
                      style={styles.settingsSearchClear}
                      onPress={() => setSettingsSearchQuery('')}
                      accessibilityRole="button"
                      accessibilityLabel={t('settings.clearSearch')}
                    >
                      <Icon name="x" size={14} color={theme.colors.iconSubtle} />
                    </TouchableOpacity>
                  ) : null}
                </View>
              ) : null}
              <Animated.ScrollView
                ref={settingsScrollRef}
                showsVerticalScrollIndicator={false}
                bounces={false}
                decelerationRate="fast"
                contentContainerStyle={styles.drawerContent}
              >
                <PremiumSettingsGroup title={t('settings.account')} visible={[
                  t('settings.accountPrivacy'), t('settings.blockedAccounts'), t('settings.verificationCenter'),
                ].some(matchesSettingsSearch)}>
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.accountPrivacy'))} visible={settingsDrawerOpen} index={0} icon="shield" label={t('settings.accountPrivacy')} onPress={() => openSidebarModal('accountPrivacy')} />
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.blockedAccounts'))} visible={settingsDrawerOpen} index={1} icon="slash" label={t('settings.blockedAccounts')} onPress={() => openSidebarModal('blockedAccounts')} />
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.verificationCenter'))} visible={settingsDrawerOpen} index={2} icon="user-check" label={t('settings.verificationCenter')} onPress={() => { setSettingsDrawerOpen(false); navigation.navigate('VerificationCenter'); }} />
                </PremiumSettingsGroup>

                <PremiumSettingsGroup title={t('settings.profile')} visible={[
                  t('settings.editProfile'),
                  t('settings.uploadedPlans'),
                  ...(canPublishEvents(role) ? [t('settings.savedTemplates')] : []),
                  ...(hasPartnerAccess ? [t('settings.partnerDashboard')] : []),
                  ...(isAdminOrCoAdmin(role) ? [t('settings.adminControls')] : []),
                ].some(matchesSettingsSearch)}>
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.editProfile'))} visible={settingsDrawerOpen} index={3} icon="edit-3" label={t('settings.editProfile')} onPress={() => { setSettingsDrawerOpen(false); navigation.navigate('EditProfile'); }} />
                  {canPublishEvents(role) ? (
                    <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.savedTemplates'))} visible={settingsDrawerOpen} index={4} icon="copy" label={t('settings.savedTemplates')} onPress={() => { setSettingsDrawerOpen(false); userId && navigation.navigate('MyTemplates', { userId }); }} />
                  ) : null}
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.uploadedPlans'))} visible={settingsDrawerOpen} index={5} icon="upload" label={t('settings.uploadedPlans')} onPress={() => { setSettingsDrawerOpen(false); userId && navigation.navigate('MyPosts', { userId }); }} />
                  {hasPartnerAccess ? (
                    <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.partnerDashboard'))} visible={settingsDrawerOpen} index={6} icon="briefcase" label={t('settings.partnerDashboard')} onPress={() => { setSettingsDrawerOpen(false); navigation.navigate('PartnerHome'); }} />
                  ) : null}
                  {isAdminOrCoAdmin(role) ? (
                    <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.adminControls'))} visible={settingsDrawerOpen} index={7} icon="users" label={t('settings.adminControls')} onPress={() => { setSettingsDrawerOpen(false); setUserManagementOpen(true); }} />
                  ) : null}
                </PremiumSettingsGroup>

                <PremiumSettingsGroup title={t('settings.preferences')} visible={[
                  t('settings.language'), t('settings.title'), t('settings.notifications'),
                ].some(matchesSettingsSearch)}>
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.language'))} visible={settingsDrawerOpen} index={8} icon="globe" label={t('settings.language')} onPress={() => { setSettingsDrawerOpen(false); navigation.navigate('Settings'); }} />
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.title'))} visible={settingsDrawerOpen} index={9} icon="settings" label={t('settings.title')} onPress={() => { setSettingsDrawerOpen(false); navigation.navigate('Settings'); }} />
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.notifications'))} visible={settingsDrawerOpen} index={10} icon="bell" label={t('settings.notifications')} onPress={() => openSidebarModal('notifications')} />
                </PremiumSettingsGroup>

                <PremiumSettingsGroup title={t('settings.privacyLegal')} visible={[
                  t('settings.privacyPolicy'), t('settings.termsOfService'),
                ].some(matchesSettingsSearch)}>
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.privacyPolicy'))} visible={settingsDrawerOpen} index={11} icon="shield" label={t('settings.privacyPolicy')} onPress={() => { setSettingsDrawerOpen(false); navigation.navigate('PrivacyPolicy'); }} />
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.termsOfService'))} visible={settingsDrawerOpen} index={12} icon="file-text" label={t('settings.termsOfService')} onPress={() => { setSettingsDrawerOpen(false); navigation.navigate('TermsOfService'); }} />
                </PremiumSettingsGroup>

                <PremiumSettingsGroup title={t('settings.support')} visible={[t('settings.faq'), t('settings.helpFeedback')].some(matchesSettingsSearch)}>
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.faq'))} visible={settingsDrawerOpen} index={13} icon="message-circle" label={t('settings.faq')} onPress={() => openSidebarModal('faq')} />
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.helpFeedback'))} visible={settingsDrawerOpen} index={14} icon="message-square" label={t('settings.helpFeedback')} onPress={() => openSidebarModal('feedback')} />
                </PremiumSettingsGroup>

                <PremiumSettingsGroup title={t('settings.session')} visible={[t('settings.logout'), t('settings.deleteAccount')].some(matchesSettingsSearch)}>
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.logout'))} visible={settingsDrawerOpen} index={15} icon="log-out" label={t('settings.logout')} destructive onPress={() => { setSettingsDrawerOpen(false); confirmSignOut(); }} />
                  <PremiumSettingsRow matches={matchesSettingsSearch(t('settings.deleteAccount'))} visible={settingsDrawerOpen} index={16} icon="trash" label={t('settings.deleteAccount')} destructive onPress={() => openSidebarModal('deleteAccount')} />
                </PremiumSettingsGroup>
                {normalizedSettingsSearch && ![
                  t('settings.accountPrivacy'), t('settings.blockedAccounts'), t('settings.verificationCenter'),
                  t('settings.editProfile'), t('settings.uploadedPlans'),
                  ...(canPublishEvents(role) ? [t('settings.savedTemplates')] : []),
                  ...(hasPartnerAccess ? [t('settings.partnerDashboard')] : []),
                  ...(isAdminOrCoAdmin(role) ? [t('settings.adminControls')] : []),
                  t('settings.language'), t('settings.title'),
                  t('settings.notifications'), t('settings.privacyPolicy'), t('settings.termsOfService'),
                  t('settings.faq'), t('settings.helpFeedback'),
                  t('settings.logout'), t('settings.deleteAccount'),
                ].some(matchesSettingsSearch) ? (
                  <View style={styles.settingsSearchEmpty}>
                    <Icon name="search" size={22} color={theme.colors.iconFaint} />
                    <Text style={[styles.settingsSearchEmptyTitle, appearance.textPrimary]}>{t('settings.noSettings')}</Text>
                    <Text style={[styles.settingsSearchEmptyBody, appearance.textMuted]}>{t('settings.noSettingsSub')}</Text>
                  </View>
                ) : null}
              </Animated.ScrollView>
            </Pressable>
          </Animated.View>
        </Pressable>
      </Modal>

      <Modal
        visible={Boolean(activeSidebarModal)}
        transparent
        statusBarTranslucent
        navigationBarTranslucent
        animationType="slide"
        onRequestClose={closeSidebarModal}
      >
        <Pressable style={[styles.drawerOverlay, appearance.drawerOverlay]} onPress={closeSidebarModal}>
          <Pressable style={[styles.settingsDrawer, styles.secondarySettingsDrawer, appearance.settingsDrawer, { paddingTop: insets.top + 16, paddingBottom: insets.bottom + 18 }]} onPress={() => undefined}>
            <View style={[styles.drawerHeader, appearance.drawerHeader]}>
              <View style={styles.drawerHeaderText}>
                <Text style={[styles.drawerTitle, appearance.textPrimary]} numberOfLines={1}>
                  {activeSidebarModal === 'accountPrivacy' ? t('settings.accountPrivacy') :
                    activeSidebarModal === 'blockedAccounts' ? t('settings.blockedAccounts') :
                      activeSidebarModal === 'faq' ? t('settings.faq') :
                        activeSidebarModal === 'feedback' ? t('settings.helpFeedback') :
                          activeSidebarModal === 'notifications' ? t('settings.notifications') :
                                activeSidebarModal === 'studentVerification' ? t('settings.studentVerification') :
                                  activeSidebarModal === 'faceVerification' ? t('settings.vibeVerification') :
                                    activeSidebarModal === 'deleteAccount' ? t('settings.deleteAccount') : ''}
                </Text>
                <Text style={[styles.drawerSubtitle, appearance.textMuted]} numberOfLines={1}>{t('settings.tonyteSettings')}</Text>
              </View>
              <TouchableOpacity style={[styles.drawerClose, appearance.drawerControl]} onPress={closeSidebarModal}>
                <Icon name="x" size={19} color={theme.colors.icon} />
              </TouchableOpacity>
            </View>

            <ScrollView
              style={styles.sidebarModalScroll}
              showsVerticalScrollIndicator
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={styles.sidebarModalContent}
            >
                {activeSidebarModal === 'accountPrivacy' ? (
                  <>
                    <GlassPanel>
                      <Text style={[styles.panelKicker, appearance.textMuted]}>{t('settings.connectedEmail')}</Text>
                      <Text style={[styles.panelValue, appearance.textPrimary]}>{profile?.email || auth.currentUser?.email || t('settings.noEmail')}</Text>
                    </GlassPanel>
                    <GlassPanel>
                      <Text style={[styles.panelKicker, appearance.textMuted]}>{t('settings.signInMethod')}</Text>
                      {auth.currentUser?.providerData?.length ? auth.currentUser.providerData.map(provider => (
                        <Text key={provider.providerId} style={[styles.panelBody, appearance.textSecondary]}>
                          {provider.providerId === 'password' ? t('settings.passwordLogin') : t('settings.connected').replace('{provider}', provider.providerId)}
                        </Text>
                      )) : <Text style={[styles.panelBody, appearance.textSecondary]}>{t('settings.noSignInMethod')}</Text>}
                      <TouchableOpacity style={styles.panelButton} disabled={passwordResetSending} onPress={sendPasswordReset}>
                        <Text style={styles.panelButtonText}>{passwordResetSending ? t('settings.sending') : t('settings.sendReset')}</Text>
                      </TouchableOpacity>
                      {passwordResetMessage ? <Text style={styles.panelMessage}>{passwordResetMessage}</Text> : null}
                    </GlassPanel>
                  </>
                ) : null}

                {activeSidebarModal === 'blockedAccounts' ? (
                  <GlassPanel noPadding>
                    {blockedAccountsLoading ? (
                      <View style={styles.panelLoading}><ActivityIndicator color={Colors.turquoise} /></View>
                    ) : blockedAccounts.length ? blockedAccounts.map((account, index) => (
                      <View key={account.id} style={[styles.blockedRow, index > 0 && styles.blockedRowDivider]}>
                        <Image source={{ uri: account.photo || DEFAULT_AVATAR }} style={styles.blockedAvatar} />
                        <View style={styles.blockedMeta}>
                          <Text style={[styles.blockedName, appearance.textPrimary]} numberOfLines={1}>{account.name}</Text>
                          <Text style={[styles.blockedUsername, appearance.textMuted]} numberOfLines={1}>{account.username ? `@${account.username}` : `ID: ${account.id}`}</Text>
                        </View>
                        <TouchableOpacity
                          style={styles.unblockButton}
                          disabled={pendingUnblockUserId === account.id}
                          onPress={() => unblockAccount(account.id)}
                        >
                          <Text style={styles.unblockButtonText}>{pendingUnblockUserId === account.id ? '...' : t('settings.unblock')}</Text>
                        </TouchableOpacity>
                      </View>
                    )) : (
                      <Text style={[styles.panelBody, appearance.textSecondary, styles.blockedEmptyText]}>{t('settings.noBlocked')}</Text>
                    )}
                    {blockedAccountsMessage ? <Text style={[styles.panelMessageError, styles.blockedMessagePadding]}>{blockedAccountsMessage}</Text> : null}
                  </GlassPanel>
                ) : null}

                {activeSidebarModal === 'faq' ? (
                  <>
                    <View style={[styles.sidebarSearchBox, appearance.drawerSearch]}>
                      <Icon name="search" size={16} color={theme.colors.iconMuted} />
                      <TextInput
                        value={faqSearchQuery}
                        onChangeText={text => { setFaqSearchQuery(text); setOpenFaqIndex(null); }}
                        placeholder={t('settings.searchFaq')}
                        placeholderTextColor={theme.colors.textMuted}
                        style={[styles.sidebarSearchInput, appearance.textPrimary]}
                      />
                    </View>
                    {filteredFaqItems.map((item, index) => {
                      const expanded = openFaqIndex === index;
                      return (
                        <TouchableOpacity key={item.question} style={[styles.faqItem, appearance.cardSurface]} activeOpacity={0.82} onPress={() => setOpenFaqIndex(current => current === index ? null : index)}>
                          <View style={styles.faqQuestionRow}>
                            <Text style={[styles.faqQuestion, appearance.textPrimary]}>{item.question}</Text>
                            <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={17} color={theme.colors.iconMuted} />
                          </View>
                          {expanded ? <Text style={[styles.faqAnswer, appearance.textSecondary]}>{item.answer}</Text> : null}
                        </TouchableOpacity>
                      );
                    })}
                  </>
                ) : null}

                {activeSidebarModal === 'feedback' ? (
                  <GlassPanel>
                    {(['general', 'bug', 'feature', 'event_issue'] as const).map(option => (
                      <TouchableOpacity key={option} style={[styles.optionRow, appearance.drawerRow, feedbackType === option && styles.optionRowActive]} onPress={() => setFeedbackType(option)}>
                        <Text style={[styles.optionText, appearance.textSecondary, feedbackType === option && styles.optionTextActive]}>{t(`profile.feedbackType_${option}`)}</Text>
                        {feedbackType === option ? <Icon name="check" size={16} color={Colors.turquoise} /> : null}
                      </TouchableOpacity>
                    ))}
                    <TextInput
                      value={feedbackMessage}
                      onChangeText={setFeedbackMessage}
                      placeholder={t('settings.feedbackPlaceholder')}
                      placeholderTextColor={theme.colors.textMuted}
                      multiline
                      style={[styles.feedbackTextArea, appearance.inputSurface, appearance.textPrimary]}
                    />
                    <TextInput
                      value={feedbackEmail}
                      onChangeText={setFeedbackEmail}
                      placeholder={t('settings.feedbackEmail')}
                      placeholderTextColor={theme.colors.textMuted}
                      autoCapitalize="none"
                      keyboardType="email-address"
                      style={[styles.feedbackInput, appearance.inputSurface, appearance.textPrimary]}
                    />
                    {feedbackSubmitMessage ? <Text style={feedbackSubmitMessage === t('profile.feedbackThanks') ? styles.panelMessage : styles.panelMessageError}>{feedbackSubmitMessage}</Text> : null}
                    <TouchableOpacity style={styles.panelButton} disabled={feedbackSubmitting || !feedbackMessage.trim()} onPress={submitFeedback}>
                      <Text style={styles.panelButtonText}>{feedbackSubmitting ? t('settings.sending') : t('settings.sendFeedback')}</Text>
                    </TouchableOpacity>
                  </GlassPanel>
                ) : null}

                {activeSidebarModal === 'notifications' ? (
                  <GlassPanel>
                    <ToggleRow label={t('settings.pushNotifications')} enabled={notificationsEnabled} disabled={notificationSaving} onPress={() => toggleNotificationPreference('notificationsEnabled', !notificationsEnabled)} />
                    <ToggleRow label={t('settings.chatNotifications')} enabled={chatNotificationsEnabled} disabled={notificationSaving} onPress={() => toggleNotificationPreference('chatNotificationsEnabled', !chatNotificationsEnabled)} />
                    <ToggleRow label={t('settings.eventNotifications')} enabled={eventNotificationsEnabled} disabled={notificationSaving} onPress={() => toggleNotificationPreference('eventNotificationsEnabled', !eventNotificationsEnabled)} />
                    <ToggleRow label={t('settings.eventRecommendationNotifications')} enabled={eventRecommendationNotificationsEnabled} disabled={notificationSaving} onPress={() => toggleNotificationPreference('eventRecommendationNotificationsEnabled', !eventRecommendationNotificationsEnabled)} />
                    <ToggleRow label={t('settings.partnerNotifications')} enabled={partnerNotificationsEnabled} disabled={notificationSaving} onPress={() => toggleNotificationPreference('partnerNotificationsEnabled', !partnerNotificationsEnabled)} />
                    <ToggleRow label={t('optionalSettings.profileReminderNotifications')} enabled={profileReminderNotificationsEnabled} disabled={notificationSaving} onPress={() => toggleNotificationPreference('profileReminderNotificationsEnabled', !profileReminderNotificationsEnabled)} />
                    <ToggleRow label={t('optionalSettings.reviewReminderNotifications')} enabled={reviewReminderNotificationsEnabled} disabled={notificationSaving} onPress={() => toggleNotificationPreference('reviewReminderNotificationsEnabled', !reviewReminderNotificationsEnabled)} />
                    <ToggleRow label={t('optionalSettings.feedbackReminderNotifications')} enabled={feedbackReminderNotificationsEnabled} disabled={notificationSaving} onPress={() => toggleNotificationPreference('feedbackReminderNotificationsEnabled', !feedbackReminderNotificationsEnabled)} />
                  </GlassPanel>
                ) : null}

                {activeSidebarModal === 'studentVerification' ? (
                  <View style={styles.studentVerificationPanel}>
                    {/* ── Approved / Verified ── */}
                    {verifiedStudent ? (
                      <>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <Icon name="check-circle" size={18} color={Colors.turquoise} />
                          <Text style={[styles.panelKicker, { color: Colors.turquoise, marginBottom: 0 }]}>
                            {t('settings.verifiedStudent')}
                          </Text>
                        </View>
                        {studentUniversityLabel ? (
                          <Text style={[styles.panelValue, appearance.textPrimary]}>{studentUniversityLabel}</Text>
                        ) : null}
                        <Text style={[styles.panelBody, appearance.textSecondary]}>
                          {t('settings.verifiedStudentSub')}
                        </Text>
                      </>
                    ) : studentStatus === 'email_verification_sent' || studentStatus === 'email_sent' ? (
                      /* ── Email sent, awaiting click ── */
                      <>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <Icon name="mail" size={18} color={Colors.turquoise} />
                          <Text style={[styles.panelKicker, { color: Colors.turquoise, marginBottom: 0 }]}>
                            {t('settings.checkInbox')}
                          </Text>
                        </View>
                        <Text style={[styles.panelBody, appearance.textSecondary]}>
                          {t('settings.verificationSentPrefix')}{' '}
                          <Text style={{ color: Colors.turquoise }}>
                            {profile?.requestedStudentEmail || studentEmailInput}
                          </Text>
                          {' '}{t('settings.verificationSentSuffix')}
                        </Text>
                        <Text style={[styles.panelBody, appearance.textSecondary]}>
                          {t('settings.linkExpiry')}
                        </Text>
                        {studentVerificationMessage ? (
                          <Text style={studentVerificationMessage.includes('sent') ? styles.panelMessage : styles.panelMessageError}>
                            {studentVerificationMessage}
                          </Text>
                        ) : null}
                        <Text style={[styles.panelKicker, { marginTop: 12 }]}>{t('settings.wrongEmail')}</Text>
                        <View style={styles.studentVerificationActionStack}>
                          <TextInput
                            value={studentEmailInput}
                            onChangeText={setStudentEmailInput}
                            placeholder={t('settings.differentUniversityEmail')}
                            placeholderTextColor={theme.colors.textMuted}
                            autoCapitalize="none"
                            keyboardType="email-address"
                            style={[styles.studentVerificationInput, appearance.inputSurface, appearance.textPrimary]}
                          />
                          <TouchableOpacity style={styles.studentVerificationButton} disabled={studentVerificationSaving} onPress={submitStudentVerification}>
                            <Text style={styles.panelButtonText}>{studentVerificationSaving ? t('settings.sending') : t('settings.resendVerification')}</Text>
                          </TouchableOpacity>
                        </View>
                      </>
                    ) : studentStatus === 'pending_admin_review' ? (
                      /* ── Awaiting admin review ── */
                      <>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <Icon name="clock" size={18} color="rgba(251,191,36,1)" />
                          <Text style={[styles.panelKicker, { color: 'rgba(251,191,36,1)', marginBottom: 0 }]}>
                            {t('settings.pendingReview')}
                          </Text>
                        </View>
                        <Text style={[styles.panelBody, appearance.textSecondary]}>
                          {t('settings.pendingReviewSub')}
                        </Text>
                      </>
                    ) : studentStatus === 'denied' || studentStatus === 'failed' || studentStatus === 'rejected' || studentStatus === 'expired' ? (
                      /* ── Denied / Failed ── */
                      <>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <Icon name="alert-triangle" size={18} color={Colors.sunset} />
                          <Text style={[styles.panelKicker, { color: Colors.sunset, marginBottom: 0 }]}>
                            {t('settings.verificationFailed')}
                          </Text>
                        </View>
                        <Text style={[styles.panelBody, appearance.textSecondary]}>
                          {t('settings.studentFailedSub')}
                        </Text>
                        <TextInput
                          value={studentEmailInput}
                          onChangeText={setStudentEmailInput}
                          placeholder={t('settings.universityEmail')}
                          placeholderTextColor={theme.colors.textMuted}
                          autoCapitalize="none"
                          keyboardType="email-address"
                          style={[styles.studentVerificationInput, appearance.inputSurface, appearance.textPrimary]}
                        />
                        {studentVerificationMessage ? (
                          <Text style={studentVerificationMessage.includes('sent') ? styles.panelMessage : styles.panelMessageError}>
                            {studentVerificationMessage}
                          </Text>
                        ) : null}
                        <TouchableOpacity style={styles.studentVerificationButton} disabled={studentVerificationSaving} onPress={submitStudentVerification}>
                          <Text style={styles.panelButtonText}>{studentVerificationSaving ? t('settings.sending') : t('settings.tryAgain')}</Text>
                        </TouchableOpacity>
                      </>
                    ) : (
                      /* ── Default: not yet submitted ── */
                      <>
                        <Text style={[styles.panelKicker, appearance.textMuted]}>{t('settings.universityEmail')}</Text>
                        <Text style={[styles.panelBody, appearance.textSecondary]}>
                          {t('settings.universityEmailHelp')}
                        </Text>
                        <TextInput
                          value={studentEmailInput}
                          onChangeText={setStudentEmailInput}
                          placeholder={t('verification.emailPlaceholder')}
                          placeholderTextColor={theme.colors.textMuted}
                          autoCapitalize="none"
                          keyboardType="email-address"
                          style={[styles.studentVerificationInput, appearance.inputSurface, appearance.textPrimary]}
                        />
                        {studentVerificationMessage ? (
                          <Text style={studentVerificationMessage.includes('sent') ? styles.panelMessage : styles.panelMessageError}>
                            {studentVerificationMessage}
                          </Text>
                        ) : null}
                        <TouchableOpacity style={styles.studentVerificationButton} disabled={studentVerificationSaving} onPress={submitStudentVerification}>
                          <Text style={styles.panelButtonText}>{studentVerificationSaving ? t('settings.sending') : t('settings.sendVerification')}</Text>
                        </TouchableOpacity>
                      </>
                    )}
                  </View>
                ) : null}

                {activeSidebarModal === 'faceVerification' ? (
                  <View style={styles.vibeVerificationPanel}>
                    {isVibeVerified ? (
                      <>
                        <View style={styles.vibeVerifiedHero}>
                          <View style={styles.vibeVerifiedIcon}>
                            <Icon name="check" size={22} color="#fff" />
                          </View>
                          <Text style={[styles.vibeVerificationTitle, appearance.textPrimary]}>{t('settings.vibeVerifiedTitle')}</Text>
                          <Text style={[styles.vibeVerificationSubtitle, appearance.textSecondary]}>
                            {t('settings.vibeVerifiedSub')}
                          </Text>
                        </View>
                        <GlassPanel>
                          <Text style={[styles.panelKicker, { marginBottom: 4 }]}>{t('settings.privacy')}</Text>
                          <Text style={[styles.panelBody, appearance.textSecondary]}>
                            {t('settings.verificationPrivacy')}
                          </Text>
                        </GlassPanel>
                      </>
                    ) : isFaceVerificationPending ? (
                      <>
                        <View style={styles.vibePendingHero}>
                          <View style={styles.vibePendingIcon}>
                            <Icon name="clock" size={20} color="#7dd3fc" />
                          </View>
                          <Text style={[styles.vibeVerificationTitle, appearance.textPrimary]}>{t('settings.verificationPending')}</Text>
                          <Text style={[styles.vibeVerificationSubtitle, appearance.textSecondary]}>
                            {t('settings.verificationPendingSub')}
                          </Text>
                        </View>
                      </>
                    ) : (
                      // Automated front/left/right pose verification lives
                      // in VerificationCenterScreen (the same flow reached
                      // from the "Verifications" pill / settings row) —
                      // this panel is a shortcut into that one canonical
                      // flow rather than a second, separate capture UI.
                      <>
                        <View style={styles.vibeVerificationHero}>
                          <LinearGradient
                            colors={['rgba(255,122,24,0.2)', 'rgba(155,81,224,0.16)', 'rgba(255,255,255,0.02)']}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={StyleSheet.absoluteFill}
                          />
                          <View style={styles.vibeVerificationHeroIcon}>
                            <Icon name="user-check" size={22} color="#fff" />
                          </View>
                          <Text style={[styles.vibeVerificationTitle, appearance.textPrimary]}>{t('settings.getVerified')}</Text>
                          <Text style={[styles.vibeVerificationSubtitle, appearance.textSecondary]}>
                            {t('settings.getVerifiedSub')}
                          </Text>
                        </View>
                        {[
                          ['eye', t('settings.faceVisible')],
                          ['sun', t('settings.goodLighting')],
                          ['shield', t('settings.noFilters')],
                        ].map(([icon, label]) => (
                          <View key={label} style={styles.vibeChecklistRow}>
                            <View style={styles.vibeChecklistIcon}>
                              <Icon name={icon as any} size={14} color={Colors.sunset} />
                            </View>
                            <Text style={[styles.vibeChecklistText, appearance.textSecondary]}>{label}</Text>
                          </View>
                        ))}
                        <TouchableOpacity
                          style={styles.studentVerificationButton}
                          onPress={() => {
                            setActiveSidebarModal(null);
                            navigation.navigate('VerificationCenter');
                          }}
                        >
                          <Text style={styles.panelButtonText}>{t('settings.takeVerificationSelfie')}</Text>
                        </TouchableOpacity>
                      </>
                    )}
                  </View>
                ) : null}

                {activeSidebarModal === 'deleteAccount' ? (
                  <GlassPanel>
                    <View style={styles.deleteWarningIcon}>
                      <Icon name="alert-triangle" size={24} color={Colors.sunset} />
                    </View>
                    <Text style={[styles.deleteTitle, appearance.textPrimary]}>{t('settings.deleteAccountTitle')}</Text>
                    <Text style={[styles.deleteBody, appearance.textSecondary]}>
                      {t('settings.deleteAccountConfirm')}
                    </Text>
                    <View style={[styles.deleteInfoBox, appearance.drawerRow]}>
                      <Text style={[styles.deleteInfoText, appearance.textSecondary]}>{t('settings.deleteProfileInfo')}</Text>
                      <Text style={[styles.deleteInfoText, appearance.textSecondary]}>{t('settings.deleteDataInfo')}</Text>
                      <Text style={[styles.deleteInfoText, appearance.textSecondary]}>{t('settings.deletePlansInfo')}</Text>
                    </View>
                    <View style={styles.deleteReauthBox}>
                      <Text style={[styles.panelKicker, appearance.textMuted]}>{t('settings.confirmIdentity')}</Text>
                      <Text style={[styles.panelBody, appearance.textSecondary]}>
                        {t('settings.reauthenticate').replace('{provider}', deletionProviderLabel)}
                      </Text>
                      {deletionProvider === 'password' ? (
                        <TextInput
                          value={deleteAccountPassword}
                          onChangeText={setDeleteAccountPassword}
                          placeholder={t('settings.enterPassword')}
                          placeholderTextColor={theme.colors.textMuted}
                          secureTextEntry
                          style={[styles.feedbackInput, appearance.inputSurface, appearance.textPrimary]}
                        />
                      ) : null}
                    </View>
                    {deleteAccountError ? <Text style={styles.panelMessageError}>{deleteAccountError}</Text> : null}
                    <View style={styles.deleteButtonRow}>
                      <TouchableOpacity style={[styles.deleteCancelButton, appearance.drawerControl]} disabled={isDeletingAccount} onPress={closeSidebarModal}>
                        <Text style={[styles.deleteCancelText, appearance.textPrimary]}>{t('common.cancel')}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.deleteConfirmButton} disabled={isDeletingAccount} onPress={deleteAccount}>
                        {isDeletingAccount ? <ActivityIndicator color="#fff" /> : <Text style={styles.deleteConfirmText}>{t('settings.deleteAccountTitle')}</Text>}
                      </TouchableOpacity>
                    </View>
                  </GlassPanel>
                ) : null}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={interestComposerOpen}
        transparent={false}
        animationType="fade"
        onRequestClose={() => !savingInterests && setInterestComposerOpen(false)}
      >
        <TonyteBackground appearanceAware style={styles.interestModal}>
          <View style={[styles.interestModalHeader, { paddingTop: insets.top + 16 }]}>
            <View>
              <Text style={styles.interestModalKicker}>Tonyte</Text>
                <Text style={[styles.interestModalTitle, appearance.textPrimary]}>{t('interests.addInterests')}</Text>
            </View>
            <TouchableOpacity style={[styles.drawerClose, appearance.drawerControl]} onPress={() => !savingInterests && setInterestComposerOpen(false)}>
              <Icon name="x" size={20} color={theme.colors.icon} />
            </TouchableOpacity>
          </View>
          <ScrollView
            style={styles.interestModalScroll}
            contentContainerStyle={{ paddingBottom: insets.bottom + 124 }}
            showsVerticalScrollIndicator={false}
          >
            <View style={[styles.selectedInterestPanel, appearance.interestPanel]}>
              <View style={styles.selectedInterestHeader}>
                <View>
                  <Text style={[styles.selectedInterestLabel, appearance.sectionHeading]}>{t('interests.selectedInterests')}</Text>
                  <Text style={[styles.selectedInterestCount, appearance.supportingText]}>{selectedInterests.length}/30 {t('interests.selected')}</Text>
                </View>
                <TouchableOpacity style={[styles.clearInterestsButton, appearance.clearInterestsButton]} onPress={clearSelectedInterests}>
                  <Text style={[styles.clearInterestsText, appearance.interestAccentText]}>{t('common.clear')}</Text>
                </TouchableOpacity>
              </View>
              <View style={[styles.interestSearchBox, appearance.interestInputSurface]}>
                <Icon name="search" size={16} color={theme.colors.iconMuted} />
                <TextInput
                  value={interestSearchQuery}
                  onChangeText={setInterestSearchQuery}
                  placeholder={t('interests.searchInterests')}
                  placeholderTextColor={theme.colors.textPlaceholder}
                  style={[styles.interestSearchInput, appearance.interestInputText]}
                />
              </View>
              {selectedInterests.length ? (
                <View style={styles.selectedInterestChips}>
                  {selectedInterests.map((interest, idx) => (
                    <TouchableOpacity key={`${interest}-${idx}`} style={[styles.selectedInterestChip, appearance.selectedInterestChip]} onPress={() => toggleInterestSelection(interest)}>
                      <Text style={[styles.selectedInterestChipText, appearance.selectedInterestChipText]}>{getInterestLabel(interest)}</Text>
                      <Icon name="x" size={12} color={theme.colors.accentSecondary} />
                    </TouchableOpacity>
                  ))}
                </View>
              ) : null}
              {normalizedInterestSearch && !hasExactInterestMatch ? (
                <TouchableOpacity style={styles.addCustomInterestButton} onPress={addCustomInterest}>
                  <Icon name="plus" size={16} color={Colors.sunset} />
                  <Text style={styles.addCustomInterestText}>{t('interests.add')} "{normalizedInterestSearch}"</Text>
                </TouchableOpacity>
              ) : null}
            </View>
            {filteredInterestCategories.map(category => (
              <View key={category.name} style={[styles.interestCategoryCard, appearance.interestCategorySurface]}>
                <Text style={[styles.interestCategoryTitle, appearance.sectionHeading]}>
                  {t(getInterestCategoryTranslationKey(category.name), category.name)}
                </Text>
                <View style={styles.interestCategoryGrid}>
                  {category.interests.map(interest => {
                    const selected = selectedInterests.includes(interest);
                    return (
                      <TouchableOpacity
                        key={`${category.name}-${interest}`}
                        style={[
                          styles.interestOptionChip,
                          appearance.interestOptionSurface,
                          selected && styles.interestOptionChipActive,
                          selected && appearance.interestOptionSelectedSurface,
                        ]}
                        onPress={() => toggleInterestSelection(interest)}
                      >
                        <Text style={[
                          styles.interestOptionText,
                          appearance.interestOptionText,
                          selected && styles.interestOptionTextActive,
                          selected && appearance.selectedInterestChipText,
                        ]}>
                          {getInterestLabel(interest)}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            ))}
          </ScrollView>
          <View style={[styles.interestModalDock, appearance.interestDock, { bottom: insets.bottom + 16 }]}>
            <TouchableOpacity style={styles.interestCancelButton} disabled={savingInterests} onPress={() => setInterestComposerOpen(false)}>
              <Text style={[styles.interestCancelText, appearance.interestCancelText]}>{t('common.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.interestSaveButton} disabled={savingInterests} onPress={saveInterests}>
              {savingInterests ? <ActivityIndicator color="#fff" /> : <Text style={styles.interestSaveText}>{t('interests.saveInterests')}</Text>}
            </TouchableOpacity>
          </View>
        </TonyteBackground>
      </Modal>

      <Modal
        visible={userManagementOpen && canManageUsers}
        transparent
        statusBarTranslucent
        navigationBarTranslucent
        animationType="slide"
        onRequestClose={() => setUserManagementOpen(false)}
      >
        <View style={[styles.adminModalOverlay, appearance.drawerOverlay]}>
          <View style={[styles.adminModalSheet, appearance.settingsDrawer, { paddingBottom: insets.bottom + 18 }]}>
            <View style={[styles.adminModalHeader, appearance.drawerHeader]}>
              <View>
                <Text style={styles.adminEyebrow}>{t('profile.admin')}</Text>
                <Text style={[styles.adminModalTitle, appearance.textPrimary]}>{t('profile.userManagement')}</Text>
                <Text style={[styles.adminModalSubtitle, appearance.textMuted]}>
                  {adminUsersLoading ? t('settings.loadingUsers') : t('settings.totalUsers').replace('{count}', String(adminUsers.length))}
                </Text>
              </View>
              <TouchableOpacity style={[styles.adminModalClose, appearance.drawerControl]} onPress={() => setUserManagementOpen(false)}>
                <Icon name="x" size={18} color={theme.colors.icon} />
              </TouchableOpacity>
            </View>

            <View style={[styles.adminSearchBox, appearance.drawerSearch]}>
              <Icon name="search" size={15} color={theme.colors.iconMuted} />
              <TextInput
                value={adminUserSearch}
                onChangeText={setAdminUserSearch}
                placeholder={t('settings.searchUsers')}
                placeholderTextColor={theme.colors.textMuted}
                style={[styles.adminSearchInput, appearance.textPrimary]}
                autoCapitalize="none"
              />
            </View>

            {adminRoleMessage ? (
              <View style={styles.adminRoleMessage}>
                <Text style={styles.adminRoleMessageText}>{adminRoleMessage}</Text>
              </View>
            ) : null}

            {adminUsersLoading ? (
              <View style={styles.adminLoadingWrap}>
                <ActivityIndicator color={Colors.turquoise} />
              </View>
            ) : (
              <ScrollView style={styles.adminUserList} showsVerticalScrollIndicator={false}>
                {filteredAdminUsers.length ? filteredAdminUsers.map(managedUser => {
                  const targetIsProtected = managedUser.role === 'admin' || managedUser.role === 'co-admin';
                  const coAdminBlocked = isCoAdminRole(role) && targetIsProtected;
                  const options = isAdminRole(role) ? ADMIN_ROLE_OPTIONS : CO_ADMIN_ROLE_OPTIONS;
                  return (
                    <View key={managedUser.id} style={[styles.adminUserRow, appearance.cardSurface]}>
                      <View style={styles.adminUserTopRow}>
                        <View style={styles.adminUserAvatarWrap}>
                          <Image source={{ uri: managedUser.photo || DEFAULT_AVATAR }} style={styles.adminUserAvatar} />
                          <VibeVerifiedBadge verified={managedUser.faceVerified === true} size={42} />
                        </View>
                        <View style={styles.adminUserMeta}>
                          <Text style={[styles.adminUserName, appearance.textPrimary]} numberOfLines={1}>{managedUser.name}</Text>
                          <Text style={[styles.adminUserEmail, appearance.textMuted]} numberOfLines={1}>{managedUser.email || t('settings.noEmailAvailable')}</Text>
                          <Text style={[styles.adminUserRoleLine, appearance.textSecondary]} numberOfLines={1}>
                            {managedUser.role}
                            {managedUser.studentVerificationStatus ? ` · ${managedUser.studentVerificationStatus.replace(/_/g, ' ')}` : ''}
                            {managedUser.restricted ? ' · restricted' : ''}
                          </Text>
                        </View>
                      </View>

                      {coAdminBlocked ? (
                        <Text style={[styles.protectedAccountText, appearance.supportingText]}>{t('settings.protectedAccount')}</Text>
                      ) : (
                        <View style={styles.roleChipRow}>
                          {options.map(option => {
                            const selected = managedUser.role === option;
                            const updating = roleUpdatingUserId === managedUser.id;
                            return (
                              <TouchableOpacity
                                key={option}
                                style={[
                                  styles.roleChip,
                                  appearance.roleChip,
                                  selected && styles.roleChipActive,
                                  selected && appearance.roleChipActive,
                                ]}
                                disabled={selected || updating}
                                onPress={() => handleRoleUpdate(managedUser, option)}
                                activeOpacity={0.82}
                              >
                                <Text style={[
                                  styles.roleChipText,
                                  appearance.roleChipText,
                                  selected && styles.roleChipTextActive,
                                  selected && appearance.roleChipTextActive,
                                ]}>
                                  {updating && !selected ? '…' : option}
                                </Text>
                              </TouchableOpacity>
                            );
                          })}
                        </View>
                      )}
                      {managedUser.faceVerified && isAdminRole(role) ? (
                        <>
                          <TouchableOpacity
                            style={styles.revokeBadgeBtn}
                            disabled={revokingBadgeUserId === managedUser.id}
                            onPress={() => revokeFaceBadge(managedUser.id)}
                            activeOpacity={0.82}
                          >
                            <Icon name="user-x" size={13} color="#f87171" />
                            <Text style={styles.revokeBadgeText}>
                              {revokingBadgeUserId === managedUser.id ? t('settings.revoking') : t('settings.revokeVerified')}
                            </Text>
                          </TouchableOpacity>
                          {/* Fallback for Alert.alert's known stacking inconsistency over an
                              open native Modal on Android — see revokeFaceBadge's comment. */}
                          {revokeFeedback?.userId === managedUser.id ? (
                            <Text style={styles.revokeErrorText}>{revokeFeedback.message}</Text>
                          ) : null}
                        </>
                      ) : null}

                      {canModerateUser(managedUser) ? (
                        <View style={styles.adminDangerRow}>
                          <TouchableOpacity
                            style={styles.suspendBtn}
                            disabled={suspendingUserId === managedUser.id || deletingUserId === managedUser.id}
                            onPress={() => handleToggleSuspend(managedUser)}
                            activeOpacity={0.82}
                          >
                            <Icon name={managedUser.restricted ? 'unlock' : 'slash'} size={13} color="#facc15" />
                            <Text style={styles.suspendBtnText}>
                              {suspendingUserId === managedUser.id
                                ? t('settings.suspending')
                                : managedUser.restricted
                                  ? t('settings.unsuspendAccountAction')
                                  : t('settings.suspendAccountAction')}
                            </Text>
                          </TouchableOpacity>

                          {canDeleteUser(managedUser) ? (
                            <TouchableOpacity
                              style={styles.deleteAccountBtn}
                              disabled={deletingUserId === managedUser.id || suspendingUserId === managedUser.id}
                              onPress={() => handleDeleteUser(managedUser)}
                              activeOpacity={0.82}
                            >
                              <Icon name="trash-2" size={13} color="#f87171" />
                              <Text style={styles.deleteAccountBtnText}>
                                {deletingUserId === managedUser.id ? t('settings.deletingAccount') : t('settings.deleteAccountAction')}
                              </Text>
                            </TouchableOpacity>
                          ) : null}
                        </View>
                      ) : null}
                    </View>
                  );
                }) : (
                  <View style={styles.adminEmptyState}>
                    <Text style={styles.adminEmptyText}>{t('settings.noUsers')}</Text>
                  </View>
                )}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* Admin — Student Verification Requests */}
      <Modal
        visible={studentVerifModalOpen && canManageUsers}
        transparent
        animationType="slide"
        onRequestClose={() => setStudentVerifModalOpen(false)}
      >
        <View style={styles.adminModalOverlay}>
          <View style={[styles.adminModalSheet, { paddingBottom: insets.bottom + 18 }]}>
            <View style={styles.adminModalHeader}>
              <View>
                <Text style={styles.adminEyebrow}>{t('profile.admin')}</Text>
                <Text style={styles.adminModalTitle}>{t('profile.studentVerifications')}</Text>
                <Text style={styles.adminModalSubtitle}>
                  {pendingVerificationsLoading
                    ? t('common.loading')
                    : tp('profile.pendingReviewCount', pendingVerifications.length)}
                </Text>
              </View>
              <TouchableOpacity
                style={styles.adminModalClose}
                onPress={() => setStudentVerifModalOpen(false)}
              >
                <Icon name="x" size={18} color="rgba(255,255,255,0.72)" />
              </TouchableOpacity>
            </View>

            {pendingVerificationsLoading ? (
              <View style={styles.adminLoadingWrap}>
                <ActivityIndicator color={Colors.turquoise} />
              </View>
            ) : pendingVerifications.length === 0 ? (
              <View style={styles.adminEmptyState}>
                <Icon name="check-circle" size={32} color="rgba(38,208,206,0.45)" />
                <Text style={[styles.adminEmptyText, { marginTop: 10 }]}>
                  {t('settings.noPendingRequests')}
                </Text>
              </View>
            ) : (
              <ScrollView style={styles.adminUserList} showsVerticalScrollIndicator={false}>
                {pendingVerifications.map(req => {
                  const isSaving = studentVerifActionSaving === req.id;
                  const ownershipConfirmed = req.verifiedEmailOwnership === true;
                  const requestedAt = (() => {
                    if (!req.createdAt) return null;
                    const ts = req.createdAt as { toDate?: () => Date } | string | number;
                    const d = typeof ts === 'object' && ts !== null && 'toDate' in ts && typeof ts.toDate === 'function'
                      ? ts.toDate()
                      : new Date(ts as string | number);
                    return isNaN(d.getTime()) ? null : d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
                  })();
                  return (
                    <View key={req.id} style={styles.adminUserRow}>
                      <View style={styles.adminUserTopRow}>
                        <View style={[styles.adminManagementIcon, { backgroundColor: 'rgba(38,208,206,0.12)', width: 40, height: 40, borderRadius: 20 }]}>
                          <Icon name="award" size={18} color={Colors.turquoise} />
                        </View>
                        <View style={styles.adminUserMeta}>
                          <Text style={styles.adminUserName} numberOfLines={1}>{req.userName || t('profile.tonyteUser')}</Text>
                          <Text style={styles.adminUserEmail} numberOfLines={1}>{req.userEmail || t('settings.noAccountEmail')}</Text>
                        </View>
                      </View>

                      <View style={styles.verifDetailGrid}>
                        <View style={styles.verifDetailCell}>
                          <Text style={styles.verifDetailLabel}>{t('settings.requestedEmail')}</Text>
                          <Text style={styles.verifDetailValue} numberOfLines={1}>{req.submittedEmail || '—'}</Text>
                        </View>
                        <View style={styles.verifDetailCell}>
                          <Text style={styles.verifDetailLabel}>{t('settings.domain')}</Text>
                          <Text style={styles.verifDetailValue} numberOfLines={1}>{req.emailDomain || t('common.unknown')}</Text>
                        </View>
                        {requestedAt ? (
                          <View style={styles.verifDetailCell}>
                            <Text style={styles.verifDetailLabel}>{t('settings.requested')}</Text>
                            <Text style={styles.verifDetailValue}>{requestedAt}</Text>
                          </View>
                        ) : null}
                        <View style={styles.verifDetailCell}>
                          <Text style={styles.verifDetailLabel}>{t('settings.emailOwnership')}</Text>
                          <Text style={[styles.verifDetailValue, { color: ownershipConfirmed ? Colors.turquoise : 'rgba(255,255,255,0.42)' }]}>
                            {ownershipConfirmed ? t('settings.confirmed') : t('settings.notConfirmed')}
                          </Text>
                        </View>
                      </View>

                      <View style={styles.roleChipRow}>
                        <TouchableOpacity
                          style={[styles.roleChip, styles.roleChipActive, !ownershipConfirmed && { opacity: 0.42 }]}
                          disabled={isSaving || !ownershipConfirmed}
                          onPress={() => approveVerification(req.id, false)}
                          activeOpacity={0.82}
                        >
                          <Text style={styles.roleChipTextActive}>{isSaving ? '…' : `✓ ${t('settings.accept')}`}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[styles.roleChip, { borderColor: 'rgba(239,68,68,0.45)', backgroundColor: 'rgba(239,68,68,0.08)' }]}
                          disabled={isSaving}
                          onPress={() => denyVerification(req.id)}
                          activeOpacity={0.82}
                        >
                          <Text style={[styles.roleChipText, { color: '#f87171' }]}>{isSaving ? '…' : `✕ ${t('settings.reject')}`}</Text>
                        </TouchableOpacity>
                      </View>
                      <TouchableOpacity
                        style={[styles.roleChip, styles.verifSaveDomainBtn, !ownershipConfirmed && { opacity: 0.42 }]}
                        disabled={isSaving || !ownershipConfirmed}
                        onPress={() => approveVerification(req.id, true)}
                        activeOpacity={0.82}
                      >
                        <Text style={[styles.roleChipText, { color: Colors.turquoise }]}>{isSaving ? '…' : `✓ ${t('profile.acceptSaveDomain')}`}</Text>
                      </TouchableOpacity>
                    </View>
                  );
                })}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* Admin — Face Verification Requests */}
      <Modal
        visible={faceVerifModalOpen && canManageUsers}
        transparent
        animationType="slide"
        onRequestClose={() => setFaceVerifModalOpen(false)}
      >
        <View style={styles.adminModalOverlay}>
          <View style={[styles.adminModalSheet, { paddingBottom: insets.bottom + 18 }]}>
            <View style={styles.adminModalHeader}>
              <View>
                <Text style={styles.adminEyebrow}>{t('profile.admin')}</Text>
                <Text style={styles.adminModalTitle}>{t('profile.faceVerificationRequests')}</Text>
                <Text style={styles.adminModalSubtitle}>
                  {pendingFaceVerificationsLoading ? t('common.loading') : tp('profile.pendingReviewCount', pendingFaceVerifications.length)}
                </Text>
              </View>
              <TouchableOpacity
                style={styles.adminModalClose}
                onPress={() => setFaceVerifModalOpen(false)}
              >
                <Icon name="x" size={18} color="rgba(255,255,255,0.72)" />
              </TouchableOpacity>
            </View>

            {pendingFaceVerificationsLoading ? (
              <View style={styles.adminLoadingWrap}>
                <ActivityIndicator color={Colors.turquoise} />
              </View>
            ) : pendingFaceVerifications.length === 0 ? (
              <View style={styles.adminEmptyState}>
                <Icon name="check-circle" size={32} color="rgba(38,208,206,0.45)" />
                <Text style={[styles.adminEmptyText, { marginTop: 10 }]}>{t('settings.noPendingFace')}</Text>
              </View>
            ) : (
              <ScrollView style={styles.adminUserList} showsVerticalScrollIndicator={false}>
                {pendingFaceVerifications.map(req => {
                  const isSaving = faceVerifActionSaving === req.id;
                  return (
                    <View key={req.id} style={styles.adminUserRow}>
                      <View style={styles.adminUserTopRow}>
                        <Image source={{ uri: req.userPhoto || DEFAULT_AVATAR }} style={styles.adminUserAvatar} />
                        <View style={styles.adminUserMeta}>
                          <Text style={styles.adminUserName} numberOfLines={1}>{req.userName || t('profile.tonyteUser')}</Text>
                          <Text style={styles.adminUserEmail} numberOfLines={1}>{req.userEmail || t('settings.noAccountEmail')}</Text>
                          <Text style={styles.adminUserRoleLine} numberOfLines={1}>
                            {req.status || 'pending'} · {normalizeRequestDate(req.submittedAt, locale)}
                          </Text>
                        </View>
                      </View>

                      {req.selfieImageUrl ? (
                        <View style={styles.faceReviewSingleWrap}>
                          <Image source={{ uri: req.selfieImageUrl }} style={styles.faceReviewSingleImage} />
                          <Text style={styles.faceReviewLabel}>{t('settings.shakaSelfie')}</Text>
                        </View>
                      ) : (
                        <View style={styles.faceReviewGrid}>
                          {[
                            [t('settings.front'), req.frontImageUrl],
                            [t('settings.left'), req.leftImageUrl],
                            [t('settings.right'), req.rightImageUrl],
                          ].map(([label, uri]) => (
                            <View key={label} style={styles.faceReviewImageWrap}>
                              {uri ? <Image source={{ uri }} style={styles.faceReviewImage} /> : null}
                              <Text style={styles.faceReviewLabel}>{label}</Text>
                            </View>
                          ))}
                        </View>
                      )}

                      {req.verificationMethod === 'shaka_live' ? (
                        <View style={styles.adminVerificationBadgeRow}>
                          <VerificationCheckBadge label={t('settings.posePassLabel')} passed={req.poseVerified} />
                          <VerificationCheckBadge label={t('settings.livenessPassLabel')} passed={req.livenessVerified} />
                        </View>
                      ) : null}
                      {req.status === 'auto_approved' ? (
                        <View style={styles.adminVerificationBadgeRow}>
                          <VerificationCheckBadge label={t('settings.front')} passed={req.automatedResult?.front} />
                          <VerificationCheckBadge label={t('settings.left')} passed={req.automatedResult?.left} />
                          <VerificationCheckBadge label={t('settings.right')} passed={req.automatedResult?.right} />
                        </View>
                      ) : null}

                      {req.status === 'auto_approved' ? (
                        // Already automatically granted — the only admin
                        // action available here is post-hoc revocation
                        // (reuses the same revokeVibeVerificationEu path as
                        // the general user-management list's "Revoke
                        // Vibe Verified" button).
                        <View style={styles.roleChipRow}>
                          <Text style={[styles.roleChipText, { color: Colors.turquoise }]}>{t('settings.autoApproved')}</Text>
                          <TouchableOpacity
                            style={[styles.roleChip, { borderColor: 'rgba(239,68,68,0.45)', backgroundColor: 'rgba(239,68,68,0.08)' }]}
                            disabled={revokingBadgeUserId === req.userId}
                            onPress={() => revokeFaceBadge(req.userId, req.id)}
                            activeOpacity={0.82}
                          >
                            <Text style={[styles.roleChipText, { color: '#f87171' }]}>
                              {revokingBadgeUserId === req.userId ? '…' : t('settings.revokeVerified')}
                            </Text>
                          </TouchableOpacity>
                          {/* Fallback for Alert.alert's known stacking inconsistency over an
                              open native Modal on Android — see revokeFaceBadge's comment. */}
                          {revokeFeedback?.userId === req.userId ? (
                            <Text style={styles.revokeErrorText}>{revokeFeedback.message}</Text>
                          ) : null}
                        </View>
                      ) : (
                        <View style={styles.roleChipRow}>
                          <TouchableOpacity
                            style={[styles.roleChip, styles.roleChipActive]}
                            disabled={isSaving}
                            onPress={() => reviewFaceVerification(req, 'approved')}
                            activeOpacity={0.82}
                          >
                            <Text style={styles.roleChipTextActive}>{isSaving ? '…' : '✓ Approve'}</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[styles.roleChip, { borderColor: 'rgba(239,68,68,0.45)', backgroundColor: 'rgba(239,68,68,0.08)' }]}
                            disabled={isSaving}
                            onPress={() => reviewFaceVerification(req, 'rejected')}
                            activeOpacity={0.82}
                          >
                            <Text style={[styles.roleChipText, { color: '#f87171' }]}>{isSaving ? '…' : '✕ Reject'}</Text>
                          </TouchableOpacity>
                        </View>
                      )}
                    </View>
                  );
                })}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      <Modal
        visible={cityPickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setCityPickerOpen(false)}
      >
        <TouchableOpacity
          activeOpacity={1}
          style={styles.modalBackdrop}
          onPress={() => setCityPickerOpen(false)}
        >
          <TouchableOpacity activeOpacity={1} style={styles.cityPickerModal}>
            <View style={styles.cityPickerHeader}>
              <Text style={styles.cityPickerTitle}>{t('settings.eventCity')}</Text>
              <TouchableOpacity onPress={() => setCityPickerOpen(false)}>
                <Icon name="x" size={18} color="rgba(255,255,255,0.72)" />
              </TouchableOpacity>
            </View>
            <Text style={styles.cityPickerSubtitle}>{t('settings.eventCityHelp')}</Text>
            {visibleCities.map(city => {
              const selected = city.value === selectedCityKey;
              return (
                <TouchableOpacity
                  key={city.value}
                  style={[styles.cityOption, selected && styles.cityOptionActive]}
                  onPress={() => {
                    setSelectedCityKey(city.value);
                    setCityPickerOpen(false);
                  }}
                >
                  <Text style={[styles.cityOptionText, selected && styles.cityOptionTextActive]}>
                    {city.label}
                  </Text>
                  {selected ? <Icon name="check" size={16} color={Colors.sunset} /> : null}
                </TouchableOpacity>
              );
            })}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </TonyteBackground>
  );
};

const useProfileAppearance = () => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createProfileAppearanceStyles(theme), [theme]);
  return { theme, appearance };
};

const ProfileCompletionPrompt: React.FC<{ score: number; onPress: () => void; tile?: boolean }> = ({ score, onPress, tile = false }) => {
  const { t } = useI18n();
  const { theme, appearance } = useProfileAppearance();
  const complete = score >= 100;
  const isFocused = useIsFocused();
  const [compact, setCompact] = useState(complete);
  const dissolve = useRef(new Animated.Value(1)).current;
  const previousComplete = useRef(complete);

  useEffect(() => {
    if (!isFocused) return;
    if (previousComplete.current === complete) return;
    previousComplete.current = complete;
    Animated.timing(dissolve, {
      toValue: 0,
      duration: 150,
      easing: Easing.in(Easing.quad),
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (!finished) return;
      setCompact(complete);
      Animated.timing(dissolve, {
        toValue: 1,
        duration: 240,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
    });
    return () => dissolve.stopAnimation();
  }, [complete, dissolve, isFocused]);

  return (
    <Animated.View
      style={[
        styles.completionPromptOuter,
        tile && styles.completionTileOuter,
        {
          opacity: dissolve,
          transform: [{ scale: dissolve.interpolate({ inputRange: [0, 1], outputRange: [0.975, 1] }) }],
        },
      ]}
    >
      <TouchableOpacity
        style={[
          styles.completionCard,
          compact && styles.completionCardCompact,
          tile && styles.completionTile,
          appearance.completionCardSurface,
        ]}
        onPress={onPress}
        activeOpacity={0.82}
        accessibilityRole="button"
        accessibilityLabel={complete ? t('settings.editProfileAnswers') : t('settings.completeProfile')}
      >
        <LinearGradient
          pointerEvents="none"
          colors={theme.isDark
            ? ['rgba(38,208,206,0.12)', 'rgba(38,208,206,0.035)']
            : ['rgba(21,155,154,0.12)', 'rgba(255,252,246,0.72)']}
          style={StyleSheet.absoluteFill}
        />
        <View style={[styles.completionLeft, tile && styles.completionTileLeft]}>
          <View style={[styles.completionIconWrap, compact && styles.completionIconWrapCompact, tile && styles.completionTileIcon, appearance.completionIconSurface]}>
            <Icon name={compact ? 'check' : 'zap'} size={compact ? 15 : 18} color={theme.colors.accentSecondary} />
          </View>
          <View style={styles.completionText}>
            <Text style={[styles.completionTitle, appearance.textPrimary]} numberOfLines={tile ? 2 : undefined}>
              {compact ? t('settings.profileCompleted') : t('settings.completeProfile')}
            </Text>
            {tile ? (
              compact ? null : (
                <View style={styles.completionTileProgress}>
                  <View style={[styles.completionBarTrack, styles.completionTileBar, appearance.completionBarTrack]}>
                    <View style={[styles.completionBarFill, { width: `${Math.max(0, Math.min(score, 100))}%` }]} />
                  </View>
                  <Text style={[styles.completionTilePct, appearance.interestAccentText]}>{score}%</Text>
                </View>
              )
            ) : (
              <Text style={[styles.completionSub, appearance.completionSub]} numberOfLines={compact ? 1 : 2}>
                {compact ? t('settings.editProfileAnswers') : t('completeProfile.enhanceExperience')}
              </Text>
            )}
          </View>
        </View>
        {tile ? null : compact ? (
          <Icon name="chevron-right" size={18} color={theme.colors.iconMuted} />
        ) : (
          <View style={styles.completionRight}>
            <Text style={[styles.completionPct, appearance.interestAccentText]}>{score}%</Text>
            <View style={[styles.completionBarTrack, appearance.completionBarTrack]}>
              <View style={[styles.completionBarFill, { width: `${Math.max(0, Math.min(score, 100))}%` }]} />
            </View>
          </View>
        )}
      </TouchableOpacity>
    </Animated.View>
  );
};

const GlassCard: React.FC<{ children: React.ReactNode; style?: object }> = ({ children, style }) => {
  const { theme, appearance } = useProfileAppearance();
  return (
    <View style={[styles.glassCard, appearance.cardSurface, style]}>
      <LinearGradient
        pointerEvents="none"
        colors={theme.isDark
          ? ['rgba(255,255,255,0.12)', 'rgba(255,255,255,0.045)']
          : ['rgba(255,255,255,0.86)', 'rgba(250,246,238,0.58)']}
        style={StyleSheet.absoluteFill}
      />
      {children}
    </View>
  );
};

const PillButton: React.FC<{ icon: string; label: string; onPress: () => void }> = ({ icon, label, onPress }) => {
  const { theme, appearance } = useProfileAppearance();
  return (
    <TouchableOpacity style={[styles.pillButton, appearance.chipSurface]} onPress={onPress} activeOpacity={0.78}>
      <Icon name={icon} size={16} color={theme.colors.icon} />
      <Text style={[styles.pillButtonText, appearance.textSecondary]}>{label}</Text>
    </TouchableOpacity>
  );
};

const ProfileBadgePill: React.FC<{ icon: string; label: string; color: string }> = ({ icon, label, color }) => {
  const { appearance } = useProfileAppearance();
  return (
    <View style={[styles.profileBadgePill, appearance.chipSurface]}>
      <Icon name={icon as any} size={13} color={color} />
      <Text style={[styles.profileBadgePillText, appearance.textSecondary]} numberOfLines={1}>{label}</Text>
    </View>
  );
};

const ProfileSectionHeader: React.FC<{ title: string; action?: string; onPress?: () => void }> = ({ title, action, onPress }) => {
  const { appearance } = useProfileAppearance();
  return (
    <View style={styles.profileSectionHeader}>
      <Text style={[styles.profileSectionTitle, appearance.textPrimary]}>{title}</Text>
      {action && onPress ? (
        <TouchableOpacity onPress={onPress} activeOpacity={0.78}>
          <Text style={styles.profileSectionAction}>{action}</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

const AvatarCluster: React.FC<{ count: number }> = ({ count }) => {
  const { tp } = useI18n();
  return (
    <View style={styles.avatarCluster}>
      {[0, 1, 2].map(index => (
        <View key={index} style={[styles.avatarClusterDot, { marginLeft: index === 0 ? 0 : -8, backgroundColor: index === 0 ? Colors.sunset : index === 1 ? Colors.turquoise : Colors.purple }]} />
      ))}
      <Text style={styles.avatarClusterText}>{tp('event.joinedCount', count)}</Text>
    </View>
  );
};

const PlanCard: React.FC<{ plan: TonyteActivity; locale: string; fallbackImage: string; onPress: () => void }> = ({ plan, locale, fallbackImage, onPress }) => {
  const joined = getPlanMemberCount(plan);
  return (
    <TouchableOpacity style={styles.planRailCard} onPress={onPress} activeOpacity={0.86}>
      <Image source={{ uri: getActivityImage(plan, fallbackImage) }} style={styles.planRailImage} />
      <LinearGradient colors={['rgba(0,0,0,0)', 'rgba(5,8,14,0.9)']} style={StyleSheet.absoluteFill} />
      <View style={styles.planRailContent}>
        <Text style={styles.planRailDate}>{getActivityDateLabel(plan, locale)}</Text>
        <Text style={styles.planRailTitle} numberOfLines={2}>{plan.title}</Text>
        <AvatarCluster count={joined} />
      </View>
    </TouchableOpacity>
  );
};

const CreatePlanCard: React.FC<{ onPress: () => void; label: string; variant?: 'orange' | 'teal' }> = ({ onPress, label, variant = 'orange' }) => {
  const { appearance } = useProfileAppearance();
  return (
    <TouchableOpacity style={styles.createPlanCard} onPress={onPress} activeOpacity={0.84}>
      <View style={[styles.createPlanIcon, variant === 'teal' && styles.createPlanIconTeal]}>
        <Icon name="plus" size={24} color="#fff" />
      </View>
      <Text style={[styles.createPlanTitle, appearance.textSecondary, variant === 'teal' && styles.createPlanTitleTeal, appearance.actionLabel]}>{label}</Text>
    </TouchableOpacity>
  );
};

const EventRailCard: React.FC<{ event: TonyteActivity; locale: string; onPress: () => void }> = ({ event, locale, onPress }) => {
  const { appearance } = useProfileAppearance();
  const [imageSource, setImageSource] = useState<number | string | null>(null);
  const [usedFallback, setUsedFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setImageSource(null);
    setUsedFallback(false);
    resolveActivityImage(event)
      .then(resolved => {
        if (!cancelled) setImageSource(resolved.source);
      })
      .catch(error => {
        console.warn('[Tonyte][RN][Profile] interested event image resolution failed', {
          eventId: event.id,
          error,
        });
        if (!cancelled) {
          setUsedFallback(true);
          setImageSource(getWebFallbackImage(event));
        }
      });
    return () => { cancelled = true; };
  }, [event]);

  return (
    <TouchableOpacity style={[styles.eventRailCard, appearance.cardSurface]} onPress={onPress} activeOpacity={0.86}>
      {imageSource ? (
        <Image
          source={typeof imageSource === 'number' ? imageSource : { uri: imageSource }}
          style={styles.eventRailImage}
          resizeMode="cover"
          onError={error => {
            console.warn('[Tonyte][RN][Profile] interested event image load failed', {
              eventId: event.id,
              imageSource,
              nativeEvent: error.nativeEvent,
            });
            if (!usedFallback) {
              setUsedFallback(true);
              setImageSource(getWebFallbackImage(event));
            } else {
              setImageSource(null);
            }
          }}
        />
      ) : (
        <LinearGradient
          colors={['rgba(38,208,206,0.18)', 'rgba(161,140,209,0.10)', 'rgba(7,11,20,0.82)']}
          style={styles.eventRailImage}
        />
      )}
      <View style={styles.eventRailCopy}>
        <Text style={[styles.eventRailTitle, appearance.textPrimary]} numberOfLines={2}>{event.title}</Text>
        <Text style={[styles.eventRailVenue, appearance.textMuted]} numberOfLines={1}>{getActivityVenueLabel(event)}</Text>
        <Text style={styles.eventRailDate}>{getActivityDateLabel(event, locale)}</Text>
      </View>
    </TouchableOpacity>
  );
};

const ProfileRailEmptyState: React.FC<{ label: string }> = ({ label }) => {
  const { theme, appearance } = useProfileAppearance();
  return (
    <View style={[styles.profileRailEmpty, appearance.emptySurface]}>
      <Icon name="calendar" size={18} color={theme.colors.iconMuted} />
      <Text style={[styles.profileRailEmptyText, appearance.textMuted, appearance.emptyText]}>{label}</Text>
    </View>
  );
};

const TodayVibeCard: React.FC<{
  aura: string | null;
  auraType: AuraType | null;
  auraLoaded: boolean;
  matches: number;
  loading?: boolean;
  onPress: () => void;
}> = ({ aura, auraType, auraLoaded, matches, loading, onPress }) => {
  const { t, tp } = useI18n();
  const { theme, appearance } = useProfileAppearance();
  // No answers yet today → invite them to the questions instead of showing
  // a made-up vibe.
  const needsAnswers = auraLoaded && !auraType;
  return (
    <TouchableOpacity style={[styles.todayVibeCard, appearance.cardSurface]} onPress={onPress} activeOpacity={0.86}>
      <LinearGradient
        colors={theme.isDark
          ? ['rgba(255,122,0,0.16)', 'rgba(38,208,206,0.10)', 'rgba(161,140,209,0.10)']
          : ['rgba(255,145,53,0.13)', 'rgba(83,157,158,0.08)', 'rgba(128,103,166,0.07)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.todayVibeGlow} />
      <View style={styles.todayVibeRow}>
        {/* AuraBadge is a fixed 88pt design (shared with DayVibesAuraScreen);
            scaled down here so the card stays one compact row. */}
        <View style={styles.todayVibeBadgeSlot}>
          {auraType ? (
            <View style={styles.todayVibeBadgeScale}>
              <AuraBadge auraType={auraType} />
            </View>
          ) : (
            <View style={styles.todayVibePromptIcon}>
              <Icon name={needsAnswers ? 'help-circle' : 'loader'} size={24} color="#FF9A3C" />
            </View>
          )}
        </View>
        <View style={styles.todayVibeCopy}>
          <Text style={[styles.todayVibeKicker, appearance.textMuted]}>{t('profile.todaysVibe')}</Text>
          <Text style={[styles.todayVibeAura, appearance.textPrimary]} numberOfLines={1}>
            {aura ?? (needsAnswers ? t('profile.todaysVibePrompt') : '…')}
          </Text>
          <Text style={[styles.todayVibeMatches, appearance.textSecondary]} numberOfLines={1}>
            {needsAnswers
              ? t('profile.todaysVibePromptSub')
              : !aura
                ? ' '
                : loading
                  ? t('profile.loadingVibeMatches')
                  : tp('profile.vibeMatchWaitingCount', matches)}
          </Text>
        </View>
        <Icon name="chevron-right" size={18} color={theme.colors.iconMuted} />
      </View>
    </TouchableOpacity>
  );
};

const AchievementPill: React.FC<{ icon: string; label: string; color: string }> = ({ icon, label, color }) => {
  const { appearance } = useProfileAppearance();
  return (
    <View style={[styles.achievementPill, appearance.chipSurface]}>
      <Icon name={icon as any} size={15} color={color} />
      <Text style={[styles.achievementText, appearance.textSecondary]}>{label}</Text>
    </View>
  );
};

const DrawerRow: React.FC<{ icon: string; label: string; onPress: () => void; destructive?: boolean }> = ({
  icon,
  label,
  onPress,
  destructive,
}) => {
  const { theme, appearance } = useProfileAppearance();
  return (
    <TouchableOpacity style={[styles.drawerRow, appearance.drawerRow]} onPress={onPress} activeOpacity={0.78}>
      <View style={[styles.drawerRowIcon, appearance.drawerIcon, destructive && styles.drawerRowIconDestructive]}>
        <Icon name={icon} size={17} color={destructive ? theme.colors.accent : theme.colors.icon} />
      </View>
      <Text style={[styles.drawerRowLabel, appearance.textPrimary, destructive && styles.drawerRowDestructive]}>{label}</Text>
      <Icon name="chevron-right" size={17} color={theme.colors.iconMuted} />
    </TouchableOpacity>
  );
};

const PremiumSettingsRow = React.memo(({
  icon,
  label,
  onPress,
  destructive = false,
  index,
  visible,
  matches = true,
}: {
  icon: string;
  label: string;
  onPress: () => void;
  destructive?: boolean;
  index: number;
  visible: boolean;
  matches?: boolean;
}) => {
  const { theme, appearance } = useProfileAppearance();
  const entrance = useRef(new Animated.Value(0)).current;
  const press = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!visible) {
      entrance.setValue(0);
      return;
    }
    Animated.timing(entrance, {
      toValue: 1,
      duration: 460,
      delay: 150 + index * 34,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [entrance, index, visible]);

  const animatePress = (toValue: number) => {
    Animated.spring(press, {
      toValue,
      speed: toValue ? 34 : 20,
      bounciness: toValue ? 0 : 7,
      useNativeDriver: true,
    }).start();
  };

  if (!matches) return null;

  return (
    <Animated.View
      style={[
        styles.premiumSettingsRowEntrance,
        {
          opacity: entrance,
          transform: [
            { translateY: entrance.interpolate({ inputRange: [0, 1], outputRange: [16, 0] }) },
            { scale: press.interpolate({ inputRange: [0, 1], outputRange: [1, 0.98] }) },
          ],
        },
      ]}
    >
      <Pressable
        style={[styles.premiumSettingsRow, appearance.settingsRow]}
        onPress={onPress}
        onPressIn={() => animatePress(1)}
        onPressOut={() => animatePress(0)}
      >
        <LinearGradient
          pointerEvents="none"
          colors={theme.isDark
            ? destructive
              ? ['rgba(242,125,38,0.12)', 'rgba(15,18,29,0.94)']
              : ['rgba(255,255,255,0.095)', 'rgba(20,23,36,0.88)', 'rgba(7,10,18,0.96)']
            : destructive
              ? ['rgba(242,125,38,0.12)', 'rgba(255,248,239,0.94)']
              : theme.gradients.control}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
        <View pointerEvents="none" style={styles.premiumRowHighlight} />
        <Animated.View
          style={[
            styles.premiumSettingsIcon,
            appearance.settingsIcon,
            destructive && styles.premiumSettingsIconDestructive,
            {
              transform: [{ rotate: press.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '2.5deg'] }) }],
            },
          ]}
        >
          <LinearGradient
            colors={theme.isDark
              ? destructive
                ? ['rgba(242,125,38,0.32)', 'rgba(91,40,16,0.34)']
                : ['rgba(255,255,255,0.18)', 'rgba(161,140,209,0.14)', 'rgba(38,208,206,0.08)']
              : destructive
                ? ['rgba(242,125,38,0.24)', 'rgba(255,244,232,0.70)']
                : ['rgba(255,255,255,0.92)', 'rgba(236,226,212,0.82)', 'rgba(21,155,154,0.08)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={StyleSheet.absoluteFill}
          />
          <View style={styles.premiumIconReflection} />
          <Icon name={icon} size={17} color={destructive ? theme.colors.accent : theme.colors.icon} />
        </Animated.View>
        <Text
          style={[styles.premiumSettingsLabel, appearance.textPrimary, destructive && styles.premiumSettingsLabelDestructive]}
          numberOfLines={1}
        >
          {label}
        </Text>
        <Animated.View
          style={{ transform: [{ translateX: press.interpolate({ inputRange: [0, 1], outputRange: [0, 3] }) }] }}
        >
          <Icon name="chevron-right" size={18} color={theme.colors.iconMuted} />
        </Animated.View>
      </Pressable>
    </Animated.View>
  );
});

const PremiumSettingsGroup: React.FC<{ title: string; children: React.ReactNode; visible?: boolean }> = ({ title, children, visible = true }) => {
  const { appearance } = useProfileAppearance();
  return visible ? (
    <View style={styles.premiumSettingsGroup}>
      <Text style={[styles.premiumSettingsGroupTitle, appearance.textMuted]}>{title}</Text>
      <View style={styles.premiumSettingsGroupRows}>{children}</View>
    </View>
  ) : null;
};

const GlassPanel: React.FC<{ children: React.ReactNode; noPadding?: boolean }> = ({ children, noPadding }) => {
  const { theme, appearance } = useProfileAppearance();
  return (
    <View style={[styles.sidebarPanel, appearance.cardSurface]}>
      <LinearGradient
        pointerEvents="none"
        colors={theme.isDark
          ? ['rgba(255,255,255,0.1)', 'rgba(255,255,255,0.045)']
          : ['rgba(255,255,255,0.72)', 'rgba(248,239,226,0.54)']}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.sidebarPanelFrame}>
        <View style={[styles.sidebarPanelContent, noPadding && styles.sidebarPanelContentNoPadding]}>
          {children}
        </View>
      </View>
    </View>
  );
};

const ToggleRow: React.FC<{ label: string; enabled: boolean; disabled?: boolean; onPress: () => void }> = ({
  label,
  enabled,
  disabled,
  onPress,
}) => {
  const { theme } = useTheme();
  const { appearance } = useProfileAppearance();
  return (
    <TouchableOpacity style={[styles.toggleRow, appearance.drawerRow]} disabled={disabled} onPress={onPress} activeOpacity={0.78}>
      <Text style={[styles.toggleLabel, appearance.textPrimary]}>{label}</Text>
      <View style={[styles.toggleTrack, !theme.isDark && appearance.toggleTrack, enabled && styles.toggleTrackActive]}>
        <View style={[styles.toggleThumb, enabled && styles.toggleThumbActive]} />
      </View>
    </TouchableOpacity>
  );
};

const interestIcon = (interest: string) => {
  const value = interest.toLowerCase();
  if (value.includes('travel')) return 'navigation';
  if (value.includes('music')) return 'music';
  if (value.includes('photo')) return 'camera';
  if (value.includes('food')) return 'coffee';
  if (value.includes('book')) return 'book-open';
  if (value.includes('adventure') || value.includes('hike')) return 'map';
  return 'compass';
};

const interestColor = (interest: string) => {
  const value = interest.toLowerCase();
  if (value.includes('food')) return '#fdba74';
  if (value.includes('book')) return '#fcd34d';
  if (value.includes('adventure') || value.includes('hike')) return '#86efac';
  if (value.includes('music')) return Colors.purple;
  return Colors.turquoise;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.bg,
  },
  safeArea: {
    flex: 1,
  },
  scroll: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  scrollContent: {
    width: '100%',
    backgroundColor: 'transparent',
    overflow: 'visible',
  },
  desktopScrollContent: { width: '100%', maxWidth: 1180, alignSelf: 'center' },
  coverWrap: {
    height: 256,
    width: '100%',
    overflow: 'hidden',
  },
  coverWrapIOS: {
    height: 276,
  },
  cover: {
    ...StyleSheet.absoluteFill,
  },
  premiumCoverWrap: {
    height: 318,
    width: '100%',
    overflow: 'hidden',
    backgroundColor: Colors.bg,
  },
  desktopCoverWrap: { height: 220, borderBottomLeftRadius: 12, borderBottomRightRadius: 12 },
  premiumCover: {
    ...StyleSheet.absoluteFill,
  },
  coverGlowBlue: {
    position: 'absolute',
    left: -70,
    bottom: 10,
    width: 190,
    height: 190,
    borderRadius: 95,
    backgroundColor: 'rgba(38,208,206,0.16)',
  },
  coverGlowPurple: {
    position: 'absolute',
    right: -82,
    bottom: 20,
    width: 210,
    height: 210,
    borderRadius: 105,
    backgroundColor: 'rgba(161,140,209,0.16)',
  },
  premiumBody: {
    marginTop: -96,
    paddingHorizontal: 18,
    gap: 18,
    width: '100%',
    maxWidth: 560,
    alignSelf: 'center',
  },
  desktopPremiumBody: { marginTop: -70, maxWidth: 1080, paddingHorizontal: 24, gap: 14 },
  premiumIdentity: {
    alignItems: 'center',
    gap: 12,
  },
  avatarCenterWrap: {
    position: 'relative',
  },
  premiumAvatarWrap: {
    position: 'relative',
    width: 150,
    height: 150,
    borderRadius: 75,
    overflow: 'visible',
    shadowColor: '#000',
    shadowOpacity: 0.5,
    shadowRadius: 30,
  },
  premiumAvatar: {
    width: 150,
    height: 150,
    borderRadius: 75,
    borderWidth: 4,
    borderColor: 'rgba(255,255,255,0.72)',
    backgroundColor: Colors.bgCardAlt,
  },
  premiumAvatarBadgePosition: {
    top: -2,
    bottom: undefined,
    right: -2,
  },
  premiumCameraButton: {
    position: 'absolute',
    right: 8,
    bottom: 10,
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.28)',
    backgroundColor: 'rgba(7,11,20,0.78)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  premiumNameBlock: {
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  premiumName: {
    color: Colors.textPrimary,
    fontSize: 34,
    lineHeight: 39,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  premiumUsername: {
    marginTop: 4,
    color: 'rgba(255,255,255,0.46)',
    fontSize: Typography.sm,
    fontWeight: Typography.medium,
  },
  premiumLocationRow: {
    minHeight: 30,
    borderRadius: Radius.pill,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  premiumLocationText: {
    color: 'rgba(255,255,255,0.78)',
    fontSize: Typography.sm,
    fontWeight: Typography.medium,
  },
  premiumDobRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  premiumDobText: {
    color: 'rgba(255,255,255,0.46)',
    fontSize: Typography.xs,
    fontWeight: Typography.medium,
  },
  premiumBio: {
    maxWidth: 500,
    color: 'rgba(255,255,255,0.82)',
    fontSize: Typography.base,
    lineHeight: 23,
    textAlign: 'center',
  },
  compactBadgeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 8,
  },
  profileBadgePill: {
    minHeight: 31,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    backgroundColor: 'rgba(255,255,255,0.075)',
    paddingHorizontal: 11,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  profileBadgePillText: {
    color: 'rgba(255,255,255,0.86)',
    fontSize: 11,
    fontWeight: Typography.bold,
  },
  // Edit profile + My Partner Profile side by side (Edit profile alone,
  // full row, for non-partners).
  profileActionRow: { flexDirection: 'row', gap: 10, width: '100%', maxWidth: 520, marginBottom: 10 },
  profileActionButton: { flex: 1, width: undefined, minHeight: 52, marginTop: 0, paddingHorizontal: 12 },
  partnerProfileButton: {
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(255,154,60,0.38)',
    backgroundColor: 'rgba(255,122,0,0.08)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  partnerProfileText: { flexShrink: 1, color: '#fff', fontSize: Typography.base, fontWeight: Typography.bold },
  premiumEditButton: {
    width: '100%',
    maxWidth: 520,
    minHeight: 52,
    marginTop: 2,
    borderRadius: 22,
    backgroundColor: '#FF7A00',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    shadowColor: '#FF7A00',
    shadowOpacity: 0.24,
    shadowRadius: 24,
  },
  vibesHubButton: {
    width: '100%',
    maxWidth: 520,
    minHeight: 76,
    borderRadius: 23,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: 'rgba(255,122,0,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(255,122,0,0.24)',
  },
  vibesHubTile: { flex: 1, width: undefined, minHeight: 62, borderRadius: 18, paddingHorizontal: 12, gap: 10 },
  vibesHubTileIcon: { width: 34, height: 34, borderRadius: 12 },
  vibesHubIcon: {
    width: 44,
    height: 44,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FF7A00',
    shadowColor: '#FF7A00',
    shadowOpacity: 0.25,
    shadowRadius: 14,
  },
  vibesHubCopy: {
    flex: 1,
    minWidth: 0,
  },
  vibesHubTitle: {
    color: '#fff',
    fontSize: Typography.md,
    fontWeight: Typography.extrabold,
  },
  vibesHubSubtitle: {
    marginTop: 3,
    color: 'rgba(255,255,255,0.52)',
    fontSize: 11,
    lineHeight: 16,
  },
  profileSectionHeader: {
    marginTop: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 2,
  },
  profileSectionTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.extrabold,
  },
  profileSectionAction: {
    color: 'rgba(125,211,252,0.86)',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  horizontalRail: {
    gap: 14,
    paddingRight: 18,
    paddingBottom: 2,
    alignItems: 'center',
  },
  planRailCard: {
    width: 190,
    height: 238,
    borderRadius: 26,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    backgroundColor: Colors.bgCard,
    overflow: 'hidden',
  },
  planRailImage: {
    ...StyleSheet.absoluteFill,
    width: '100%',
    height: '100%',
  },
  planRailContent: {
    flex: 1,
    justifyContent: 'flex-end',
    padding: 15,
  },
  planRailDate: {
    color: '#fdba74',
    fontSize: 11,
    fontWeight: Typography.bold,
    marginBottom: 6,
  },
  planRailTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    lineHeight: 21,
    fontWeight: Typography.extrabold,
  },
  avatarCluster: {
    marginTop: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarClusterDot: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: '#07101d',
  },
  avatarClusterText: {
    marginLeft: 8,
    color: 'rgba(255,255,255,0.72)',
    fontSize: 11,
    fontWeight: Typography.bold,
  },
  profileRailEmpty: {
    width: 186,
    minHeight: 92,
    borderRadius: 18,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.025)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
    gap: 8,
  },
  profileRailEmptyText: {
    color: 'rgba(255,255,255,0.48)',
    fontSize: Typography.xs,
    lineHeight: 17,
    textAlign: 'center',
    fontWeight: Typography.medium,
  },
  createPlanCard: {
    width: 82,
    minHeight: 92,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    gap: 8,
  },
  createPlanIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: 'rgba(255,122,0,0.72)',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FF7A00',
    shadowColor: '#FF7A00',
    shadowOpacity: 0.28,
    shadowRadius: 18,
  },
  createPlanIconTeal: {
    borderColor: 'rgba(38,208,206,0.72)',
    backgroundColor: Colors.turquoise,
    shadowColor: Colors.turquoise,
  },
  createPlanTitle: {
    color: 'rgba(255,255,255,0.82)',
    fontSize: 11,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  createPlanTitleTeal: {
    color: 'rgba(224,242,254,0.9)',
  },
  eventRailCard: {
    width: 174,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    backgroundColor: 'rgba(16,23,34,0.74)',
    overflow: 'hidden',
  },
  eventRailImage: {
    width: '100%',
    height: 118,
    backgroundColor: Colors.bgCardAlt,
  },
  eventRailCopy: {
    padding: 13,
  },
  eventRailTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    lineHeight: 20,
    fontWeight: Typography.extrabold,
  },
  eventRailVenue: {
    marginTop: 6,
    color: 'rgba(255,255,255,0.56)',
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  eventRailDate: {
    marginTop: 8,
    color: Colors.sunset,
    fontSize: 11,
    fontWeight: Typography.bold,
  },
  todayVibeCard: {
    width: '100%',
    alignSelf: 'stretch',
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.11)',
    backgroundColor: 'rgba(16,23,34,0.74)',
    overflow: 'hidden',
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  todayVibeGlow: {
    position: 'absolute',
    left: -30,
    top: -40,
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: 'rgba(255,122,0,0.16)',
  },
  todayVibeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  todayVibeBadgeSlot: {
    width: 54,
    height: 54,
    alignItems: 'center',
    justifyContent: 'center',
  },
  todayVibePromptIcon: {
    width: 46,
    height: 46,
    borderRadius: 23,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,122,0,0.14)',
    borderWidth: 1,
    borderColor: 'rgba(255,154,60,0.3)',
  },
  todayVibeBadgeScale: {
    transform: [{ scale: 0.62 }],
  },
  todayVibeCopy: {
    flex: 1,
    minWidth: 0,
  },
  todayVibeKicker: {
    color: 'rgba(255,255,255,0.52)',
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
  },
  todayVibeAura: {
    marginTop: 2,
    color: Colors.textPrimary,
    fontSize: 20,
    lineHeight: 25,
    fontWeight: Typography.extrabold,
  },
  todayVibeMatches: {
    marginTop: 1,
    color: 'rgba(255,255,255,0.76)',
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  badgeRail: {
    gap: 10,
    paddingRight: 18,
  },
  achievementPill: {
    minHeight: 40,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    backgroundColor: 'rgba(255,255,255,0.075)',
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  achievementText: {
    color: 'rgba(255,255,255,0.88)',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  topHeader: {
    position: 'absolute',
    left: 24,
    right: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 4,
  },
  topButton: {
    marginLeft: 'auto',
    width: 40,
    height: 40,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    marginTop: -80,
    paddingHorizontal: 24,
    gap: 16,
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
  },
  bodyIOS: {
    alignSelf: 'stretch',
    maxWidth: '100%',
    marginTop: -74,
    paddingHorizontal: 18,
    gap: 17,
    backgroundColor: 'transparent',
    overflow: 'visible',
  },
  identity: {
    alignItems: 'center',
  },
  avatarWrap: {
    position: 'relative',
    overflow: 'visible',
  },
  avatar: {
    width: 144,
    height: 144,
    borderRadius: 72,
    borderWidth: 3,
    borderColor: 'rgba(255,255,255,0.6)',
    backgroundColor: Colors.bgCardAlt,
    ...Shadow.strong,
  },
  cameraButton: {
    position: 'absolute',
    right: 10,
    bottom: 8,
    width: 39,
    height: 39,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(186,230,253,0.4)',
    backgroundColor: 'rgba(0,0,0,0.62)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  name: {
    color: Colors.textPrimary,
    fontSize: 36,
    lineHeight: 42,
    fontWeight: Typography.semibold,
    textAlign: 'center',
    flexShrink: 1,
    width: '100%',
  },
  nameAuraRow: {
    marginTop: 20,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: '100%',
    paddingHorizontal: 18,
  },
  username: {
    marginTop: 4,
    color: 'rgba(255,255,255,0.45)',
    fontSize: Typography.sm,
  },
  locationRow: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  locationText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: Typography.sm,
  },
  about: {
    maxWidth: 520,
    marginTop: 12,
    color: 'rgba(255,255,255,0.8)',
    fontSize: Typography.base,
    lineHeight: 23,
    textAlign: 'center',
  },
  aboutEmpty: {
    color: 'rgba(255,255,255,0.45)',
  },
  studentBadgeWrap: {
    marginTop: 12,
    alignItems: 'center',
    gap: 6,
  },
  studentBadge: {
    minHeight: 34,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(125,211,252,0.25)',
    backgroundColor: 'rgba(125,211,252,0.08)',
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    shadowColor: '#6366f1',
    shadowOpacity: 0.24,
    shadowRadius: 28,
  },
  studentBadgeText: {
    color: '#e0f2fe',
    fontSize: 12,
    fontWeight: Typography.bold,
  },
  studentBadgeCheck: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#7dd3fc',
    alignItems: 'center',
    justifyContent: 'center',
  },
  studentUniversity: {
    color: 'rgba(224,242,254,0.6)',
    fontSize: 11,
    fontWeight: Typography.semibold,
  },
  faceVerifiedBadgeWrap: {
    marginTop: 10,
    alignItems: 'center',
  },
  faceVerifiedBadge: {
    minHeight: 30,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.35)',
    backgroundColor: 'rgba(38,208,206,0.10)',
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  faceVerifiedBadgeText: {
    color: Colors.turquoise,
    fontSize: 11,
    fontWeight: Typography.bold,
  },
  faceVerifiedBadgeCheck: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: Colors.turquoise,
    alignItems: 'center',
    justifyContent: 'center',
  },
  faceVerifCard: {
    width: '100%',
    maxWidth: 520,
    minHeight: 100,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    backgroundColor: 'rgba(7,16,29,0.65)',
    padding: 16,
    flexDirection: 'row',
    gap: 14,
    overflow: 'hidden',
  },
  faceVerifBorder: {
    ...StyleSheet.absoluteFill,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.12)',
  },
  faceVerifIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  faceVerifContent: {
    flex: 1,
  },
  faceVerifTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.bold,
  },
  faceVerifBody: {
    marginTop: 4,
    color: 'rgba(255,255,255,0.55)',
    fontSize: Typography.sm,
    lineHeight: 18,
  },
  faceVerifAction: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  faceVerifActionText: {
    color: 'rgba(224,242,254,0.75)',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  revokeBadgeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(248,113,113,0.3)',
    backgroundColor: 'rgba(248,113,113,0.08)',
    alignSelf: 'flex-start',
  },
  revokeBadgeText: {
    color: '#f87171',
    fontSize: 12,
    fontWeight: Typography.semibold,
  },
  // Fallback error display for when Alert.alert doesn't render above the
  // open Modal these buttons live in — see revokeFaceBadge's comment.
  revokeErrorText: {
    marginTop: 6,
    color: '#fbbf24',
    fontSize: 11,
    fontWeight: Typography.semibold,
  },
  adminDangerRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 10,
  },
  suspendBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(250,204,21,0.32)',
    backgroundColor: 'rgba(250,204,21,0.08)',
  },
  suspendBtnText: {
    color: '#facc15',
    fontSize: 12,
    fontWeight: Typography.semibold,
  },
  deleteAccountBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(248,113,113,0.4)',
    backgroundColor: 'rgba(248,113,113,0.12)',
  },
  deleteAccountBtnText: {
    color: '#f87171',
    fontSize: 12,
    fontWeight: Typography.bold,
  },
  studentCard: {
    width: '100%',
    maxWidth: 520,
    minHeight: 132,
    marginTop: 20,
    borderRadius: 28,
    borderWidth: 1,
    borderColor: 'rgba(125,211,252,0.16)',
    backgroundColor: 'rgba(7,16,29,0.72)',
    padding: 20,
    flexDirection: 'row',
    gap: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.38,
    shadowRadius: 28,
  },
  studentGlowA: {
    position: 'absolute',
    left: -64,
    top: -64,
    width: 144,
    height: 144,
    borderRadius: 72,
    backgroundColor: 'rgba(56,189,248,0.18)',
  },
  studentGlowB: {
    position: 'absolute',
    right: -52,
    bottom: -76,
    width: 176,
    height: 176,
    borderRadius: 88,
    backgroundColor: 'rgba(139,92,246,0.2)',
  },
  studentCardIcon: {
    width: 48,
    height: 48,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(186,230,253,0.2)',
    backgroundColor: 'rgba(125,211,252,0.09)',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#60a5fa',
    shadowOpacity: 0.22,
    shadowRadius: 22,
  },
  studentCardContent: {
    flex: 1,
  },
  studentCardTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.bold,
  },
  studentCardBody: {
    marginTop: 6,
    color: 'rgba(255,255,255,0.62)',
    fontSize: Typography.sm,
    lineHeight: 20,
  },
  studentCardAction: {
    marginTop: 14,
    minHeight: 44,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.065)',
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  studentCardActionText: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
    flex: 1,
  },
  studiZoneCard: {
    width: '100%',
    maxWidth: 520,
    minHeight: 58,
    marginTop: 14,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(125,211,252,0.16)',
    backgroundColor: 'rgba(7,16,29,0.68)',
    paddingHorizontal: 14,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    overflow: 'hidden',
    shadowColor: '#60a5fa',
    shadowOpacity: 0.18,
    shadowRadius: 22,
  },
  studiZoneGlowA: {
    position: 'absolute',
    left: -42,
    top: -54,
    width: 118,
    height: 118,
    borderRadius: 59,
    backgroundColor: 'rgba(56,189,248,0.16)',
  },
  studiZoneGlowB: {
    position: 'absolute',
    right: -54,
    bottom: -66,
    width: 136,
    height: 136,
    borderRadius: 68,
    backgroundColor: 'rgba(139,92,246,0.16)',
  },
  studiZoneIcon: {
    width: 38,
    height: 38,
    borderRadius: 15,
    borderWidth: 1,
    borderColor: 'rgba(186,230,253,0.2)',
    backgroundColor: 'rgba(125,211,252,0.09)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  studiZoneCopy: {
    flex: 1,
    minWidth: 0,
  },
  studiZoneTitle: {
    flex: 1,
    minWidth: 0,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
  },
  studiZoneSubtitle: {
    marginTop: 3,
    color: 'rgba(255,255,255,0.62)',
    fontSize: Typography.xs,
    lineHeight: 16,
  },
  studiZoneButton: {
    minHeight: 38,
    borderRadius: 18,
    backgroundColor: '#6366f1',
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    shadowColor: '#6366f1',
    shadowOpacity: 0.28,
    shadowRadius: 22,
  },
  studiZoneButtonText: {
    color: Colors.textPrimary,
    fontSize: 11,
    fontWeight: Typography.extrabold,
  },
  editButton: {
    minWidth: 240,
    minHeight: 56,
    marginTop: 20,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(96,165,250,0.45)',
    backgroundColor: 'rgba(255,255,255,0.1)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    shadowColor: '#3b82f6',
    shadowOpacity: 0.18,
    shadowRadius: 22,
  },
  editButtonText: {
    color: '#fff',
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
  shareRow: {
    marginTop: 16,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 12,
  },
  pillButton: {
    minHeight: 44,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.1)',
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pillButtonText: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  glassCard: {
    width: '100%',
    alignSelf: 'stretch',
    borderRadius: 30,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(16,23,34,0.62)',
    overflow: 'hidden',
  },
  infoGrid: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
    alignSelf: 'stretch',
  },
  infoTileWrap: {
    flex: 1,
    minWidth: 0,
  },
  infoTile: {
    flex: 1,
    minHeight: 118,
    borderRadius: 24,
    overflow: 'hidden',
    padding: 14,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  tileEyebrow: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 9,
    fontWeight: Typography.bold,
    textTransform: 'uppercase',
    letterSpacing: 1.6,
    textAlign: 'center',
  },
  tileValue: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
    textAlign: 'center',
  },
  tileDetail: {
    color: 'rgba(255,255,255,0.50)',
    fontSize: 10,
    lineHeight: 14,
    textAlign: 'center',
  },
  statsCard: {
    borderRadius: 32,
    padding: 20,
    width: '100%',
    alignSelf: 'stretch',
  },
  statsCardIOS: {
    borderRadius: 32,
    minHeight: 172,
    paddingVertical: 22,
  },
  statsRow: {
    flexDirection: 'row',
    width: '100%',
    minHeight: 128,
    alignItems: 'stretch',
  },
  statWrap: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
    position: 'relative',
  },
  statDivider: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  statBubble: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statValue: {
    marginTop: 8,
    fontSize: Typography.xl,
    lineHeight: 26,
    fontWeight: Typography.bold,
  },
  statLabel: {
    marginTop: 8,
    color: 'rgba(255,255,255,0.7)',
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    textAlign: 'center',
  },
  statDetail: {
    marginTop: 4,
    color: 'rgba(255,255,255,0.45)',
    fontSize: 10,
    textAlign: 'center',
  },
  interestsCard: {
    paddingVertical: 12,
    paddingHorizontal: 14,
    width: '100%',
    alignSelf: 'stretch',
  },
  interestsCardIOS: {
    borderRadius: 30,
    minHeight: 166,
    paddingBottom: 24,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  cardTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.bold,
  },
  linkText: {
    color: '#7dd3fc',
    fontSize: Typography.sm,
    fontWeight: Typography.medium,
  },
  interestScroller: {
    marginTop: 10,
    marginHorizontal: -14, // chips scroll edge to edge inside the card
  },
  interestRow: {
    gap: 8,
    paddingHorizontal: 14,
  },
  interestChip: {
    height: 30,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.1)',
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  interestText: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 12,
    fontWeight: Typography.medium,
  },
  addInterestChip: {
    height: 30,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: 'rgba(147,197,253,0.4)',
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  addInterestText: {
    color: '#93c5fd',
    fontSize: 12,
    fontWeight: Typography.semibold,
  },
  actionGrid: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
    alignSelf: 'stretch',
  },
  actionButton: {
    flex: 1,
    minWidth: 0,
    minHeight: 50,
    borderRadius: 18,
    backgroundColor: Colors.sunset,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    ...Shadow.card,
  },
  actionButtonCyan: {
    backgroundColor: Colors.turquoise,
  },
  actionButtonText: {
    color: '#fff',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
    flexShrink: 1,
  },
  adminCard: {
    padding: 16,
    width: '100%',
    alignSelf: 'stretch',
  },
  adminCardIOS: {
    borderRadius: 30,
    paddingBottom: 22,
  },
  adminEyebrow: {
    color: 'rgba(224,242,254,0.48)',
    fontSize: 10,
    fontWeight: Typography.extrabold,
    textTransform: 'uppercase',
    letterSpacing: 2,
  },
  adminHeaderText: {
    flex: 1,
    minWidth: 0,
  },
  adminTitle: {
    marginTop: 4,
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.extrabold,
    flexShrink: 1,
  },
  adminBadge: {
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(186,230,253,0.14)',
    backgroundColor: 'rgba(125,211,252,0.08)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    flexShrink: 0,
  },
  adminBadgeText: {
    color: '#e0f2fe',
    fontSize: 10,
    fontWeight: Typography.extrabold,
  },
  adminText: {
    marginTop: 8,
    color: 'rgba(255,255,255,0.48)',
    fontSize: Typography.xs,
    lineHeight: 17,
  },
  adminManagementButton: {
    marginTop: 14,
    minHeight: 66,
    width: '100%',
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  adminManagementIcon: {
    width: 40,
    height: 40,
    borderRadius: 15,
    backgroundColor: 'rgba(125,211,252,0.1)',
    borderWidth: 1,
    borderColor: 'rgba(186,230,253,0.16)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  adminManagementCopy: { flex: 1, minWidth: 0 },
  // Compact side-by-side tiles: Following count + StudiZone.
  quickTiles: { flexDirection: 'row', gap: 10, width: '100%', maxWidth: 520, marginBottom: 10 },
  quickTile: {
    flex: 1,
    minHeight: 62,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    paddingHorizontal: 14,
    paddingVertical: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  quickTileLabel: { color: 'rgba(255,255,255,0.55)', fontSize: 12, fontWeight: Typography.bold },
  quickTileValue: { color: Colors.textPrimary, fontSize: 20, fontWeight: Typography.extrabold, marginTop: 2 },
  followingTile: {
    borderColor: 'rgba(45,212,191,0.2)',
    backgroundColor: 'rgba(6,24,28,0.7)',
    overflow: 'hidden',
  },
  followingGlowA: {
    position: 'absolute',
    right: -42,
    top: -54,
    width: 118,
    height: 118,
    borderRadius: 59,
    backgroundColor: 'rgba(38,208,206,0.18)',
  },
  followingGlowB: {
    position: 'absolute',
    left: -54,
    bottom: -66,
    width: 136,
    height: 136,
    borderRadius: 68,
    backgroundColor: 'rgba(255,122,0,0.14)',
  },
  followingTileLabel: { color: 'rgba(204,251,241,0.72)' },
  followingTileValue: { color: '#f0fdfa' },
  studiZoneTile: {
    borderColor: 'rgba(125,211,252,0.16)',
    backgroundColor: 'rgba(7,16,29,0.68)',
    overflow: 'hidden',
  },
  studiZoneTileLabel: { color: 'rgba(224,242,254,0.72)' },
  studiZoneTileStatus: { color: '#e0f2fe' },
  quickTileStatus: { color: Colors.textPrimary, fontSize: 15, fontWeight: Typography.extrabold, marginTop: 4, textAlign: 'center' },
  adminManagementTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
  },
  adminManagementSubtitle: {
    marginTop: 2,
    color: 'rgba(255,255,255,0.48)',
    fontSize: Typography.xs,
    flexShrink: 1,
  },
  viewerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(2,6,23,0.84)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 22,
  },
  viewerCard: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 30,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: 'rgba(7,11,20,0.96)',
    padding: 14,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.44,
    shadowRadius: 34,
  },
  viewerCoverImage: {
    width: '100%',
    height: 210,
    borderRadius: 24,
    backgroundColor: Colors.bgCardAlt,
  },
  viewerProfileImage: {
    width: 236,
    height: 236,
    borderRadius: 118,
    backgroundColor: Colors.bgCardAlt,
  },
  viewerStatus: {
    marginTop: 12,
    color: Colors.turquoise,
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  viewerActions: {
    width: '100%',
    marginTop: 16,
    flexDirection: 'row',
    gap: 8,
  },
  viewerAction: {
    flex: 1,
    minHeight: 46,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.07)',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 7,
  },
  viewerActionText: {
    color: Colors.textPrimary,
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
  },
  viewerRemoveText: {
    color: Colors.sunset,
  },
  qrOverlay: {
    flex: 1,
    backgroundColor: 'rgba(2,6,23,0.76)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 22,
  },
  qrCard: {
    width: '100%',
    maxWidth: 360,
    borderRadius: 30,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: 'rgba(7,11,20,0.97)',
    padding: 20,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.44,
    shadowRadius: 34,
  },
  qrKicker: {
    color: 'rgba(255,255,255,0.48)',
    fontSize: 11,
    fontWeight: Typography.extrabold,
    letterSpacing: 2,
    textTransform: 'uppercase',
  },
  qrTitle: {
    marginTop: 8,
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.extrabold,
    maxWidth: '100%',
  },
  qrFrame: {
    marginTop: 18,
    padding: 14,
    borderRadius: 26,
    backgroundColor: '#fff',
    shadowColor: '#fff',
    shadowOpacity: 0.08,
    shadowRadius: 20,
  },
  qrLink: {
    marginTop: 14,
    color: 'rgba(255,255,255,0.56)',
    fontSize: Typography.xs,
    textAlign: 'center',
  },
  qrActions: {
    width: '100%',
    flexDirection: 'row',
    gap: 10,
    marginTop: 18,
  },
  qrActionButton: {
    flex: 1,
    minHeight: 46,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  qrActionText: {
    color: Colors.textPrimary,
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
  },
  sheetOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(2,6,23,0.58)',
  },
  bottomSheet: {
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(7,11,20,0.98)',
    paddingHorizontal: 18,
    paddingTop: 18,
  },
  sheetTitle: {
    marginBottom: 12,
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.extrabold,
  },
  drawerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(2,5,13,0.66)',
  },
  settingsDrawer: {
    width: '100%',
    height: '100%',
    backgroundColor: '#070911',
    overflow: 'hidden',
  },
  secondarySettingsDrawer: {
    paddingHorizontal: 18,
  },
  settingsDrawerInteractionLayer: {
    flex: 1,
    paddingHorizontal: 18,
  },
  drawerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
  },
  drawerHeaderText: {
    flex: 1,
    minWidth: 0,
  },
  drawerTitle: {
    color: Colors.textPrimary,
    fontSize: 24,
    lineHeight: 29,
    fontWeight: Typography.extrabold,
    letterSpacing: -0.65,
  },
  drawerSubtitle: {
    marginTop: 4,
    color: 'rgba(226,232,240,0.44)',
    fontSize: Typography.xs,
  },
  drawerClose: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    shadowColor: '#A18CD1',
    shadowOpacity: 0.18,
    shadowRadius: 15,
    shadowOffset: { width: 0, height: 7 },
  },
  settingsSearchButtonActive: {
    borderColor: 'rgba(38,208,206,0.36)',
    shadowColor: Colors.turquoise,
    shadowOpacity: 0.28,
  },
  settingsSearchWrap: {
    minHeight: 48,
    marginTop: 4,
    marginBottom: 8,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 19,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.13)',
    backgroundColor: 'rgba(255,255,255,0.065)',
    shadowColor: '#A18CD1',
    shadowOpacity: 0.12,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
  },
  settingsSearchInput: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 0,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  settingsSearchClear: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.09)',
  },
  settingsSearchEmpty: {
    minHeight: 180,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 24,
  },
  settingsSearchEmptyTitle: {
    color: 'rgba(248,250,252,0.84)',
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
  settingsSearchEmptyBody: {
    color: 'rgba(226,232,240,0.4)',
    fontSize: Typography.xs,
  },
  drawerContent: {
    paddingTop: 8,
    paddingBottom: 38,
    gap: 28,
  },
  premiumSettingsHeader: {
    minHeight: 124,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 16,
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.075)',
  },
  premiumAvatarRing: {
    width: 74,
    height: 74,
    borderRadius: 37,
    padding: 2,
    overflow: 'hidden',
    shadowColor: '#A18CD1',
    shadowOpacity: 0.38,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
  },
  premiumAvatarMetal: {
    flex: 1,
    padding: 3,
    borderRadius: 35,
    backgroundColor: '#151925',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.28)',
  },
  premiumSettingsAvatar: {
    width: '100%',
    height: '100%',
    borderRadius: 31,
  },
  premiumSettingsGroup: {
    gap: 10,
  },
  premiumSettingsGroupTitle: {
    paddingHorizontal: 5,
    color: 'rgba(226,232,240,0.36)',
    fontSize: 10,
    fontWeight: Typography.extrabold,
    letterSpacing: 1.9,
    textTransform: 'uppercase',
  },
  premiumSettingsGroupRows: {
    gap: 11,
  },
  premiumSettingsRowEntrance: {
    shadowColor: '#000',
    shadowOpacity: 0.34,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
  },
  premiumSettingsRow: {
    minHeight: 66,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.115)',
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    overflow: 'hidden',
  },
  premiumRowHighlight: {
    position: 'absolute',
    left: 18,
    right: 38,
    top: 0,
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.16)',
  },
  premiumSettingsIcon: {
    width: 42,
    height: 42,
    borderRadius: 21,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(31,36,51,0.82)',
    overflow: 'hidden',
    shadowColor: '#A18CD1',
    shadowOpacity: 0.18,
    shadowRadius: 9,
    shadowOffset: { width: 0, height: 5 },
  },
  premiumSettingsIconDestructive: {
    borderColor: 'rgba(242,125,38,0.26)',
    shadowColor: '#F27D26',
  },
  premiumIconReflection: {
    position: 'absolute',
    top: 3,
    left: 7,
    width: 20,
    height: 8,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.11)',
    transform: [{ rotate: '-18deg' }],
  },
  premiumSettingsLabel: {
    flex: 1,
    minWidth: 0,
    color: 'rgba(248,250,252,0.92)',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
    letterSpacing: -0.1,
  },
  premiumSettingsLabelDestructive: {
    color: '#fb923c',
  },
  settingsPurpleGlow: {
    position: 'absolute',
    width: 310,
    height: 310,
    borderRadius: 155,
    right: -150,
    top: 40,
    backgroundColor: 'rgba(161,140,209,0.105)',
    filter: [{ blur: 44 }],
  },
  settingsOrangeGlow: {
    position: 'absolute',
    width: 260,
    height: 260,
    borderRadius: 130,
    left: -160,
    bottom: 100,
    backgroundColor: 'rgba(242,125,38,0.072)',
    filter: [{ blur: 46 }],
  },
  settingsVignette: {
    ...StyleSheet.absoluteFill,
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(255,255,255,0.06)',
    backgroundColor: 'rgba(0,0,0,0.055)',
  },
  settingsParticles: {
    ...StyleSheet.absoluteFill,
    opacity: 0.24,
  },
  settingsParticle: {
    position: 'absolute',
    width: 2,
    height: 2,
    borderRadius: 1,
    backgroundColor: 'rgba(255,255,255,0.42)',
  },
  settingsParticle0: { top: '8%', left: '18%' },
  settingsParticle1: { top: '17%', right: '12%' },
  settingsParticle2: { top: '31%', left: '9%' },
  settingsParticle3: { top: '48%', right: '22%' },
  settingsParticle4: { top: '65%', left: '24%' },
  settingsParticle5: { top: '78%', right: '13%' },
  settingsParticle6: { top: '91%', left: '42%' },
  drawerRow: {
    minHeight: 54,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
  },
  drawerRowIcon: {
    width: 34,
    height: 34,
    borderRadius: 13,
    backgroundColor: 'rgba(125,211,252,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  drawerRowIconDestructive: {
    backgroundColor: 'rgba(242,125,38,0.12)',
  },
  drawerRowLabel: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  drawerRowDestructive: {
    color: Colors.sunset,
  },
  sidebarModalScroll: {
    flex: 1,
  },
  sidebarModalContent: {
    paddingVertical: 14,
    paddingBottom: 52,
    gap: 12,
  },
  sidebarPanel: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    padding: 0,
    overflow: 'hidden',
  },
  sidebarPanelContent: {
    padding: 14,
    gap: 12,
  },
  sidebarPanelFrame: {
    width: '100%',
    minWidth: 0,
    padding: 0,
  },
  sidebarPanelContentNoPadding: {
    padding: 0,
    gap: 0,
  },
  studentVerificationPanel: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    padding: 14,
    gap: 12,
    overflow: 'visible',
  },
  vibeVerificationPanel: {
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(10,12,24,0.78)',
    padding: 10,
    gap: 10,
    overflow: 'visible',
    shadowColor: '#9B51E0',
    shadowOpacity: 0.16,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 10 },
  },
  vibeVerificationHero: {
    minHeight: 96,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    padding: 12,
    overflow: 'hidden',
    justifyContent: 'flex-end',
    gap: 5,
  },
  vibeVerificationHeroIcon: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,122,24,0.34)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  vibeVerificationTitle: {
    color: Colors.textPrimary,
    fontSize: 21,
    lineHeight: 25,
    fontWeight: Typography.extrabold,
  },
  vibeVerificationSubtitle: {
    color: 'rgba(255,255,255,0.68)',
    fontSize: 13,
    lineHeight: 18,
  },
  poseExampleFrame: {
    alignSelf: 'center',
    width: '100%',
    height: 224,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    padding: 6,
    overflow: 'hidden',
  },
  poseExampleGlow: {
    position: 'absolute',
    left: 26,
    right: 26,
    bottom: -16,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(255,122,24,0.22)',
  },
  poseExampleImage: {
    width: '100%',
    height: '100%',
    borderRadius: 15,
    backgroundColor: Colors.bgCardAlt,
  },
  poseExampleLoading: {
    flex: 1,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  poseExampleLabel: {
    marginTop: -2,
    textAlign: 'center',
    color: 'rgba(224,242,254,0.62)',
    fontSize: 10,
    fontWeight: Typography.extrabold,
    textTransform: 'uppercase',
    letterSpacing: 1.4,
  },
  vibeInstructionTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    lineHeight: 19,
    fontWeight: Typography.extrabold,
  },
  vibeChecklistRow: {
    minHeight: 34,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.045)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingHorizontal: 10,
  },
  vibeChecklistIcon: {
    width: 23,
    height: 23,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,122,24,0.12)',
  },
  vibeChecklistText: {
    color: 'rgba(255,255,255,0.78)',
    fontSize: 13,
    fontWeight: Typography.semibold,
  },
  selfieReviewCard: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    padding: 8,
    overflow: 'hidden',
  },
  selfieReviewImage: {
    width: '100%',
    height: 320,
    borderRadius: 18,
    backgroundColor: Colors.bgCardAlt,
  },
  vibeVerificationButtonRow: {
    flexDirection: 'row',
    gap: 10,
  },
  vibeSecondaryButton: {
    flex: 0.42,
    minHeight: 54,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  vibeSecondaryButtonText: {
    color: 'rgba(255,255,255,0.78)',
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
  },
  vibeSubmitButton: {
    flex: 1,
  },
  vibePendingHero: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    padding: 16,
    gap: 8,
  },
  vibePendingIcon: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(125,211,252,0.12)',
  },
  vibeVerifiedHero: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.24)',
    backgroundColor: 'rgba(38,208,206,0.08)',
    padding: 16,
    gap: 8,
  },
  vibeVerifiedIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.turquoise,
    shadowColor: Colors.turquoise,
    shadowOpacity: 0.36,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
  },
  studentVerificationActionStack: {
    gap: 12,
    overflow: 'visible',
  },
  studentVerificationInput: {
    minHeight: 52,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.07)',
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    paddingHorizontal: 13,
    paddingVertical: 0,
  },
  studentVerificationButton: {
    minHeight: 54,
    borderRadius: 18,
    backgroundColor: Colors.sunset,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  panelKicker: {
    color: 'rgba(224,242,254,0.48)',
    fontSize: 10,
    fontWeight: Typography.extrabold,
    textTransform: 'uppercase',
    letterSpacing: 1.7,
  },
  panelValue: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.bold,
  },
  panelBody: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: Typography.sm,
    lineHeight: 20,
  },
  panelButton: {
    minHeight: 46,
    borderRadius: 16,
    backgroundColor: Colors.sunset,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  panelButtonText: {
    color: '#fff',
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
  },
  panelMessage: {
    color: Colors.turquoise,
    fontSize: Typography.xs,
    lineHeight: 18,
    fontWeight: Typography.semibold,
  },
  panelMessageError: {
    color: Colors.sunset,
    fontSize: Typography.xs,
    lineHeight: 18,
    fontWeight: Typography.semibold,
  },
  panelLoading: {
    minHeight: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  faceScanStepRow: {
    minHeight: 62,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.045)',
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  faceScanStepRowActive: {
    borderColor: 'rgba(38,208,206,0.42)',
    backgroundColor: 'rgba(38,208,206,0.08)',
  },
  faceScanStepDot: {
    width: 30,
    height: 30,
    borderRadius: 15,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  faceScanStepDotDone: {
    backgroundColor: Colors.turquoise,
    borderColor: Colors.turquoise,
  },
  faceScanStepDotText: {
    color: Colors.textPrimary,
    fontSize: 11,
    fontWeight: Typography.extrabold,
  },
  faceScanStepCopy: {
    flex: 1,
    minWidth: 0,
  },
  faceScanStepTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
  },
  faceScanStepInstruction: {
    marginTop: 2,
    color: 'rgba(255,255,255,0.52)',
    fontSize: Typography.xs,
  },
  faceReviewGrid: {
    marginTop: 12,
    flexDirection: 'row',
    gap: 8,
  },
  faceReviewImageWrap: {
    flex: 1,
    minWidth: 0,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.045)',
    overflow: 'hidden',
  },
  faceReviewImage: {
    width: '100%',
    height: 112,
    backgroundColor: Colors.bgCardAlt,
  },
  faceReviewLabel: {
    paddingVertical: 7,
    textAlign: 'center',
    color: 'rgba(224,242,254,0.78)',
    fontSize: 10,
    fontWeight: Typography.extrabold,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
  },
  faceReviewSingleWrap: {
    marginTop: 12,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.045)',
    overflow: 'hidden',
  },
  faceReviewSingleImage: {
    width: '100%',
    height: 260,
    backgroundColor: Colors.bgCardAlt,
  },
  adminVerificationBadgeRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 10,
  },
  adminVerificationBadge: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  adminVerificationBadgeText: {
    fontSize: 11,
    fontWeight: Typography.bold,
  },
  blockedRow: {
    minHeight: 62,
    paddingHorizontal: 14,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  blockedRowDivider: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.07)',
  },
  blockedEmptyText: {
    paddingHorizontal: 14,
    paddingVertical: 14,
  },
  blockedMessagePadding: {
    paddingHorizontal: 14,
    paddingBottom: 10,
  },
  blockedAvatar: {
    width: 42,
    height: 42,
    borderRadius: 15,
    backgroundColor: Colors.bgCardAlt,
  },
  blockedMeta: {
    flex: 1,
    minWidth: 0,
  },
  blockedName: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  blockedUsername: {
    marginTop: 2,
    color: 'rgba(255,255,255,0.4)',
    fontSize: 11,
  },
  unblockButton: {
    minHeight: 36,
    borderRadius: 13,
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.32)',
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  unblockButtonText: {
    color: Colors.sunset,
    fontSize: 11,
    fontWeight: Typography.extrabold,
  },
  sidebarSearchBox: {
    minHeight: 46,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  sidebarSearchInput: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    paddingVertical: 0,
  },
  faqItem: {
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    padding: 13,
  },
  faqQuestionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  faqQuestion: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
    lineHeight: 19,
  },
  faqAnswer: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.07)',
    color: 'rgba(255,255,255,0.62)',
    fontSize: Typography.sm,
    lineHeight: 20,
  },
  optionRow: {
    minHeight: 46,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.045)',
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  optionRowActive: {
    borderColor: 'rgba(38,208,206,0.36)',
    backgroundColor: 'rgba(38,208,206,0.14)',
  },
  optionText: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
    textTransform: 'capitalize',
  },
  optionTextActive: {
    color: Colors.turquoise,
  },
  feedbackTextArea: {
    minHeight: 118,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    paddingHorizontal: 13,
    paddingTop: 12,
    textAlignVertical: 'top',
  },
  feedbackInput: {
    minHeight: 46,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    paddingHorizontal: 13,
    paddingVertical: 0,
  },
  toggleRow: {
    minHeight: 50,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.045)',
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  toggleLabel: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  toggleTrack: {
    width: 48,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.16)',
    padding: 3,
  },
  toggleTrackActive: {
    backgroundColor: 'rgba(38,208,206,0.42)',
  },
  toggleThumb: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: 'rgba(255,255,255,0.84)',
  },
  toggleThumbActive: {
    marginLeft: 20,
    backgroundColor: Colors.turquoise,
  },
  deleteWarningIcon: {
    width: 56,
    height: 56,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.28)',
    backgroundColor: 'rgba(242,125,38,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.xxl,
    lineHeight: 30,
    fontWeight: Typography.extrabold,
  },
  deleteBody: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: Typography.sm,
    lineHeight: 21,
  },
  deleteInfoBox: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: 13,
    gap: 8,
  },
  deleteInfoText: {
    color: 'rgba(255,255,255,0.68)',
    fontSize: Typography.xs,
    lineHeight: 18,
  },
  deleteReauthBox: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: 13,
    gap: 10,
  },
  deleteButtonRow: {
    flexDirection: 'row',
    gap: 10,
  },
  deleteCancelButton: {
    flex: 1,
    minHeight: 46,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteCancelText: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  deleteConfirmButton: {
    flex: 1,
    minHeight: 46,
    borderRadius: 16,
    backgroundColor: Colors.sunset,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteConfirmText: {
    color: '#fff',
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
  },
  interestModal: {
    flex: 1,
  },
  interestModalHeader: {
    paddingHorizontal: 20,
    paddingBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
  },
  interestModalKicker: {
    color: 'rgba(125,211,252,0.62)',
    fontSize: 10,
    fontWeight: Typography.extrabold,
    letterSpacing: 2,
    textTransform: 'uppercase',
  },
  interestModalTitle: {
    marginTop: 4,
    color: Colors.textPrimary,
    fontSize: Typography.xxl,
    fontWeight: Typography.extrabold,
  },
  interestModalScroll: {
    flex: 1,
    paddingHorizontal: 18,
  },
  selectedInterestPanel: {
    borderRadius: 26,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.065)',
    padding: 15,
  },
  selectedInterestHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  selectedInterestLabel: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.extrabold,
  },
  selectedInterestCount: {
    marginTop: 3,
    color: 'rgba(255,255,255,0.45)',
    fontSize: Typography.xs,
  },
  clearInterestsButton: {
    minHeight: 34,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    paddingHorizontal: 13,
    justifyContent: 'center',
  },
  clearInterestsText: {
    color: '#93c5fd',
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
  },
  interestSearchBox: {
    minHeight: 46,
    marginTop: 14,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  interestSearchInput: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    paddingVertical: 0,
  },
  selectedInterestChips: {
    marginTop: 13,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  selectedInterestChip: {
    minHeight: 34,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.24)',
    backgroundColor: 'rgba(38,208,206,0.1)',
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  selectedInterestChipText: {
    color: Colors.textPrimary,
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
  },
  addCustomInterestButton: {
    marginTop: 13,
    minHeight: 42,
    borderRadius: 16,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: 'rgba(242,125,38,0.38)',
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  addCustomInterestText: {
    flex: 1,
    color: Colors.sunset,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  interestCategoryCard: {
    marginTop: 14,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.045)',
    padding: 14,
  },
  interestCategoryTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
  },
  interestCategoryGrid: {
    marginTop: 12,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  interestOptionChip: {
    minHeight: 36,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    paddingHorizontal: 13,
    justifyContent: 'center',
  },
  interestOptionChipActive: {
    borderColor: 'rgba(38,208,206,0.36)',
    backgroundColor: 'rgba(38,208,206,0.16)',
  },
  interestOptionText: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  interestOptionTextActive: {
    color: Colors.turquoise,
  },
  interestModalDock: {
    position: 'absolute',
    left: 18,
    right: 18,
    minHeight: 64,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(7,11,20,0.96)',
    padding: 8,
    flexDirection: 'row',
    gap: 9,
  },
  interestCancelButton: {
    flex: 1,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  interestCancelText: {
    color: 'rgba(255,255,255,0.66)',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  interestSaveButton: {
    flex: 1.5,
    borderRadius: 18,
    backgroundColor: Colors.sunset,
    alignItems: 'center',
    justifyContent: 'center',
  },
  interestSaveText: {
    color: '#fff',
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
  },
  adminModalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.58)',
  },
  adminModalSheet: {
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
    maxHeight: '88%',
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(7,11,20,0.98)',
    paddingHorizontal: 18,
    paddingTop: 18,
  },
  adminModalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 14,
  },
  adminModalTitle: {
    marginTop: 4,
    color: Colors.textPrimary,
    fontSize: Typography.xl,
    fontWeight: Typography.extrabold,
  },
  adminModalSubtitle: {
    marginTop: 3,
    color: 'rgba(255,255,255,0.45)',
    fontSize: Typography.xs,
  },
  adminModalClose: {
    width: 36,
    height: 36,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.07)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  adminSearchBox: {
    minHeight: 46,
    marginTop: 16,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  adminSearchInput: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    paddingVertical: 0,
  },
  adminRoleMessage: {
    marginTop: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.2)',
    backgroundColor: 'rgba(38,208,206,0.1)',
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  adminRoleMessageText: {
    color: Colors.turquoise,
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  adminLoadingWrap: {
    minHeight: 180,
    alignItems: 'center',
    justifyContent: 'center',
  },
  adminUserList: {
    marginTop: 12,
  },
  adminUserRow: {
    marginBottom: 10,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.07)',
    backgroundColor: 'rgba(255,255,255,0.045)',
    padding: 12,
  },
  adminUserTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  adminUserAvatarWrap: { width: 42, height: 42 },
  adminUserAvatar: {
    width: 42,
    height: 42,
    borderRadius: 15,
    backgroundColor: Colors.bgCardAlt,
  },
  adminUserMeta: { flex: 1, minWidth: 0 },
  adminUserName: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  adminUserEmail: {
    marginTop: 2,
    color: 'rgba(255,255,255,0.42)',
    fontSize: 11,
  },
  adminUserRoleLine: {
    marginTop: 3,
    color: 'rgba(255,255,255,0.32)',
    fontSize: 10,
    fontWeight: Typography.bold,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
  },
  protectedAccountText: {
    marginTop: 10,
    color: 'rgba(255,255,255,0.34)',
    fontSize: Typography.xs,
    fontStyle: 'italic',
  },
  roleChipRow: {
    marginTop: 11,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  roleChip: {
    borderRadius: 13,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    paddingHorizontal: 10,
    paddingVertical: 7,
  },
  roleChipActive: {
    borderColor: 'rgba(38,208,206,0.36)',
    backgroundColor: 'rgba(38,208,206,0.16)',
  },
  roleChipText: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: 11,
    fontWeight: Typography.bold,
  },
  roleChipTextActive: {
    color: Colors.turquoise,
  },
  verifDetailGrid: {
    marginTop: 10,
    gap: 6,
  },
  verifDetailCell: {
    backgroundColor: 'rgba(0,0,0,0.18)',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  verifDetailLabel: {
    color: 'rgba(255,255,255,0.32)',
    fontSize: 10,
    fontWeight: Typography.bold,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  verifDetailValue: {
    color: 'rgba(255,255,255,0.78)',
    fontSize: 12,
    fontWeight: Typography.semibold,
    marginTop: 2,
  },
  verifSaveDomainBtn: {
    marginTop: 6,
    alignSelf: 'stretch',
    borderColor: 'rgba(38,208,206,0.22)',
    backgroundColor: 'rgba(38,208,206,0.07)',
    alignItems: 'center',
  },
  adminEmptyState: {
    paddingVertical: 34,
    alignItems: 'center',
  },
  adminEmptyText: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: Typography.sm,
  },
  contentBoxRow: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
    alignSelf: 'stretch',
  },
  contentBox: {
    flex: 1,
    minWidth: 0,
    minHeight: 130,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    overflow: 'hidden',
    padding: 16,
    gap: 4,
    position: 'relative',
    ...Shadow.card,
  },
  contentBoxFull: {
    flex: 1,
  },
  planRequestsCardWeb: {
    // `flex: 1` is useful for cards inside contentBoxRow, but this card is a
    // standalone child. RN Web otherwise resolves it to the 130px minimum
    // and clips the subtitle behind overflow: hidden.
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    width: '100%',
    minHeight: 158,
    paddingBottom: 18,
  },
  contentBoxIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 14,
    backgroundColor: 'rgba(242,125,38,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 6,
  },
  contentBoxIconCyan: {
    backgroundColor: 'rgba(38,208,206,0.12)',
  },
  contentBoxCount: {
    color: Colors.sunset,
    fontSize: Typography.xxl,
    fontWeight: Typography.bold,
    lineHeight: 32,
  },
  contentBoxCountCyan: {
    color: Colors.turquoise,
  },
  contentBoxTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  contentBoxSub: {
    color: 'rgba(255,255,255,0.42)',
    fontSize: Typography.xs,
    lineHeight: 16,
  },
  contentBoxArrow: {
    position: 'absolute',
    top: 14,
    right: 14,
  },
  emptyPosts: {
    padding: 24,
  },
  emptyPostsText: {
    color: 'rgba(255,255,255,0.5)',
    textAlign: 'center',
    fontSize: Typography.sm,
    lineHeight: 20,
  },
  completionCard: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.22)',
    overflow: 'hidden',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    gap: 12,
    backgroundColor: 'rgba(38,208,206,0.055)',
  },
  completionTileOuter: { flex: 1, width: undefined },
  completionTile: {
    width: '100%',
    minHeight: 62,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 18,
    justifyContent: 'flex-start',
  },
  completionTileLeft: { gap: 9 },
  completionTileIcon: { width: 32, height: 32, borderRadius: 11 },
  completionTileProgress: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 5 },
  completionTileBar: { flex: 1, width: undefined },
  completionTilePct: { fontSize: 11, fontWeight: Typography.extrabold },
  completionPromptOuter: {
    width: '100%',
    maxWidth: 520,
  },
  completionCardCompact: {
    minHeight: 58,
    borderRadius: 19,
    paddingHorizontal: 13,
    paddingVertical: 10,
  },
  completionIconWrapCompact: {
    width: 34,
    height: 34,
    borderRadius: 12,
  },
  completionCardBorder: {
    ...StyleSheet.absoluteFill,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.18)',
  },
  completionLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  completionIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.3)',
    backgroundColor: 'rgba(38,208,206,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  completionText: {
    flex: 1,
    gap: 3,
  },
  completionTitle: {
    color: Colors.textPrimary,
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.1,
  },
  completionSub: {
    color: 'rgba(255,255,255,0.52)',
    fontSize: 11,
    fontWeight: '500',
  },
  completionRight: {
    alignItems: 'flex-end',
    gap: 5,
  },
  completionPct: {
    color: Colors.turquoise,
    fontSize: 18,
    fontWeight: '800',
    letterSpacing: -0.5,
  },
  completionBarTrack: {
    width: 64,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.1)',
    overflow: 'hidden',
  },
  completionBarFill: {
    height: 4,
    borderRadius: 2,
    backgroundColor: Colors.turquoise,
  },
  signOutButton: {
    minHeight: 48,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.26)',
    backgroundColor: 'rgba(242,125,38,0.08)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  signOutText: {
    color: Colors.sunset,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  primaryButton: {
    width: '100%',
    minHeight: 48,
    marginTop: 24,
    borderRadius: 18,
    backgroundColor: Colors.sunset,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  glassButton: {
    width: '100%',
    minHeight: 48,
    marginTop: 12,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  glassButtonText: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  cityPickerModal: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 16,
    backgroundColor: '#0b111d',
    padding: 14,
  },
  cityPickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  cityPickerTitle: {
    color: Colors.textPrimary,
    fontSize: 16,
    fontWeight: Typography.extrabold,
  },
  cityPickerSubtitle: {
    color: 'rgba(255,255,255,0.44)',
    fontSize: 12,
    marginBottom: 12,
  },
  cityOption: {
    minHeight: 48,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    paddingHorizontal: 13,
    paddingVertical: 10,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cityOptionActive: {
    borderColor: 'rgba(242,125,38,0.62)',
    backgroundColor: 'rgba(242,125,38,0.14)',
  },
  cityOptionText: {
    color: 'rgba(255,255,255,0.78)',
    fontSize: 15,
  },
  cityOptionTextActive: {
    color: Colors.sunset,
    fontWeight: Typography.extrabold,
  },
});

const createProfileAppearanceStyles = (theme: TonyteTheme) => StyleSheet.create({
  root: daylightStyle(theme, {
    backgroundColor: theme.colors.background,
  }),
  coverWrap: daylightStyle(theme, {
    backgroundColor: theme.colors.background,
  }),
  textPrimary: daylightStyle(theme, {
    color: theme.colors.textPrimary,
  }),
  textSecondary: daylightStyle(theme, {
    color: theme.colors.textSecondary,
  }),
  textMuted: daylightStyle(theme, {
    color: theme.colors.textMuted,
  }),
  bioText: daylightStyle(theme, {
    color: theme.colors.bioText,
  }),
  bioPlaceholder: daylightStyle(theme, {
    color: theme.colors.bioPlaceholder,
  }),
  actionLabel: daylightStyle(theme, {
    color: theme.colors.actionLabel,
  }),
  emptyText: daylightStyle(theme, {
    color: theme.colors.textSecondary,
  }),
  sectionHeading: daylightStyle(theme, {
    color: theme.colors.sectionHeading,
  }),
  supportingText: daylightStyle(theme, {
    color: theme.colors.textSecondary,
  }),
  accentText: daylightStyle(theme, {
    color: theme.colors.accentSecondary,
  }),
  interestText: daylightStyle(theme, {
    color: theme.colors.chipText,
  }),
  interestAccentText: daylightStyle(theme, {
    color: theme.colors.chipSelectedText,
  }),
  chipSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.border,
  }),
  interestChipSurface: daylightStyle(theme, {
    backgroundColor: 'rgba(255,252,246,0.88)',
    borderColor: 'rgba(148,126,102,0.28)',
  }),
  addInterestChipSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: 'rgba(8,126,125,0.38)',
  }),
  cardSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.border,
    ...theme.shadows.card,
  }),
  emptySurface: daylightStyle(theme, {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.border,
  }),
  warmSurface: daylightStyle(theme, {
    backgroundColor: 'rgba(242,125,38,0.08)',
    borderColor: 'rgba(196,92,16,0.22)',
  }),
  signOutButton: daylightStyle(theme, {
    backgroundColor: 'rgba(196,92,16,0.06)',
    borderColor: 'rgba(196,92,16,0.20)',
  }),
  drawerOverlay: daylightStyle(theme, {
    backgroundColor: 'rgba(72,58,44,0.24)',
  }),
  settingsDrawer: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
  }),
  drawerHeader: daylightStyle(theme, {
    borderBottomColor: theme.colors.divider,
  }),
  drawerControl: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
    shadowColor: '#80684F',
    shadowOpacity: 0.12,
  }),
  drawerSearch: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.borderStrong,
    shadowColor: '#80684F',
    shadowOpacity: 0.10,
  }),
  inputSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.borderStrong,
  }),
  interestPanel: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceGlass,
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.card,
  }),
  clearInterestsButton: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  interestInputSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.borderStrong,
  }),
  interestInputText: daylightStyle(theme, {
    color: theme.colors.textPrimary,
  }),
  selectedInterestChip: daylightStyle(theme, {
    backgroundColor: 'rgba(21,155,154,0.11)',
    borderColor: 'rgba(8,126,125,0.38)',
  }),
  selectedInterestChipText: daylightStyle(theme, {
    color: theme.colors.chipSelectedText,
  }),
  interestCategorySurface: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceGlass,
    borderColor: theme.colors.borderStrong,
  }),
  interestOptionSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: 'rgba(148,126,102,0.26)',
  }),
  interestOptionSelectedSurface: daylightStyle(theme, {
    backgroundColor: 'rgba(21,155,154,0.11)',
    borderColor: 'rgba(8,126,125,0.42)',
  }),
  interestOptionText: daylightStyle(theme, {
    color: theme.colors.chipText,
  }),
  interestDock: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.elevated,
  }),
  interestCancelText: daylightStyle(theme, {
    color: theme.colors.actionLabel,
  }),
  completionCardSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceGlass,
    borderColor: theme.colors.accentSecondaryBorder,
    ...theme.shadows.card,
  }),
  completionIconSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  completionSub: daylightStyle(theme, {
    color: theme.colors.textSecondary,
  }),
  completionBarTrack: daylightStyle(theme, {
    backgroundColor: theme.colors.borderStrong,
  }),
  roleChip: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: 'rgba(148,126,102,0.28)',
  }),
  roleChipText: daylightStyle(theme, {
    color: theme.colors.actionLabel,
  }),
  roleChipActive: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: 'rgba(8,126,125,0.42)',
  }),
  roleChipTextActive: daylightStyle(theme, {
    color: theme.colors.chipSelectedText,
  }),
  toggleTrack: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
  }),
  settingsRow: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.cardBorder,
    shadowColor: '#80684F',
    shadowOpacity: 0.10,
  }),
  settingsIcon: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
    shadowColor: '#80684F',
    shadowOpacity: 0.10,
  }),
  drawerRow: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.border,
  }),
  drawerIcon: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
  }),
  bottomSheet: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.borderStrong,
    shadowColor: '#80684F',
    shadowOpacity: 0.16,
  }),
});

export default ProfileScreen;
